// [NEW FILE] src/analysis/premium/orchestrator.ts

import SlimSelect from 'slim-select'


import * as $store from '@/store'
import { paths } from '@/db/paths'
import { getQueryScope } from '@/analysis/branch-selector'
import { generateRingkasanFromSummaries } from '@/analysis/sections/general/sales/ringkasan'
import type { SalesSummary } from '@/analysis/sections/general/sales/types'

import { viewCompiledAnalysis } from '@/analysis/actions'

import { generateTcApcHarianChartFromSummaries, generateOmzetHarianChartFromSummaries } from '@/analysis/sections/general/sales/charts'
import { generateOmzetHeatmapFromSummaries } from '@/analysis/sections/general/sales/heatmaps'
import { generateOmzetByOutletChart } from './charts'
import { showView } from '@/core/views'
import { navigateToAdminPanel } from '@/admin/admin-panel'

import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'

// Add these imports at the top of src/analysis/premium/orchestrator.ts
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'
import { formatCurrency, formatNumber, shortenNumber } from '@/utils/string'
import { createChart } from '@/analysis/helpers'
import { mergeChartOptions, chartTooltip, currencyTooltipCallback, shortenCurrency, chartYTicks } from '@/analysis/utils/chart-formatters'
import { generateOmzetHarianChartFromSummaries, generateOmzetMingguanChartFromSummaries } from '@/analysis/sections/general/sales/charts'
import { generateChannelDonutChart } from '@/analysis/sections/general/product-channel/index'

import { showLoading, hideLoading } from '@/core/ui'
import { createAlsoStoreFn } from '@/analysis/utils/store-helpers'
import { setupPageSummary } from '@/components/PageSummary'
import { getGeminiAnalysis } from '@/config/gemini'
import { checkFeatureAccess } from '@/tier/feature-gate'
import { showUpgradePrompt } from '@/tier/upgrade-modal'
import {
  generatePnlTargetComparisonTable,
  generateHistoricalPnlTable,
  initializePnlAccordionListener,
} from '@/analysis/sections/general/finance/tables'
import {
  generatePnlOverviewChart,
  generateFinancialRatioChart,
  generateSpecificSubCategoryRatioChart,
} from '@/analysis/sections/general/finance/charts'

// Add these imports at the top of src/analysis/premium/orchestrator.ts
import { generateRingkasanFromSummaries } from '@/analysis/sections/general/sales/ringkasan'
import { generateTcApcHarianChartFromSummaries } from '@/analysis/sections/general/sales/charts'
import { generateOmzetHeatmapFromSummaries, generateSalesTrendHourlyDailyChartFromSummaries, generateDailyOmzetHeatmapFromSummaries } from '@/analysis/sections/general/sales/heatmaps'

// Add this import to the top of src/analysis/premium/orchestrator.ts
import { generateOmzetYearlyChart, generateOmzetQuarterlyChart, generateOmzetWeeklyChart, generateTop5MenuChart } from './sales-charts'

// Add these imports to the top of src/analysis/premium/orchestrator.ts
import {
  drawGeneralMenuTrendChart,
  generateChannelDonutChart,
  generateOrderByCategoryDonutChart,
} from '@/analysis/sections/general/product-channel'
import { generateTop10MenuTable } from './sales-tables'

import {
  generatePnlComparisonTable,
  generateRatioComparisonChart,
} from '@/analysis/sections/time-comparison/finance/generators'
import { generatePremiumSubCategoryComparisonChart } from './finance-charts'
import { getPnlReportsCollectionRef } from '@/analysis/utils/pnl'

import { populateTimeComparisonKPIs, generatePremiumTimeComparisonChart } from './sales-charts'

// Add these imports to the top of src/analysis/premium/orchestrator.ts
import { generateTop10ComparisonTable } from './sales-tables'
import { generateCategoryComparisonChart, generateChannelComparisonChart } from '@/analysis/sections/time-comparison/product-channel'
import { generatePremiumMenuTrendComparisonChart } from './sales-charts'

import {
  generateBranchPnlComparisonTable,
  generateBranchRatioComparisonChart,
} from '@/analysis/sections/branch-comparison/finance'

import { populateBranchComparisonKPIs, generatePremiumBranchComparisonChart } from './sales-charts'

import {
  drawBranchMenuTrendChart,
  generateBranchChannelComparisonChart,
} from '@/analysis/sections/branch-comparison/product-channel'

import { handleSalesDataUpload } from '@/uploads/sales'
import { handleTargetUpload, downloadPnlTargetTemplate, downloadSalesTargetTemplate } from '@/uploads/targets'
import { uploadAndProcessPnlFile, downloadPnlTemplate } from '@/uploads/pnl'

import { createUserWithEmailAndPassword } from 'firebase/auth'
import { deleteDoc, setDoc } from 'firebase/firestore'
import { auth } from '@/core/firebase'
import { setAdminCredentials } from '@/core/state'
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore'
import { deepmerge } from 'deepmerge-ts'
import { formatMachineYearMonthDay } from '@/utils/string'

import { getGeminiAnalysis } from '@/config/gemini'
import { viewPromptCreators } from '@/prompt'
import { generateSHA256 } from '@/utils/hash'
import { findLiveCache, createLiveCache, deactivateHistoricalCache } from '@/services/analysisCacheService'

import { signOut } from 'firebase/auth'
import { auth } from '@/core/firebase'

import { ref, uploadBytes } from 'firebase/storage'
import { storage } from '@/core/firebase'

import { populateCompiledDataTable } from '@/data-hub/table'

import { clearSummariesCache } from '@/services/localCacheService'
import { renderBranchList } from '@/branches/branch-list'

import { calculateAllPnlMetrics } from '@/analysis/utils/pnl'
import { collection, query, where, getDocs } from 'firebase/firestore' // Make sure these are imported

import Chart from 'chart.js/auto'; // Import Chart type
import type { Chart as ChartTypeInstance } from 'chart.js';
import { renderMemberList } from '@/members/member-list'

import { renderBrandList } from '@/brands/brand-list'

import { createAlsoStoreFn, createMaybeAlsoStoreFn } from '@/analysis/utils/store-helpers'

import { store } from '@/store/core'

import type { AppState } from '@/store/types'

import { showPnlDataModal } from '@/data-hub/modals/pnl-data'
import { openViewChoiceModal, initializeViewChoiceListeners } from '@/data-hub/modals/view-choice'

import { getAiOptions, buildAiPrompt } from './ai-prompts';

declare const marked: any
declare const jspdf: any



/**
 * T016: Helper function to setup AI analysis with tier gating
 *
 * Wraps setupPageSummary to conditionally enable AI insights based on user's tier.
 * Free users see a locked state with upgrade prompt. Standard/Premium users get full AI access.
 *
 * @param pageId - Page identifier for the summary component
 * @param promptKey - Optional prompt key (defaults to pageId without '-section')
 * @param promptDataFormatter - Optional data formatter for prompt
 */
function setupTierGatedAI(params: {
  pageId: string
  promptKey?: string
  promptDataFormatter?: (data: any) => object | Promise<object>
}) {
  const aiAccess = checkFeatureAccess('aiInsights')

  if (aiAccess.allowed) {
    // Standard/Premium users: Enable AI analysis
    setupPageSummary({
      pageId: params.pageId,
      promptKey: params.promptKey as any,
      analyzeUsingAI: getGeminiAnalysis,
      promptDataFormatter: params.promptDataFormatter,
    })
  } else {
    // Free users: Show locked state with upgrade prompt
    const page = document.getElementById(params.pageId)
    const placeholder = page?.querySelector<HTMLDivElement>('div[data-el="analyze-page-summary"]')

    if (placeholder) {
      placeholder.outerHTML = `
        <div class="bg-white rounded-lg shadow-md p-6 flex flex-col gap-3" data-el="analyze-page-summary" data-initialized="true">
          <div data-state="locked">
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-semibold text-gray-700">Summary</h3>
              <div class="flex items-center gap-2">
                <span class="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  ${aiAccess.requiredTier?.charAt(0).toUpperCase() + aiAccess.requiredTier?.slice(1)} Required
                </span>
              </div>
            </div>
            <div class="mt-4 text-gray-600">
              <p class="mb-3">AI Insights require ${aiAccess.requiredTier} plan or higher.</p>
              <button
                class="bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition"
                data-el="upgrade-for-ai-btn"
              >
                Upgrade to ${aiAccess.requiredTier?.charAt(0).toUpperCase() + aiAccess.requiredTier?.slice(1)}
              </button>
            </div>
          </div>
        </div>
      `

      // Add upgrade button click handler
      const upgradeBtn = page?.querySelector('button[data-el="upgrade-for-ai-btn"]')
      upgradeBtn?.addEventListener('click', () => {
        showUpgradePrompt('aiInsights', aiAccess.requiredTier!)
      })
    }
  }
}


/**
 * Renders the overview dashboard KPIs, table, and chart based on the selected brand and branch.
 * Includes a failsafe for when no data is available for the selection.
 */
function renderOverviewDashboard() {
  console.log('Rendering the new Premium Overview Dashboard...');
  
  const branchSelect = document.getElementById('overview-dashboard-branch-select') as HTMLSelectElement;
  const allSalesData: SalesSummary[] = $store.getAllSalesData();
  const { isDemoDataActive } = $store.getAnalysisState().config;
  
  // 1. Filter Data by Brand Context
  let brandData: SalesSummary[];
  let branchFilterSet: Set<string>; // Set of valid IDs (Real) or Names (Demo)

  if (isDemoDataActive) {
    brandData = allSalesData;
    // For demo, we filter by Name
    branchFilterSet = new Set(allSalesData.flatMap(s => s.branches));
  } else {
    const { userBranches, selectedBrandId } = $store.getAccountContext();
    const targetBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    
    // For real data, we filter by ID
    branchFilterSet = new Set(targetBranches.map(b => b.branchId));
    
    // Filter data that belongs to one of the target branch IDs
    brandData = allSalesData.filter(s => branchFilterSet.has((s as any).branchId));
  }

  const selectedValue = branchSelect.value; // ID (Real) or Name (Demo)

  // 2. Filter by Selected Branch
  const filteredData = selectedValue === 'ALL'
    ? brandData
    : brandData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });

  // ... (Rest of the render logic remains the same) ...
  // [KEEP THE REST OF THE FUNCTION FROM PREVIOUS TURN]
  // 3. --- GET ALL UI ELEMENTS ---
  const kpiOmzet = document.getElementById('all-time-omzet') as HTMLElement;
  const kpiCheck = document.getElementById('all-time-check') as HTMLElement;
  const kpiAvgCheck = document.getElementById('all-time-avg-check') as HTMLElement;
  const tableBody = document.getElementById('all-time-period-table-body');
  const chartCanvasId = 'all-time-monthly-omzet-chart';
  const topProductCanvasId = 'all-time-top-products-chart';

  if (filteredData.length === 0) {
    if (kpiOmzet) kpiOmzet.textContent = 'Rp-';
    if (kpiCheck) kpiCheck.textContent = '-';
    if (kpiAvgCheck) kpiAvgCheck.textContent = 'Rp-';
    if (tableBody) tableBody.innerHTML = `<tr><td colspan="5" class="p-8 text-center text-gray-500">There's no data on this Brand/Branch.</td></tr>`;
    createChart(chartCanvasId, 'bar', { labels: [], datasets: [] }, {});
    createChart(topProductCanvasId, 'bar', { labels: [], datasets: [] }, {});
    return;
  }

  const allTimeTotals = filteredData.reduce((acc, summary) => {
    acc.omzet += summary.totalOmzet;
    acc.checks += summary.totalTransactions;
    return acc;
  }, { omzet: 0, checks: 0 });

  const allTimeAvgCheck = allTimeTotals.checks > 0 ? allTimeTotals.omzet / allTimeTotals.checks : 0;

  if (kpiOmzet) kpiOmzet.textContent = formatCurrency(allTimeTotals.omzet);
  if (kpiCheck) kpiCheck.textContent = formatNumber(allTimeTotals.checks);
  if (kpiAvgCheck) kpiAvgCheck.textContent = formatCurrency(allTimeAvgCheck);

  // For the table, we need to map back to names for display
  const dataByPeriod: Record<string, { omzet: number, checks: number, branch: string }> = {};
  for (const summary of filteredData) {
    const period = summary.date.toISOString().slice(0, 7);
    
    // Display Name: For Demo use s.branches[0], For Real use the name from context or summary
    // Since summary might have old name, we try to look up new name from store if possible
    let displayBranchName = summary.branches[0]; 
    if (!isDemoDataActive) {
        const { userBranches } = $store.getAccountContext();
        const b = userBranches.find(ub => ub.branchId === (summary as any).branchId);
        if (b) displayBranchName = b.branchName;
    }

    const key = `${displayBranchName}|${period}`; // Group by Name for display
    if (!dataByPeriod[key]) {
      dataByPeriod[key] = { omzet: 0, checks: 0, branch: displayBranchName };
    }
    dataByPeriod[key].omzet += summary.totalOmzet;
    dataByPeriod[key].checks += summary.totalTransactions;
  }

  if (tableBody) {
    const sortedKeys = Object.keys(dataByPeriod).sort((a, b) => b.localeCompare(a));
    tableBody.innerHTML = sortedKeys.map((key) => {
        const item = dataByPeriod[key];
        const periodDate = new Date(key.split('|')[1] + '-02');
        const formattedPeriod = periodDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        const avgCheck = item.checks > 0 ? item.omzet / item.checks : 0;
        return `
        <tr class="border-t">
            <td class="p-2 font-medium text-gray-800">${item.branch}</td>
            <td class="p-2 text-gray-600">${formattedPeriod}</td>
            <td class="p-2 text-right font-mono">${formatCurrency(item.omzet)}</td>
            <td class="p-2 text-right font-mono">${formatNumber(item.checks)}</td>
            <td class="p-2 text-right font-mono">${formatCurrency(avgCheck)}</td>
        </tr>`;
    }).join('');
  }

  const monthlyData = filteredData.reduce((acc, summary) => {
    const month = summary.date.toISOString().slice(0, 7);
    if (!acc[month]) acc[month] = { omzet: 0, checks: 0 };
    acc[month].omzet += summary.totalOmzet;
    acc[month].checks += summary.totalTransactions;
    return acc;
  }, {} as Record<string, { omzet: number, checks: number }>);

  const sortedMonths = Object.keys(monthlyData).sort();
  const chartLabels = sortedMonths.map((m) => new Date(m + '-02').toLocaleString('default', { month: 'short', year: 'numeric' }));
  const omzetData = sortedMonths.map((m) => monthlyData[m].omzet);
  const checkData = sortedMonths.map((m) => monthlyData[m].checks);
  const avgCheckData = sortedMonths.map((m) => monthlyData[m].checks > 0 ? monthlyData[m].omzet / monthlyData[m].checks : 0);

  createChart(chartCanvasId, 'bar', {
    labels: chartLabels,
    datasets: [
      { type: 'bar', label: 'Total Omzet', data: omzetData, backgroundColor: '#4f46e5', yAxisID: 'y-omzet', order: 3 },
      { type: 'line', label: 'Total Check', data: checkData, borderColor: '#F97316', backgroundColor: '#F97316', yAxisID: 'y-secondary', order: 2, tension: 0.1 },
      { type: 'line', label: 'Average Check', data: avgCheckData, borderColor: '#10B981', backgroundColor: '#10B981', yAxisID: 'y-secondary', order: 1, tension: 0.1 },
    ],
  }, mergeChartOptions({
      scales: {
        'y-omzet': { type: 'linear', position: 'left', title: { display: true, text: 'Total Omzet (Rp)' }, ticks: { callback: (n: any) => shortenCurrency(n) } },
        'y-secondary': { type: 'linear', position: 'right', title: { display: true, text: 'Count / Average (Rp)' }, grid: { drawOnChartArea: false }, ticks: { callback: (n: any) => shortenNumber(n) } },
      },
    },
    chartTooltip({ label: (c: any) => `${c.dataset.label}: ${c.dataset.label === 'Total Check' ? formatNumber(c.parsed.y) : formatCurrency(c.parsed.y)}` })
  ));

  const topProductCanvas = document.getElementById(topProductCanvasId) as HTMLCanvasElement;
  if (topProductCanvas) generateTop5MenuChart(filteredData, topProductCanvasId);
}

/**
 * Sets up the branch selector and initial rendering for the new overview dashboard.
 * NOTE: The Brand Selector is now handled globally by setupGlobalBrandSelector.
 * This function is ONLY responsible for the BRANCH selector.
 */
function setupPremiumOverviewDashboard() {
  const branchSelect = document.getElementById('overview-dashboard-branch-select') as HTMLSelectElement;
  if (!branchSelect) return;

  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext();
  let options = '';

  if (isDemoDataActive) {
    const allSalesData = $store.getAllSalesData();
    const branches = [...new Set(allSalesData.flatMap((s) => s.branches))].sort();
    options = branches.map((b) => `<option value="${b}">${b}</option>`).join('');
  } else {
    // [FIX] Populate with ID as value, Name as text
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    options = filteredBranches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
  }

  branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + options;
  
  const newBranchSelect = branchSelect.cloneNode(true) as HTMLSelectElement;
  branchSelect.parentNode?.replaceChild(newBranchSelect, branchSelect);
  newBranchSelect.addEventListener('change', renderOverviewDashboard);
  renderOverviewDashboard();
}

async function generatePremiumGeneralFinance() {
  if (!currentUser) return
  const branchSelect = document.getElementById('premium-finance-branch-select') as HTMLSelectElement
  const periodSelect = document.getElementById('premium-finance-period-select') as HTMLSelectElement
  const selectedBranch = branchSelect.value
  const selectedPeriod = periodSelect.value
  if (!selectedPeriod || !selectedBranch) return

  showLoading({ message: 'Fetching financial data...', value: 20 })

  const endDate = new Date(selectedPeriod + '-01T00:00:00')
  endDate.setMonth(endDate.getMonth() + 1)
  endDate.setDate(endDate.getDate() - 1)
  endDate.setHours(23, 59, 59, 999)

  const startDate = new Date(selectedPeriod + '-01T00:00:00')
  startDate.setMonth(startDate.getMonth() - 11)

  // Get selected branch from store
  const { selectedBranchId, userBranches, selectedBrandId } = $store.getAccountContext()
  if (!selectedBranchId) {
    throw new Error('No branch selected. Please select a branch first.')
  }

  // [FIX] Resolve the correct branch ID based on the selected name in the dropdown
  const targetBranchObj = userBranches.find(b => b.branchName === selectedBranch);
  const targetBranchId = targetBranchObj?.branchId || selectedBranchId;

  const reportsRef = getPnlReportsCollectionRef(targetBranchId)
  const reportsSnap = await getDocs(reportsRef)

  const historicalReports = reportsSnap.docs
    .map((doc) => doc.data())
    .filter((report) => {
      if (!report || typeof report.period !== 'string') return false
      const reportDate = new Date(report.period + '-02')
      return reportDate >= startDate && reportDate <= endDate
    })
    .sort((a, b) => a.period.localeCompare(b.period))

  showLoading({ message: 'Generating tables and charts...', value: 50 })

  $store.clearViewData('premium-general-keuangan')
  const periodRange = historicalReports.length > 0
    ? `${historicalReports[0]!.period} to ${historicalReports[historicalReports.length - 1]!.period}`
    : 'No data'
  $store.setActiveViewData('premium-general-keuangan', { viewContext: { selectedBranch, selectedPeriod, periodRange } }, { selectedBranch, selectedPeriod })
  const alsoStore = createAlsoStoreFn($store, 'premium-general-keuangan')

  // [FIX] Pass branchId and brandId to the table generator
  await generatePnlTargetComparisonTable(
    selectedPeriod, 
    selectedBranch, 
    'premium-finance-pnl-target-container', 
    { 
      alsoStore, 
      branchId: targetBranchId, 
      brandId: selectedBrandId || undefined 
    }
  )

  // [FIX] Attach listener for the new "Set Target" button
  const addTargetBtn = document.getElementById('btn-add-brand-target');
  if (addTargetBtn) {
    addTargetBtn.addEventListener('click', () => {
      const brandId = addTargetBtn.getAttribute('data-brand-id');
      if (brandId) showAddBrandTargetModal(brandId);
    });
  }

  generateHistoricalPnlTable(historicalReports, 'premium-finance-pnl-history-thead', 'premium-finance-pnl-history-tbody', { alsoStore })
  generatePnlOverviewChart(historicalReports, 'premium-finance-pnl-overview-chart', { alsoStore })

  generateFinancialRatioChart(historicalReports, { canvasId: 'premium-finance-cogs-chart', metric: 'Harga Pokok Produksi', title: 'COGS', alsoStore })
  generateFinancialRatioChart(historicalReports, { canvasId: 'premium-finance-gpm-chart', metric: 'Laba Kotor (Gross Profit)', title: 'Gross Profit Margin', alsoStore })
  generateSpecificSubCategoryRatioChart(historicalReports, { canvasId: 'premium-finance-wages-chart', mainCategory: 'Beban Operasional (OPEX)', subCategory: 'Wages', title: 'Wages', alsoStore })
  generateSpecificSubCategoryRatioChart(historicalReports, { canvasId: 'premium-finance-rent-chart', mainCategory: 'Beban Operasional (OPEX)', subCategory: 'Rent', title: 'Rent', alsoStore })
  // [FIX] Look in BOTH categories for Advertising
  generateSpecificSubCategoryRatioChart(historicalReports, { 
    canvasId: 'premium-finance-advertising-chart', 
    mainCategory: ['Beban Non Operasional', 'Beban Operasional (OPEX)'], // <--- Changed to Array
    subCategory: 'Advertising', 
    title: 'Advertising', 
    alsoStore 
  })
  generateFinancialRatioChart(historicalReports, { canvasId: 'premium-finance-npm-chart', metric: 'Pendapatan Bersih (Net Income)', title: 'Net Profit Margin', alsoStore })

  hideLoading()
}

function showAddBrandTargetModal(brandId: string) {
  const modalId = 'add-brand-target-modal';
  let modal = document.getElementById(modalId);
  if (modal) modal.remove();

  modal = document.createElement('div');
  modal.id = modalId;
  modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm';
  
  modal.innerHTML = `
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col overflow-hidden max-h-[90vh]">
      <div class="p-6 border-b bg-gray-50">
        <h3 class="text-xl font-bold text-gray-900">Global P&L Target Configuration</h3>
        <p class="text-sm text-gray-500 mt-1">Set your target percentages for main categories. Profit margins will be calculated automatically based on 100% Revenue.</p>
      </div>
      
      <div class="p-6 space-y-6 overflow-y-auto custom-scrollbar">
        
        <div class="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-100">
            <span class="font-bold text-blue-900">Pendapatan (Revenue)</span>
            <span class="font-bold text-blue-900">100%</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <label for="target-cogs" class="text-sm font-medium text-gray-700">(-) Harga Pokok Produksi (COGS)</label>
            <div class="relative rounded-md shadow-sm">
                <input type="number" id="target-cogs" class="target-input block w-full pr-8 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" placeholder="0">
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
            </div>
        </div>

        <div class="flex items-center justify-between p-2 bg-gray-100 rounded-md">
            <span class="font-semibold text-gray-700">(=) Laba Kotor (Gross Profit)</span>
            <span id="calc-gross-profit" class="font-bold text-gray-900">100%</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <label for="target-opex" class="text-sm font-medium text-gray-700">(-) Beban Operasional (OPEX)</label>
            <div class="relative rounded-md shadow-sm">
                <input type="number" id="target-opex" class="target-input block w-full pr-8 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" placeholder="0">
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
            </div>
        </div>

        <div class="flex items-center justify-between p-2 bg-gray-100 rounded-md">
            <span class="font-semibold text-gray-700">(=) Net Operating Income</span>
            <span id="calc-noi" class="font-bold text-gray-900">100%</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <label for="target-non-opex" class="text-sm font-medium text-gray-700">(-) Beban Non Operasional</label>
            <div class="relative rounded-md shadow-sm">
                <input type="number" id="target-non-opex" class="target-input block w-full pr-8 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" placeholder="0">
                <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
            </div>
        </div>

        <div class="flex items-center justify-between p-2 bg-indigo-50 rounded-md border border-indigo-100">
            <span class="font-bold text-indigo-900">(=) EBITDA</span>
            <span id="calc-ebitda" class="font-bold text-indigo-900">100%</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                <label for="target-depreciation" class="block text-xs font-medium text-gray-500 mb-1">Depresiasi/Amortisasi</label>
                <div class="relative rounded-md shadow-sm">
                    <input type="number" id="target-depreciation" class="target-input block w-full pr-6 text-xs border-gray-300 rounded-md" placeholder="0">
                    <div class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none"><span class="text-gray-500 text-xs">%</span></div>
                </div>
            </div>
            <div>
                <label for="target-interest" class="block text-xs font-medium text-gray-500 mb-1">Bunga</label>
                <div class="relative rounded-md shadow-sm">
                    <input type="number" id="target-interest" class="target-input block w-full pr-6 text-xs border-gray-300 rounded-md" placeholder="0">
                    <div class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none"><span class="text-gray-500 text-xs">%</span></div>
                </div>
            </div>
            <div>
                <label for="target-tax" class="block text-xs font-medium text-gray-500 mb-1">Pajak (PB1)</label>
                <div class="relative rounded-md shadow-sm">
                    <input type="number" id="target-tax" class="target-input block w-full pr-6 text-xs border-gray-300 rounded-md" placeholder="0">
                    <div class="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none"><span class="text-gray-500 text-xs">%</span></div>
                </div>
            </div>
        </div>

        <div class="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <span class="text-lg font-bold text-green-900">(=) Pendapatan Bersih (Net Income)</span>
            <span id="calc-net-income" class="text-xl font-bold text-green-700">100%</span>
        </div>

      </div>

      <div class="p-6 border-t bg-gray-50 flex justify-end gap-3">
        <button id="target-cancel" class="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
        <button id="target-save" class="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700">Save Targets</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // --- Get Elements ---
  const inputs = {
      cogs: document.getElementById('target-cogs') as HTMLInputElement,
      opex: document.getElementById('target-opex') as HTMLInputElement,
      nonOpex: document.getElementById('target-non-opex') as HTMLInputElement,
      depreciation: document.getElementById('target-depreciation') as HTMLInputElement,
      interest: document.getElementById('target-interest') as HTMLInputElement,
      tax: document.getElementById('target-tax') as HTMLInputElement,
  };
  const displays = {
      gp: document.getElementById('calc-gross-profit'),
      noi: document.getElementById('calc-noi'),
      ebitda: document.getElementById('calc-ebitda'),
      netIncome: document.getElementById('calc-net-income'),
  };

  // --- Pre-load existing data ---
  (async () => {
      try {
        const branchDocRef = doc(db, paths.branches(brandId).path);
        const docSnap = await getDoc(branchDocRef);
        if (docSnap.exists()) {
            const t = docSnap.data().targets?.pnl || {};
            if (t.cogsPercent) inputs.cogs.value = t.cogsPercent;
            if (t.opexPercent) inputs.opex.value = t.opexPercent;
            if (t.nonOpexPercent) inputs.nonOpex.value = t.nonOpexPercent;
            if (t.depreciationPercent) inputs.depreciation.value = t.depreciationPercent;
            if (t.interestPercent) inputs.interest.value = t.interestPercent;
            if (t.taxPercent) inputs.tax.value = t.taxPercent;
            calculateTotals(); // Recalculate on load
        }
      } catch (e) { console.error('Error loading targets for modal', e); }
  })();

  // --- Auto-Calculation Logic ---
  function calculateTotals() {
      const cogs = parseFloat(inputs.cogs.value) || 0;
      const opex = parseFloat(inputs.opex.value) || 0;
      const nonOpex = parseFloat(inputs.nonOpex.value) || 0;
      const depr = parseFloat(inputs.depreciation.value) || 0;
      const interest = parseFloat(inputs.interest.value) || 0;
      const tax = parseFloat(inputs.tax.value) || 0;

      const gp = 100 - cogs;
      const noi = gp - opex;
      const ebitda = noi - nonOpex;
      const netIncome = ebitda - depr - interest - tax;

      if (displays.gp) displays.gp.textContent = `${gp.toFixed(1)}%`;
      if (displays.noi) displays.noi.textContent = `${noi.toFixed(1)}%`;
      if (displays.ebitda) displays.ebitda.textContent = `${ebitda.toFixed(1)}%`;
      
      if (displays.netIncome) {
          displays.netIncome.textContent = `${netIncome.toFixed(1)}%`;
          displays.netIncome.className = `text-xl font-bold ${netIncome >= 0 ? 'text-green-700' : 'text-red-600'}`;
      }
  }

  // Attach listeners to all inputs
  Object.values(inputs).forEach(input => {
      input.addEventListener('input', calculateTotals);
  });


  // --- Save Logic ---
  const saveBtn = document.getElementById('target-save') as HTMLButtonElement;
  const cancelBtn = document.getElementById('target-cancel') as HTMLButtonElement;

  const closeModal = () => modal?.remove();
  cancelBtn.addEventListener('click', closeModal);

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';

    const pnlTargetData = {
        cogsPercent: parseFloat(inputs.cogs.value) || 0,
        opexPercent: parseFloat(inputs.opex.value) || 0,
        nonOpexPercent: parseFloat(inputs.nonOpex.value) || 0,
        depreciationPercent: parseFloat(inputs.depreciation.value) || 0,
        interestPercent: parseFloat(inputs.interest.value) || 0,
        taxPercent: parseFloat(inputs.tax.value) || 0,
    };

    try {
      const brandDocRef = doc(db, paths.branches(brandId).path);
      await setDoc(brandDocRef, { targets: { pnl: pnlTargetData } }, { merge: true });

      alert('Brand targets saved successfully!');
      closeModal();
      window.location.reload();

    } catch (error) {
      console.error('Error saving brand targets:', error);
      alert('Failed to save targets.');
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save Targets';
    }
  });
}

async function setupPremiumGeneralFinance() {
  const pageId = 'premium-placeholder-general-keuangan'
  
  // --- [THIS IS THE FIX] ---
  // We check the flag, but we also attach the listener *outside* the flag.
  // This ensures that even if the table is just re-generated, the listener is active.
  
  if ($store.getInitFlag('premiumGeneralFinanceInitialized')) {
    await generatePremiumGeneralFinance()
    return // Don't re-attach the listener if just re-generating
  }
  // --- [END FIX] ---

  if (!currentUser) return

  const periodSelect = document.getElementById('premium-finance-period-select') as HTMLSelectElement
  const branchSelect = document.getElementById('premium-finance-branch-select') as HTMLSelectElement

  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()

  const allReports: any[] = []
  if (isDemoDataActive) {
    // DEMO MODE: Fetch from demo collection
    console.log('[Orchestrator] Demo mode active in setupPremiumGeneralFinance');
    const reportsRef = getPnlReportsCollectionRef('demo') // 'demo' is a special key
    const reportsSnap = await getDocs(reportsRef)
    reportsSnap.docs.forEach((doc) => allReports.push(doc.data()))
  } else {
    // REAL DATA MODE: Fetch only from user's *filtered* branches
    const filteredBranches = selectedBrandId 
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches

    for (const branch of filteredBranches) {
      const reportsRef = getPnlReportsCollectionRef(branch.branchId)
      const reportsSnap = await getDocs(reportsRef)
      reportsSnap.docs.forEach((doc) => allReports.push(doc.data()))
    }
  }

  const branches = [...new Set(allReports.map((r) => r.branchName))].sort()
  
  if (branches.length === 0) {
    branchSelect.innerHTML = '<option>No P&L data available for this brand</option>'
    periodSelect.innerHTML = '<option>No data available</option>'
    return
  }

  branchSelect.innerHTML = branches.map((b) => `<option value="${b}">${b}</option>`).join('')
  
  // Ensure a default branch is selected if possible
  if (branches.length > 0) {
    branchSelect.value = branches[0];
  }

  const updatePeriods = async () => {
    const selectedBranch = branchSelect.value
    
    const periods = allReports
      .filter(report => report.branchName === selectedBranch)
      .map((doc) => doc.period)
      .filter(Boolean)
      .sort()
      .reverse()
    
    if (periods.length === 0) {
      periodSelect.innerHTML = '<option>No P&L data for this branch</option>'
      // Clear the charts if no data
      await generatePremiumGeneralFinance()
      return
    }
    periodSelect.innerHTML = periods.map((p) => `<option value="${p}">${new Date(p + '-02').toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`).join('')
    
    // Ensure a default period is selected
    if (periods.length > 0) {
      periodSelect.value = periods[0];
    }
    
    await generatePremiumGeneralFinance()
  }

  branchSelect.addEventListener('change', updatePeriods)
  periodSelect.addEventListener('change', generatePremiumGeneralFinance)

  setupTierGatedAI({
    pageId,
    promptKey: 'general-keuangan', 
    promptDataFormatter: (viewData) => viewData.data,
  })

  // --- [THIS IS THE FIX] ---
  // Call the listener function *once* after setup.
  // We need to attach the listener to the *tbody* element in the premium view.
  document.getElementById('premium-finance-pnl-history-tbody')?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const headerRow = target.closest('.pnl-main-category') as HTMLElement | null
    if (headerRow) {
      const targetClass = (headerRow as HTMLElement).dataset.target
      if (!targetClass) return
      // We must query relative to the correct table, as there are multiple on the page
      const tableBody = headerRow.closest('tbody')
      if (!tableBody) return
      
      const subRows = tableBody.querySelectorAll(`.${targetClass}`)
      const chevron = headerRow.querySelector('.chevron-icon')
      subRows.forEach((row) => row.classList.toggle('hidden'))
      chevron?.classList.toggle('rotate-180')
    }
  })
  // --- [END FIX] ---

  $store.setInitFlag('premiumGeneralFinanceInitialized', true)
  await updatePeriods()
}

/**
 * Updates the plan type badge in the premium analysis sidebar
 */
function updatePlanTypeBadge() {
  const badgeEl = document.getElementById('premium-plan-badge')
  if (!badgeEl) return

  const { planType } = $store.getAccountContext()

  // Define badge styles based on plan type
  const badgeConfig = {
    free: {
      text: 'Free Plan',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300',
    },
    standard: {
      text: 'Standard Plan',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-300',
    },
    enterprise: {
      text: 'Enterprise Plan',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-700',
      borderColor: 'border-indigo-300',
    },
  }

  const config = badgeConfig[planType] || badgeConfig.free

  // Clear existing classes
  badgeEl.className = 'mb-4 px-3 py-2 rounded-lg text-center text-sm font-semibold'

  // Add new classes (including 'border' class)
  badgeEl.classList.add('border', config.bgColor, config.textColor, config.borderColor)
  badgeEl.textContent = config.text
}

/**
 * Sets up the GLOBAL brand selector in the sidebar.
 * This runs once when the premium view is loaded.
 */
function setupGlobalBrandSelector() {
  const brandSelect = document.getElementById('sidebar-brand-select') as HTMLSelectElement;
  if (!brandSelect) return;

  const { isDemoDataActive } = $store.getAnalysisState().config;

  if (isDemoDataActive) {
    // If we're in demo mode, hide the brand selector entirely.
    brandSelect.style.display = 'none';
    return;
  }

  // If we're in REAL data mode, populate it.
  const { userBrands, selectedBrandId } = $store.getAccountContext();

  brandSelect.innerHTML = `<option value="ALL_BRANDS">All Brands</option>` + userBrands.map((brand) => 
    `<option value="${brand.brandId}">${brand.brandName}</option>`
  ).join('');
  
  brandSelect.value = selectedBrandId || 'ALL_BRANDS';

  // Add event listener
  brandSelect.addEventListener('change', () => {
    const newBrandId = brandSelect.value === 'ALL_BRANDS' ? null : brandSelect.value;
    $store.setSelectedBrandId(newBrandId); // This saves to localStorage
    
    // Force a full page reload.
    // This is the cleanest way to ensure all dashboard tabs
    // (Aspek Keuangan, Penjualan, etc.) re-fetch their data
    // with the new brand context.
    window.location.reload();
  });
}



export async function setupPremiumAnalysisView() {
  // Prevent re-initialization on subsequent views
  if ($store.getInitFlag('premiumAnalysisInitialized')) {
    return
  }

  

  // Update plan type badge
  updatePlanTypeBadge()
  setupGlobalBrandSelector()
  initializeViewChoiceListeners()

  // --- [NEW] Context-Aware Sidebar Logic (ID-Based Fix) ---
  const updateUiBasedOnData = async () => {
    const { isDemoDataActive } = $store.getAnalysisState().config;
    const { selectedBrandId, userBranches } = $store.getAccountContext();

    // --- 1. Gather Capability Metrics ---
    let metrics = {
      salesFileCount: 0,
      salesUniquePeriods: 0,
      hasSalesOverlap: false,
      pnlFileCount: 0,
      pnlUniquePeriods: 0,
      hasPnlOverlap: false
    };

    if (isDemoDataActive) {
      metrics = {
        salesFileCount: 10, salesUniquePeriods: 10, hasSalesOverlap: true,
        pnlFileCount: 10, pnlUniquePeriods: 10, hasPnlOverlap: true
      };
    } else {
      // A. Analyze Sales Data (Filter by ID)
      const allSalesData = $store.getAllSalesData();
      let relevantSales = allSalesData;
      
      if (selectedBrandId) {
        // [FIX] Use Branch IDs for filtering, not Names
        const branchIdsInCurrentBrand = new Set(
          userBranches
            .filter(b => b.brandId === selectedBrandId)
            .map(b => b.branchId)
        );
        
        relevantSales = allSalesData.filter(s => 
          branchIdsInCurrentBrand.has((s as any).branchId)
        );
      }

      if (relevantSales.length > 0) {
        const uniquePeriods = new Set<string>();
        const overlapMap = new Map<string, Set<string>>();

        relevantSales.forEach(s => {
            const period = s.date.toISOString().slice(0, 7);
            uniquePeriods.add(period);
            
            if (!overlapMap.has(period)) overlapMap.set(period, new Set());
            // [FIX] Count unique IDs, not Names
            const bId = (s as any).branchId || s.branches[0];
            overlapMap.get(period)?.add(bId);
        });
        
        metrics.salesFileCount = relevantSales.length;
        metrics.salesUniquePeriods = uniquePeriods.size;
        metrics.hasSalesOverlap = Array.from(overlapMap.values()).some(ids => ids.size >= 2);
      }

      // B. Analyze P&L Data
      try {
        const targetBranches = selectedBrandId 
          ? userBranches.filter(b => b.brandId === selectedBrandId) 
          : userBranches;

        const pnlPromises = targetBranches.map(async (branch) => {
          const reportsRef = getPnlReportsCollectionRef(branch.branchId);
          const snapshot = await getDocs(reportsRef);
          return {
            periods: snapshot.docs.map(d => d.data().period)
          };
        });

        const pnlResults = await Promise.all(pnlPromises);
        const pnlPeriodsSet = new Set<string>();
        const pnlOverlapMap = new Map<string, number>();

        pnlResults.forEach(res => {
          metrics.pnlFileCount += res.periods.length;
          res.periods.forEach(p => {
              pnlPeriodsSet.add(p);
              pnlOverlapMap.set(p, (pnlOverlapMap.get(p) || 0) + 1);
          });
        });

        metrics.pnlUniquePeriods = pnlPeriodsSet.size;
        metrics.hasPnlOverlap = Array.from(pnlOverlapMap.values()).some(count => count >= 2);

      } catch (e) {
        console.warn('[UI_UPDATE] Failed to fetch P&L metrics:', e);
      }
    }

    // --- 2. Define Visibility Rules ---
    const hiddenReasons: { title: string, reason: string }[] = [];
    const checkCap = (val: boolean, title: string, reason: string) => { if (!val) hiddenReasons.push({ title, reason }); return val; };

    const hasAnyData = checkCap(metrics.salesFileCount > 0 || metrics.pnlFileCount > 0, 'Dashboard', 'No data uploaded.');
    
    let capabilities = {
      generalSales: false, generalPnl: false,
      timeSales: false, timePnl: false,
      branchSales: false, branchPnl: false,
      hasAnyData
    };

    if (hasAnyData) {
        capabilities = {
          generalSales: checkCap(metrics.salesFileCount >= 1, 'Analisis General: Sales', 'Upload at least 1 Sales file.'),
          generalPnl: checkCap(metrics.pnlFileCount >= 1, 'Analisis General: Finance', 'Upload at least 1 P&L file.'),
          timeSales: checkCap(metrics.salesUniquePeriods >= 2, 'Time Comparison: Sales', 'Upload Sales data for 2+ periods.'),
          timePnl: checkCap(metrics.pnlUniquePeriods >= 2, 'Time Comparison: Finance', 'Upload P&L data for 2+ periods.'),
          branchSales: checkCap(metrics.hasSalesOverlap, 'Branch Comparison: Sales', 'Upload Sales data for 2+ branches in the SAME period.'),
          branchPnl: checkCap(metrics.hasPnlOverlap, 'Branch Comparison: Finance', 'Upload P&L data for 2+ branches in the SAME period.'),
          hasAnyData: true
        };
    }

    // --- 3. Apply to DOM Elements ---
    const setVisible = (id: string, visible: boolean) => {
      const el = document.getElementById(id);
      if (!el) return;
      const target = el.closest('li') || el; 
      if (visible) {
        target.classList.remove('hidden');
        target.style.display = ''; 
      } else {
        target.classList.add('hidden');
        target.style.display = 'none'; 
      }
    };

    // A. Main Menu Items
    const dashboardLi = document.getElementById('premium-goto-dashboard-btn')?.closest('li');
    const exportPdfLi = document.getElementById('premium-goto-export-pdf-btn')?.closest('li');
    const analysisHeader = document.querySelector('aside nav h3.mt-6');

    if (capabilities.hasAnyData) {
      dashboardLi?.classList.remove('hidden');
      exportPdfLi?.classList.remove('hidden');
      analysisHeader?.classList.remove('hidden');
    } else {
      dashboardLi?.classList.add('hidden');
      exportPdfLi?.classList.add('hidden');
      analysisHeader?.classList.add('hidden');
    }

    // B. Status Message
    let statusEl = document.getElementById('analysis-data-status-msg');
    if (!statusEl && analysisHeader && analysisHeader.parentNode) {
        statusEl = document.createElement('p');
        statusEl.id = 'analysis-data-status-msg';
        statusEl.className = 'text-[10px] text-orange-600 -mt-1 mb-3 px-1 italic font-medium hidden'; 
        analysisHeader.after(statusEl);
    }

    if (statusEl && capabilities.hasAnyData && hiddenReasons.length > 0) {
       statusEl.textContent = `${hiddenReasons.length} features unavailable`;
       statusEl.classList.remove('hidden');
    } else if (statusEl) {
       statusEl.classList.add('hidden');
    }

    // C. Granular Analysis Menus
    setVisible('premium-general-keuangan', capabilities.generalPnl);
    setVisible('premium-general-penjualan', capabilities.generalSales);
    setVisible('premium-general-produk-channel', capabilities.generalSales);
    setVisible('premium-waktu-keuangan', capabilities.timePnl);
    setVisible('premium-waktu-penjualan', capabilities.timeSales);
    setVisible('premium-waktu-produk-channel', capabilities.timeSales);
    setVisible('premium-cabang-keuangan', capabilities.branchPnl);
    setVisible('premium-cabang-penjualan', capabilities.branchSales);
    setVisible('premium-cabang-produk-channel', capabilities.branchSales);

    // D. Smart Parent Hiding
    const checkParentVisibility = (parentId: string, childIds: string[]) => {
      const parentToggle = document.querySelectorAll('#premium-analysis-menu .premium-submenu-toggle');
      let targetParent: Element | null = null;
      parentToggle.forEach(toggle => { if (toggle.textContent?.includes(parentId)) targetParent = toggle.closest('.submenu-container'); });

      if (targetParent) {
        const childCapabilities = childIds.map(id => {
          if (id.includes('general-keuangan')) return capabilities.generalPnl;
          if (id.includes('general-penjualan')) return capabilities.generalSales;
          if (id.includes('general-produk')) return capabilities.generalSales;
          if (id.includes('waktu-keuangan')) return capabilities.timePnl;
          if (id.includes('waktu-penjualan')) return capabilities.timeSales;
          if (id.includes('waktu-produk')) return capabilities.timeSales;
          if (id.includes('cabang-keuangan')) return capabilities.branchPnl;
          if (id.includes('cabang-penjualan')) return capabilities.branchSales;
          if (id.includes('cabang-produk')) return capabilities.branchSales;
          return false;
        });
        const anyChildVisible = childCapabilities.some(v => v === true);
        if (anyChildVisible) targetParent.classList.remove('hidden'); else targetParent.classList.add('hidden');
      }
    };

    checkParentVisibility('Analisis General', ['premium-general-keuangan', 'premium-general-penjualan', 'premium-general-produk-channel']);
    checkParentVisibility('Analisis Perbandingan Waktu', ['premium-waktu-keuangan', 'premium-waktu-penjualan', 'premium-waktu-produk-channel']);
    checkParentVisibility('Analisis Perbandingan Cabang', ['premium-cabang-keuangan', 'premium-cabang-penjualan', 'premium-cabang-produk-channel']);

    // 4. Toggle Indicator & Store Reasons
    const indicatorLi = document.getElementById('hidden-menu-indicator-li');
    const indicatorBtn = document.getElementById('show-hidden-menu-info-btn');
    
    if (hiddenReasons.length > 0) {
        indicatorLi?.classList.remove('hidden');
        indicatorBtn?.setAttribute('data-reasons', JSON.stringify(hiddenReasons));
    } else {
        indicatorLi?.classList.add('hidden');
    }

    // 5. Force Redirect
    if (!capabilities.hasAnyData) {
        const isSafeView = !document.getElementById('premium-manage-data-content')?.classList.contains('hidden') ||
            !document.getElementById('premium-branches-content')?.classList.contains('hidden') ||
            !document.getElementById('premium-brands-content')?.classList.contains('hidden') ||
            !document.getElementById('premium-branch-members-content')?.classList.contains('hidden') ||
            !document.getElementById('premium-user-management-content')?.classList.contains('hidden') ||
            !document.getElementById('premium-configuration-content')?.classList.contains('hidden');

        if (!isSafeView) document.getElementById('premium-goto-manage-data-btn')?.click();
    }
  }
  
  updateUiBasedOnData()

  // --- [NEW] Event Listener for Hidden Menu Modal ---
  const showHiddenInfoBtn = document.getElementById('show-hidden-menu-info-btn');
  const hiddenModal = document.getElementById('hidden-menu-modal');
  const hiddenModalClose = document.getElementById('hidden-menu-modal-close');
  const hiddenModalOk = document.getElementById('hidden-menu-modal-ok-btn');

  showHiddenInfoBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const reasonsJson = showHiddenInfoBtn.getAttribute('data-reasons');
      const listContainer = document.getElementById('hidden-menu-list');
      
      if (listContainer && reasonsJson) {
          const reasons = JSON.parse(reasonsJson);
          listContainer.innerHTML = reasons.map((item: any) => `
            <div class="bg-amber-50 p-3 rounded-md border border-amber-100">
                <p class="font-semibold text-amber-800 text-sm">${item.title}</p>
                <p class="text-xs text-amber-700 mt-1">${item.reason}</p>
            </div>
          `).join('');
          hiddenModal?.classList.remove('hidden');
      }
  });

  const closeHiddenModal = () => hiddenModal?.classList.add('hidden');
  hiddenModalClose?.addEventListener('click', closeHiddenModal);
  
  hiddenModalOk?.addEventListener('click', () => {
      closeHiddenModal();
      document.getElementById('premium-goto-manage-data-btn')?.click();
  });



  async function showFinSlideEditor(deckId: string) {
    console.log(`[FINSLIDE_EDIT] Loading deck: ${deckId}`)
    if (!currentUser) return

    const deckListPage = document.getElementById('premium-finslide-page')
    const editorPage = document.getElementById('finslide-editor-view')
    const deckTitleEl = document.getElementById('finslide-deck-title')
    const saveBtn = document.getElementById('finslide-save-btn') as HTMLButtonElement | null;

    if (!deckListPage || !editorPage || !deckTitleEl) {
      console.error('FinSlide editor UI elements not found!')
      return
    }

    showLoading({ message: 'Loading deck...' })

    try {
      // Fetch the specific deck document
      const deckDocRef = doc(db, `users/${currentUser.uid}/finSlideDecks`, deckId)
      const docSnap = await getDoc(deckDocRef)

      if (!docSnap.exists()) {
        throw new Error(`Deck with ID ${deckId} not found.`)
      }

      const deckData = docSnap.data()
      currentEditingDeck = { id: deckId, slides: deckData.slides || [] }
      currentSlideIndex = 0 // Start at the first slide

      deckHasChanges = false;
        if (saveBtn) {
            saveBtn.disabled = true;
            saveBtn.classList.add('disabled:opacity-50');
        } 

      // Update title and switch views
      deckTitleEl.textContent = `${deckData.branch || 'N/A'} - ${deckData.period || 'N/A'} (${deckData.range || 'N/A'})`
      deckListPage.classList.add('hidden')
      editorPage.classList.remove('hidden')

      // Initial render
      renderSlideThumbnails(currentEditingDeck.slides)
      renderCurrentSlide(currentEditingDeck.slides[currentSlideIndex], currentSlideIndex)
    } catch (error) {
      console.error('[FINSLIDE_EDIT] Error loading deck:', error)
      alert(`Failed to load deck: ${error instanceof Error ? error.message : String(error)}`)
      // Optionally switch back to list view on error
      // editorPage.classList.add('hidden');
      // deckListPage.classList.remove('hidden');
    } finally {
      hideLoading()
    }
  }

  /**
 * Renders the list of slide thumbnails in the sidebar.
 */
  function renderSlideThumbnails(slides: any[]) {
    const thumbnailList = document.getElementById('finslide-thumbnail-list')
    if (!thumbnailList) return

    thumbnailList.innerHTML = slides.map((slide, index) => `
        <button data-slide-index="${index}" class="finslide-thumbnail-btn w-full text-left p-2 rounded border border-gray-300 bg-white hover:bg-indigo-50 ${index === currentSlideIndex ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}">
            <p class="text-xs font-medium truncate">${index + 1}. ${slide.title || slide.slideType || 'Untitled Slide'}</p>
            <p class="text-xs text-gray-500">${slide.slideType || 'Unknown Type'}</p>
        </button>
    `).join('')

    // Add click listeners to thumbnails
    thumbnailList.querySelectorAll('.finslide-thumbnail-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = parseInt((btn as HTMLElement).dataset.slideIndex || '0', 10)
        if (currentEditingDeck) {
          currentSlideIndex = index
          renderCurrentSlide(currentEditingDeck.slides[index], index)
          // Update active state visuals for thumbnails
          thumbnailList.querySelectorAll('.finslide-thumbnail-btn').forEach((b) => b.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-1'))
          btn.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-1')
        }
      })
    })
  }

  /**
 * Renders the content of the currently selected slide, including charts and editable fields.
 */
function renderCurrentSlide(slideData: any | undefined, slideIndex: number) {
    const slideContentArea = document.getElementById('finslide-current-slide');
    const saveBtn = document.getElementById('finslide-save-btn') as HTMLButtonElement | null;
    if (!slideContentArea) return;

    // --- Destroy previous chart instance ---
    if (currentSlideChartInstance) {
        currentSlideChartInstance.destroy();
        currentSlideChartInstance = null;
    }

    if (!slideData) {
        slideContentArea.innerHTML = `<p class="text-red-500 text-center">Error: Slide data not found for index ${slideIndex}.</p>`;
        return;
    }

    // --- Render Editable Fields ---
    let contentHtml = `
        <div class="mb-4">
            <label for="finslide-title-input" class="block text-sm font-medium text-gray-700">Slide Title</label>
            <input type="text" id="finslide-title-input" class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-2xl font-semibold" value="${slideData.title || ''}">
        </div>
    `;

    if (slideData.subtitle !== undefined) {
        contentHtml += `
            <div class="mb-6">
                <label for="finslide-subtitle-input" class="block text-sm font-medium text-gray-700">Subtitle</label>
                <input type="text" id="finslide-subtitle-input" class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-lg text-gray-600" value="${slideData.subtitle || ''}">
            </div>
        `;
    }

    // --- START: CORRECTED RENDER LOGIC ---
    const isChartSlide = slideData.chartType && slideData.data && slideData.data.labels && slideData.data.datasets;

    if (slideData.slideType === 'AI_Summary' && slideData.content) {
        // RENDER AI SUMMARY
        contentHtml += `
            <div class="prose prose-sm max-w-none mt-6 p-4 border rounded bg-purple-50 border-purple-200 text-purple-800">
                ${marked.parse(slideData.content)}
            </div>
        `;
    } else if (isChartSlide) {
        // RENDER CHART
        contentHtml += `
            <div class="chart-container mx-auto my-6" style="max-width: 600px; height: 350px;">
                <canvas id="finslide-active-chart-canvas"></canvas>
            </div>
        `;
    } else if (slideData.slideType === 'KPI_Overview' && slideData.data) {
        // RENDER KPI LIST (READ-ONLY)
        contentHtml += `<h4 class="text-lg font-medium text-gray-800 mb-2 mt-6">KPI Data (Read-only)</h4>`;
        contentHtml += `<ul class="list-disc pl-5 space-y-2 max-w-md">`;
        for (const key in slideData.data) {
            const value = slideData.data[key];
            const formattedValue = typeof value === 'number'
                ? (key.toLowerCase().includes('omzet') || key.toLowerCase().includes('check') ? formatCurrencyUtil(value) : formatNumberUtil(value))
                : String(value);
            contentHtml += `<li class="text-sm"><strong>${key}:</strong> ${formattedValue}</li>`;
        }
        contentHtml += `</ul>`;
    } else if (slideData.data) {
        // RENDER GENERIC DATA (READ-ONLY)
        contentHtml += `<h4 class="text-lg font-medium text-gray-800 mb-2 mt-6">Raw Data (Read-only)</h4>`;
        contentHtml += `<pre class="bg-gray-100 p-3 rounded text-xs overflow-auto">${JSON.stringify(slideData.data, null, 2)}</pre>`;
    }
    // --- END: CORRECTED RENDER LOGIC ---

    // --- Render Editable Notes ---
    if (slideData.notes !== undefined) {
        contentHtml += `
            <div class="mt-6 pt-4 border-t">
                <label for="finslide-notes-input" class="block text-sm font-medium text-gray-700">Notes</label>
                <textarea id="finslide-notes-input" rows="4" class="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm">${slideData.notes || ''}</textarea>
            </div>
        `;
    }

    slideContentArea.innerHTML = contentHtml; // Set the HTML

    // --- Create Chart After Canvas Exists ---
    if (isChartSlide) {
        const canvasElement = document.getElementById('finslide-active-chart-canvas') as HTMLCanvasElement | null;
        if (canvasElement) {
            try {
                const baseChartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: slideData.data.datasets.length > 1 } } };
                const finalChartOptions = mergeChartOptions(
                    baseChartOptions,
                    slideData.options || {},
                    chartTooltip({ label: currencyTooltipCallback })
                );

                currentSlideChartInstance = createChart(
                    'finslide-active-chart-canvas',
                    slideData.chartType,
                    slideData.data,
                    finalChartOptions
                );
            } catch (error) {
                 console.error("[FINSLIDE_RENDER] Error creating chart:", error);
                 // Check if slideContentArea still exists before appending
                 if (slideContentArea) {
                     slideContentArea.innerHTML += `<p class="text-red-500 text-center mt-4">Error rendering chart.</p>`;
                 }
            }
        }
    }

    // --- Add Input Listeners ---
    const updateSlideData = (field: string, value: string) => {
        if (currentEditingDeck && currentEditingDeck.slides[slideIndex]) {
            (currentEditingDeck.slides[slideIndex] as any)[field] = value;
            if (!deckHasChanges) {
                deckHasChanges = true;
                if (saveBtn) {
                    saveBtn.disabled = false;
                    saveBtn.classList.remove('disabled:opacity-50');
                }
            }
        }
    };

    slideContentArea.querySelector('#finslide-title-input')?.addEventListener('input', (e) => updateSlideData('title', (e.target as HTMLInputElement).value));
    slideContentArea.querySelector('#finslide-subtitle-input')?.addEventListener('input', (e) => updateSlideData('subtitle', (e.target as HTMLInputElement).value));
    slideContentArea.querySelector('#finslide-notes-input')?.addEventListener('input', (e) => updateSlideData('notes', (e.target as HTMLTextAreaElement).value));
}

  async function setupPremiumFinSlidePage() {
    console.log('Setting up FinSlide Page...')
    const deckListContainer = document.getElementById('finslide-deck-list')

    if (!deckListContainer) {
      console.error('FinSlide deck list container not found!')
      return
    }
    if (!currentUser) {
      deckListContainer.innerHTML = '<p class="text-red-500 text-center">Please log in to view decks.</p>'
      return
    }

    // Prevent re-initialization if already loaded (optional, but good practice)
    // if (deckListContainer.hasAttribute('data-loaded')) {
    //     console.log('FinSlide page already loaded.');
    //     return; // Keep commented out if you want it to refresh each time
    // }

    deckListContainer.innerHTML = '<p class="text-gray-500 text-center">Loading your generated decks...</p>'

    try {
      // --- START: Replace Simulation with Actual Firestore Fetch ---
      const decksRef = collection(db, `users/${currentUser.uid}/finSlideDecks`)
      // Assuming you add a 'createdAt' field (Timestamp) when creating the deck in the Firebase Function
      const q = query(decksRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      const decks = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      console.log(`[FINSLIDE_LIST] Fetched ${decks.length} decks from Firestore.`)
      // --- END: Replace Simulation with Actual Firestore Fetch ---

      if (decks.length === 0) {
        deckListContainer.innerHTML = '<p class="text-gray-500 text-center">No FinSlide decks generated yet. Use the "Export to FinSlide" button on the analysis pages.</p>'
      } else {
      // Render as a list
        deckListContainer.innerHTML = `
                <ul class="space-y-4">
                    ${decks.map((deck) => {
                      // Safely handle potential missing or incorrect createdAt field
                      let createdAtDate = new Date() // Default to now if invalid
                      if (deck.createdAt && typeof deck.createdAt.toDate === 'function') {
                        createdAtDate = deck.createdAt.toDate()
                      } else if (deck.createdAt) {
                        // Attempt to parse if it's stored differently (e.g., ISO string) - adjust if needed
                        try { createdAtDate = new Date(deck.createdAt) } catch (e) {}
                      }
                      const formattedDate = createdAtDate.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })

                      return `
                        <li class="border border-gray-200 rounded-lg p-4 flex justify-between items-center hover:bg-gray-50">
                            <div>
                                <p class="font-semibold text-gray-800">${deck.branch || 'N/A'} - ${deck.period || 'N/A'} (${deck.range || 'N/A'})</p>
                                <p class="text-sm text-gray-500">Generated: ${formattedDate}</p>
                            </div>
                            <div>
                                <button class="text-indigo-600 hover:text-indigo-800 text-sm font-medium mr-2" data-deck-id="${deck.id}">View/Edit (Not Implemented)</button>
                                <button class="text-red-600 hover:text-red-800 text-sm font-medium" data-deck-id="${deck.id}">Delete (Not Implemented)</button>
                            </div>
                        </li>
                    `
                    }).join('')}
                </ul>
            `
      // TODO: Add listeners for View/Edit/Delete buttons here if needed later
      }
      // START: Add listener for View/Edit buttons
      deckListContainer.addEventListener('click', async (e) => { // <-- Add async here
            const target = e.target as HTMLElement;
            // Find the closest button with data-deck-id, whether View or Delete
            const actionButton = target.closest('button[data-deck-id]');

            if (!actionButton) return; // Exit if the click wasn't on a relevant button

            const deckId = actionButton.getAttribute('data-deck-id');
            if (!deckId) return; // Exit if deckId is missing

            // --- START: Added Delete Logic ---
            if (actionButton.textContent?.includes('Delete')) {
                // It's the Delete button
                if (confirm(`Are you sure you want to permanently delete this FinSlide deck (${deckId})? This cannot be undone.`)) {
                    showLoading({ message: 'Deleting deck...' });
                    actionButton.setAttribute('disabled', 'true'); // Disable button during deletion
                    try {
                        // --- TODO: Replace with actual Firebase Function call ---
                        console.log(`[FINSLIDE_DELETE] Simulating call to deleteFinSlideDeck with deckId: ${deckId}`);
                        // const deleteDeck = httpsCallable(functions, 'deleteFinSlideDeck');
                        // await deleteDeck({ deckId: deckId });
                        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate delay
                        // --- End Simulation ---

                        // Remove the list item from the UI immediately on success
                        actionButton.closest('li')?.remove();
                        hideLoading();
                        alert('Deck deleted successfully.'); // Simple feedback

                        // Optional: Check if the list is now empty
                        if (!deckListContainer.querySelector('li')) {
                            deckListContainer.innerHTML = '<p class="text-gray-500 text-center">No FinSlide decks generated yet.</p>';
                        }

                    } catch (error) {
                        hideLoading();
                        actionButton.removeAttribute('disabled'); // Re-enable on error
                        console.error("[FINSLIDE_DELETE] Error deleting deck:", error);
                        alert(`Failed to delete deck. Error: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            // --- END: Added Delete Logic ---

            } else { // It's the View/Edit button
                showFinSlideEditor(deckId); // Call function to load and show editor
            }
      // Add delete listener logic here later if needed
      })
    // END: Add listener for View/Edit buttons
    } catch (error) {
      console.error('Error fetching FinSlide decks:', error)
      deckListContainer.innerHTML = `<p class="text-red-500 text-center">Error loading decks. Please try again later.</p>`
    }
  }

  function calculateFinancialProjection(inputs: {
    startRevenue: number
    fixedCosts: number
    cashOnHand: number
    arpu: number // Currently unused, but kept for potential future use
    marketingSpend: number
    growthTarget: number // Percentage, e.g., 5 for 5%
    variableCostPercent: number // Percentage, e.g., 40 for 40%
    duration: number // In months
  }): {
    monthlyProjections: { month: number, revenue: number, grossProfit: number, ebitda: number, endingCash: number }[]
    runwayMonths: number | null
    breakEvenMonth: number | null
    avgGrossMargin: number
  } {
    const monthlyProjections: { month: number, revenue: number, grossProfit: number, ebitda: number, endingCash: number }[] = []
    let currentRevenue = inputs.startRevenue
    let currentCash = inputs.cashOnHand
    let cumulativeEbitda = 0
    let runwayMonths: number | null = null
    let breakEvenMonth: number | null = null
    let totalProjectedRevenue = 0
    let totalProjectedVariableCosts = 0

    // Month 0 (Starting Point - not added to table, but used for initial cash)
    // We could potentially add starting values if needed, but the loop starts from month 1

    for (let month = 1; month <= inputs.duration; month++) {
      // Apply growth for month 1 onwards
      if (month > 1) {
        currentRevenue = currentRevenue * (1 + inputs.growthTarget / 100)
      } else {
        // For the very first month, use the startRevenue directly
        currentRevenue = inputs.startRevenue
      }

      const variableCosts = currentRevenue * (inputs.variableCostPercent / 100)
      const grossProfit = currentRevenue - variableCosts
      const ebitda = grossProfit - inputs.fixedCosts - inputs.marketingSpend
      const endingCash = currentCash + ebitda

      monthlyProjections.push({
        month,
        revenue: currentRevenue,
        grossProfit,
        ebitda,
        endingCash,
      })

      totalProjectedRevenue += currentRevenue
      totalProjectedVariableCosts += variableCosts
      cumulativeEbitda += ebitda

      // Check for runway end
      if (endingCash < 0 && runwayMonths === null) {
        runwayMonths = month
      }

      // Check for break-even (cumulative profit turns positive)
      // Using cumulative EBITDA as proxy for profit before interest/depreciation/tax
      if (cumulativeEbitda > 0 && breakEvenMonth === null) {
        breakEvenMonth = month
      }

      currentCash = endingCash // Update cash for the next month

      // Stop projection if cash runs out completely and runway wasn't already set this month
      // (Prevents negative cash balances from continuing indefinitely)
      if (currentCash < 0 && runwayMonths === null) {
        runwayMonths = month // Mark runway if cash just went negative
        // break; // Optional: Stop simulation if cash runs out
      } else if (currentCash < 0 && runwayMonths !== month) {
        // If cash was already negative, we can stop
        // break; // Optional: Stop simulation if cash runs out
      }
    }

    const avgGrossMargin = totalProjectedRevenue > 0
      ? ((totalProjectedRevenue - totalProjectedVariableCosts) / totalProjectedRevenue) * 100
      : 0

    return {
      monthlyProjections,
      runwayMonths,
      breakEvenMonth,
      avgGrossMargin,
    }
  }

  async function setupPremiumProjectionView() {
    console.log('Setting up Financial Projection View...')
    const generateBtn = document.getElementById('projection-generate-btn')
    const outputSection = document.getElementById('projection-output-section')

    const inputElements = {
      startRevenue: document.getElementById('proj-start-revenue') as HTMLInputElement,
      fixedCosts: document.getElementById('proj-fixed-costs') as HTMLInputElement,
      cashOnHand: document.getElementById('proj-cash-on-hand') as HTMLInputElement,
      arpu: document.getElementById('proj-arpu') as HTMLInputElement,
      marketingSpend: document.getElementById('proj-marketing-spend') as HTMLInputElement,
      growthTarget: document.getElementById('proj-growth-target') as HTMLInputElement,
      variableCostPercent: document.getElementById('proj-variable-cost') as HTMLInputElement,
      duration: document.getElementById('proj-duration') as HTMLInputElement,
    }

    const outputElements = {
      runway: document.getElementById('proj-runway') as HTMLElement,
      breakEven: document.getElementById('proj-break-even') as HTMLElement,
      grossMargin: document.getElementById('proj-gross-margin') as HTMLElement,
      viability: document.getElementById('proj-viability') as HTMLElement,
      tableBody: document.getElementById('projection-table-body') as HTMLElement,
    }

    generateBtn?.addEventListener('click', () => {
      // --- 1. Read and Validate Inputs ---
      const inputs = {
        startRevenue: parseFloat(inputElements.startRevenue.value.replace(/[^0-9.-]+/g, '')) || 0,
        fixedCosts: parseFloat(inputElements.fixedCosts.value.replace(/[^0-9.-]+/g, '')) || 0,
        cashOnHand: parseFloat(inputElements.cashOnHand.value.replace(/[^0-9.-]+/g, '')) || 0,
        arpu: parseFloat(inputElements.arpu.value.replace(/[^0-9.-]+/g, '')) || 0,
        marketingSpend: parseFloat(inputElements.marketingSpend.value.replace(/[^0-9.-]+/g, '')) || 0,
        growthTarget: parseFloat(inputElements.growthTarget.value) || 0,
        variableCostPercent: parseFloat(inputElements.variableCostPercent.value) || 0,
        duration: parseInt(inputElements.duration.value, 10) || 12,
      }

      // Basic validation
      if (inputs.startRevenue <= 0 || inputs.duration <= 0) {
        alert('Please enter a valid Starting Revenue and Projection Duration.')
        return
      }
      if (inputs.variableCostPercent < 0 || inputs.variableCostPercent > 100) {
        alert('Variable Cost % must be between 0 and 100.')
        return
      }
      if (inputs.growthTarget < -50 || inputs.growthTarget > 100) { // Example reasonable bounds
        alert('Monthly Growth Target % seems unusual. Please check the value (e.g., enter 5 for 5%).')
        return
      }

      // --- 2. Calculate Projection ---
      const results = calculateFinancialProjection(inputs)

      // --- 3. Populate Output UI ---
      outputElements.runway.textContent = results.runwayMonths ? `${results.runwayMonths} Months` : 'Cash Positive'
      outputElements.breakEven.textContent = results.breakEvenMonth ? `${results.breakEvenMonth} Months` : 'Doesn\'t Break Even'
      outputElements.grossMargin.textContent = `${results.avgGrossMargin.toFixed(1)} %`

      // Simple Viability Logic
      if (results.runwayMonths === null && results.breakEvenMonth !== null && results.breakEvenMonth <= 12) {
        outputElements.viability.textContent = '🟢 Healthy Growth'
        outputElements.viability.className = 'text-xl font-bold text-green-600 mt-1'
      } else if (results.runwayMonths === null || (results.runwayMonths > inputs.duration)) {
        outputElements.viability.textContent = '🟡 Growth Required'
        outputElements.viability.className = 'text-xl font-bold text-yellow-600 mt-1'
      } else if (results.runwayMonths > 6) {
        outputElements.viability.textContent = '🟠 High Burn Rate'
        outputElements.viability.className = 'text-xl font-bold text-orange-600 mt-1'
      } else {
        outputElements.viability.textContent = '🔴 Unsustainable'
        outputElements.viability.className = 'text-xl font-bold text-red-600 mt-1'
      }

      // Populate Table
      if (outputElements.tableBody) {
        outputElements.tableBody.innerHTML = results.monthlyProjections.map((row) => `
                <tr class="border-t">
                    <td class="p-2 text-gray-600">Month ${row.month}</td>
                    <td class="p-2 text-right font-mono">${formatCurrency(row.revenue)}</td>
                    <td class="p-2 text-right font-mono">${formatCurrency(row.grossProfit)}</td>
                    <td class="p-2 text-right font-mono">${formatCurrency(row.ebitda)}</td>
                    <td class="p-2 text-right font-mono ${row.endingCash < 0 ? 'text-red-600' : ''}">${formatCurrency(row.endingCash)}</td>
                </tr>
            `).join('')
      }

      // Populate Charts
      const chartLabels = results.monthlyProjections.map((p) => `Month ${p.month}`)
      const revenueData = results.monthlyProjections.map((p) => p.revenue)
      const totalCostData = results.monthlyProjections.map((p) => inputs.fixedCosts + inputs.marketingSpend + (p.revenue * inputs.variableCostPercent / 100))
      const cashData = results.monthlyProjections.map((p) => p.endingCash)

      createChart('projection-break-even-chart', 'line', {
        labels: chartLabels,
        datasets: [
          { label: 'Projected Revenue', data: revenueData, borderColor: '#3B82F6', tension: 0.1 },
          { label: 'Total Costs', data: totalCostData, borderColor: '#EF4444', tension: 0.1 },
        ],
      }, mergeChartOptions(chartYTicks(shortenCurrency), chartTooltip({ label: currencyTooltipCallback })))

      createChart('projection-cash-balance-chart', 'line', {
        labels: chartLabels,
        datasets: [
          { label: 'Ending Cash Balance', data: cashData, borderColor: '#10B981', backgroundColor: 'rgba(16, 185, 129, 0.1)', fill: true, tension: 0.1 },
        ],
      }, mergeChartOptions(
        { scales: { y: { beginAtZero: false, ticks: { callback: (n: any) => shortenCurrency(n) } } } }, // Allow negative axis
        chartTooltip({ label: currencyTooltipCallback }),
      ))

      // Show the output section
      outputSection?.classList.remove('hidden')
    })

    // Initialize AI summary for this specific view if needed (can be added later)
    // setupPageSummary({ pageId: 'premium-placeholder-projection', analyzeUsingAI: getGeminiAnalysis });
  }

  async function handleExportToPdf() {
    const reportContent = document.getElementById('pdf-preview-content')
    const exportButton = document.getElementById('export-to-pdf-btn')
    if (!reportContent || !exportButton) return

    showLoading({ message: 'Initializing PDF export...' })
    exportButton.setAttribute('disabled', 'true')

    try {
      const { jsPDF } = jspdf; // Get the constructor
      const pdf = new jsPDF('p', 'mm', 'a4'); // Create new PDF in Portrait, A4
      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      const pageMargin = 15; // 15mm margin on left and right
      const contentWidth = pdfPageWidth - (pageMargin * 2);
      let currentY = 15; // Start 15mm from the top
      const spacing = 5; // 5mm spacing after each element

      // --- Helper for standard, full-width elements ---
      const addElementToPdf = async (element: HTMLElement | null) => {
        if (!element) return; // Skip if element doesn't exist

        const canvas = await html2canvas(element, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/png');
        
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const pdfImgWidth = contentWidth; // Force image to fit content width
        const pdfImgHeight = (imgHeight * pdfImgWidth) / imgWidth; // Maintain aspect ratio
        
        // Check if it fits on the current page
        if (currentY + pdfImgHeight + spacing > pdfPageHeight - pageMargin) {
          pdf.addPage(); // Add a new page
          currentY = pageMargin; // Reset Y to top margin
        }

        pdf.addImage(imgData, 'PNG', pageMargin, currentY, pdfImgWidth, pdfImgHeight);
        currentY += pdfImgHeight + spacing; // Update Y position
      }
      
      // --- Helper for side-by-side elements ---
      const addSideBySideElementsToPdf = async (elementLeft: HTMLElement | null, elementRight: HTMLElement | null) => {
        if (!elementLeft || !elementRight) {
          await addElementToPdf(elementLeft);
          await addElementToPdf(elementRight);
          return;
        }

        const canvasLeft = await html2canvas(elementLeft, { scale: 2, useCORS: true });
        const canvasRight = await html2canvas(elementRight, { scale: 2, useCORS: true });

        const imgDataLeft = canvasLeft.toDataURL('image/png');
        const imgDataRight = canvasRight.toDataURL('image/png');

        const columnSpacing = 4; // 4mm spacing between charts
        const pdfImgWidth = (contentWidth / 2) - (columnSpacing / 2);
        
        const pdfImgHeightLeft = (canvasLeft.height * pdfImgWidth) / canvasLeft.width;
        const pdfImgHeightRight = (canvasRight.height * pdfImgWidth) / canvasRight.width;
        
        const maxImgHeight = Math.max(pdfImgHeightLeft, pdfImgHeightRight);

        if (currentY + maxImgHeight + spacing > pdfPageHeight - pageMargin) {
          pdf.addPage();
          currentY = pageMargin;
        }

        const xLeft = pageMargin;
        const xRight = pageMargin + pdfImgWidth + columnSpacing;
        
        pdf.addImage(imgDataLeft, 'PNG', xLeft, currentY, pdfImgWidth, pdfImgHeightLeft);
        pdf.addImage(imgDataRight, 'PNG', xRight, currentY, pdfImgWidth, pdfImgHeightRight);

        currentY += maxImgHeight + spacing;
      }
      

      // --- [NEW] Processing elements with Page Break ---
      showLoading({ message: 'Generating PDF (Page 1)...' })
      const header = reportContent.querySelector('div:nth-child(1)') as HTMLElement;
      await addElementToPdf(header);

      const kpis = reportContent.querySelector('div:nth-child(2)') as HTMLElement;
      await addElementToPdf(kpis);
      
      const pnlTable = document.getElementById('pdf-pnl-data-table');
      await addElementToPdf(pnlTable);

      const pnlAiSummary = document.getElementById('pdf-pnl-ai-summary'); // This is the first AI box (purple)
      await addElementToPdf(pnlAiSummary);

      // --- [THIS IS THE FIX] ---
      // Force a new page after the P&L AI summary.
      pdf.addPage();
      currentY = pageMargin; // Reset Y to the top margin for the new page
      // --- [END FIX] ---

      showLoading({ message: 'Generating PDF (Page 2)...' })
      const omzetChart = document.getElementById('export-omzet-trend-chart')?.closest('.bg-white') as HTMLElement;
      const channelChart = document.getElementById('export-channel-chart')?.closest('.bg-white') as HTMLElement;
      await addSideBySideElementsToPdf(omzetChart, channelChart); // Add side-by-side
      
      const topMenuChart = document.getElementById('export-top-menu-chart')?.closest('.bg-white') as HTMLElement;
      await addElementToPdf(topMenuChart); // Add full-width

      const pnlAiSummary2 = document.getElementById('pdf-sales-ai-summary'); // This is the second AI box (indigo)
      await addElementToPdf(pnlAiSummary2); // Add full-width
      // --- [END] New processing logic ---

      pdf.save('Finalytics_Report.pdf');

    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('An error occurred while generating the PDF. Please try again.')
    } finally {
      hideLoading()
      exportButton.removeAttribute('disabled')
    }
  }
  

  const userEmailDisplay = document.getElementById('premium-user-email-display')
  if (userEmailDisplay && currentUser?.email) {
    userEmailDisplay.textContent = `Hello //${currentUser.email}`
  }
  // --- Get all necessary DOM elements ---
  const premiumView = document.getElementById('premium-analysis-view')
  const dashboardContent = document.getElementById('premium-dashboard-content')
  const manageDataContent = document.getElementById('premium-manage-data-content')
  const userManagementContent = document.getElementById('premium-user-management-content')
  const branchMembersContent = document.getElementById('premium-branch-members-content')
  const branchesContent = document.getElementById('premium-branches-content')
  const brandsContent = document.getElementById('premium-brands-content')
  const configurationContent = document.getElementById('premium-configuration-content')
  const exportPdfContent = document.getElementById('premium-export-pdf-content')
  const recommendationContent = document.getElementById('premium-recommendation-content')
  const highlightContent = document.getElementById('premium-business-highlight-content')
  const allMainContent = document.querySelectorAll('.premium-analysis-content')
  const dashboardBtn = document.getElementById('premium-goto-dashboard-btn')
  const manageDataBtn = document.getElementById('premium-goto-manage-data-btn')
  const userManagementBtn = document.getElementById('premium-goto-user-management-btn')
  const branchMembersBtn = document.getElementById('premium-goto-branch-members-btn')
  const branchesBtn = document.getElementById('premium-goto-branches-btn')
  const brandsBtn = document.getElementById('premium-goto-brands-btn')
  const configurationBtn = document.getElementById('premium-goto-configuration-btn')
  const exportPdfNavBtn = document.getElementById('premium-goto-export-pdf-btn')
  const recommendationBtn = document.getElementById('premium-goto-recommendation-btn')
  const highlightBtn = document.getElementById('premium-goto-business-highlight-btn')
  const configBackBtn = document.getElementById('premium-config-back-btn')
  const downloadSalesTargetBtn = document.getElementById('premium-download-sales-target-btn')
  const downloadPnlDataBtn = document.getElementById('premium-download-pnl-data-btn')
  const downloadPnlTargetBtn = document.getElementById('premium-download-pnl-target-btn')

  if (!premiumView) return

  // Subscribe to selectedBranchId changes and keep branch-related selects in sync.
  // This prevents UI drift when selectedBranchId is updated elsewhere.
  try {
    $store.subscribeToSelectedBranch((branchId) => {
      const uploadTarget = document.getElementById('premium-upload-target-branch-select') as HTMLSelectElement | null
      if (uploadTarget && uploadTarget.value !== (branchId || '')) uploadTarget.value = branchId || ''

      const membersSelect = document.getElementById('premium-branch-members-branch-select') as HTMLSelectElement | null
      if (membersSelect && membersSelect.value !== (branchId || '')) membersSelect.value = branchId || ''

      const topBranchDropdown = document.getElementById('branch-selector-dropdown') as HTMLSelectElement | null
      if (topBranchDropdown && topBranchDropdown.value !== (branchId || '')) topBranchDropdown.value = branchId || ''
    })
  } catch (err) {
    console.warn('[PREMIUM] subscribeToSelectedBranch is not available or failed', err)
  }

  // --- State variables in a shared scope for nested functions ---
  let hasLoadedManageData = false
  let hasLoadedUserData = false
  let hasInitializedPdfPage = false
  let selectedSalesFormat: 'ESB' | 'MOKA' | null = null
  let aggregatedData: Record<string, any> = {}
  let currentPage = 1
  let currentReportDataForAI: object | null = null
  let currentEditingDeck: { id: string, slides: any[] } | null = null
  let currentSlideIndex = 0
  let currentSlideChartInstance: Chart | null = null;
  let deckHasChanges = false;
  let currentConfigBranchId: string | null = null;
  const CARDS_PER_PAGE = 8

  function generateTop5MenuChart(summaries: SalesSummary[], canvasId: string) {
    const menuRevenues = new Map<string, number>()

    summaries.forEach((summary) => {
      if (summary.menuItemRevenues) {
        for (const category in summary.menuItemRevenues) {
          for (const itemName in summary.menuItemRevenues[category]) {
            const revenue = summary.menuItemRevenues[category][itemName]
            menuRevenues.set(itemName, (menuRevenues.get(itemName) || 0) + revenue)
          }
        }
      }
    })

    const sortedTop5 = Array.from(menuRevenues.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)

    createChart(canvasId, 'bar', {
      labels: sortedTop5.map(([name]) => name),
      datasets: [{
        label: 'Total Revenue',
        data: sortedTop5.map(([, revenue]) => revenue),
        backgroundColor: '#4f46e5',
      }],
    }, mergeChartOptions(
      { indexAxis: 'y' }, // Make it a horizontal bar chart
      { scales: { x: { ticks: { callback: (n: string | number) => shortenCurrency(Number(n)) } } } },
      chartTooltip({ label: currencyTooltipCallback }),
    ))
  }

  // In: apps/web/src/analysis/premium/orchestrator.ts
// ADD THIS NEW FUNCTION

/**
 * Renders a simple P&L data table into a container.
 * This is a simplified version for the PDF export page.
 */
function renderSimplePnlTable(
  pnlData: any, // Expects pnlData from the report
  containerId: string
) {
  const container = document.getElementById(containerId)
  if (!container) return

  if (!pnlData) {
    container.innerHTML = `<p class="text-sm text-gray-500 p-4">P&L data is not available for this period.</p>`
    return
  }

  // Use the same metrics from the historical table
  const metrics = calculateAllPnlMetrics(pnlData || {})
  const allMetricKeys = [
    'Pendapatan (Revenue)',
    'Harga Pokok Produksi',
    'Laba Kotor (Gross Profit)',
    'Beban Operasional (OPEX)',
    'Pendapatan Bersih Operasional (Net Operating Income)',
    'Beban Non Operasional',
    'Pendapatan Bersih Sebelum Deprisiasi/Amortisasi, Bunga & Pajak (EBITDA)',
    'Depresiasi/ Amortisasi',
    'Bunga',
    'Pajak (PB1)',
    'Pendapatan Bersih (Net Income)',
  ]

  let tableHtml = `
    <table class="min-w-full divide-y divide-gray-200">
      <tbody class="bg-white divide-y divide-gray-200">
  `

  allMetricKeys.forEach(key => {
    const value = metrics[key] || 0
    // Check if this is a main category (has sub-items) or a calculated total
    const isMainCategory = pnlData[key] && Object.keys(pnlData[key]).length > 0
    const isTotal = key.includes('Laba Kotor') || key.includes('Net Income') || key.includes('EBITDA') || key.includes('Net Operating Income')

    // Apply bolding for totals and main categories
    const labelClass = (isMainCategory || isTotal) ? 'font-semibold text-gray-800' : 'text-gray-700 pl-4'
    const valueClass = (isMainCategory || isTotal) ? 'font-semibold text-gray-800' : 'text-gray-600'

    tableHtml += `
      <tr>
        <td class="px-4 py-2 text-left text-sm ${labelClass}">${key}</td>
        <td class="px-4 py-2 text-right text-sm ${valueClass} font-mono">${formatCurrency(value)}</td>
      </tr>
    `
    
    // If it's a main category, list its sub-items
    if (isMainCategory) {
      for (const subItem in pnlData[key]) {
        const subValue = pnlData[key][subItem] || 0
        tableHtml += `
          <tr class="bg-gray-50">
            <td class="px-4 py-1 text-left text-sm text-gray-600 pl-8">${subItem}</td>
            <td class="px-4 py-1 text-right text-sm text-gray-600 font-mono">${formatCurrency(subValue)}</td>
          </tr>
        `
      }
    }
  })

  tableHtml += `</tbody></table>`
  container.innerHTML = tableHtml
}

 async function generatePdfReportData() {
    showLoading({ message: 'Generating report preview...' })

    // 1. Get DOM Elements and Current Filter Values
    const branchSelect = document.getElementById('export-pdf-branch-select') as HTMLSelectElement
    const rangeSelect = document.getElementById('export-pdf-range-select') as HTMLSelectElement
    const periodSelect = document.getElementById('export-pdf-period-select') as HTMLSelectElement

    if (!branchSelect || !rangeSelect || !periodSelect || !periodSelect.value) {
      hideLoading()
      return null
    }

    const selectedBranch = branchSelect.value
    const selectedRange = rangeSelect.value
    const selectedPeriod = periodSelect.value

    let startDate: Date, endDate: Date, prevStartDate: Date, prevEndDate: Date, comparisonLabel: string

    // 2. Calculate Date Ranges and Labels
    try {
      const [yearStr, partStr] = selectedPeriod.split('-')
      const year = parseInt(yearStr)

      switch (selectedRange) {
        case 'yearly':
          startDate = new Date(year, 0, 1)
          endDate = new Date(year, 11, 31, 23, 59, 59)
          const prevYear = year - 1
          prevStartDate = new Date(prevYear, 0, 1)
          prevEndDate = new Date(prevYear, 11, 31, 23, 59, 59)
          currentPeriodStr = year.toString()
          prevPeriodStr = prevYear.toString()
          comparisonLabel = `vs. Last Year (${prevYear})`
          break
        case 'quarterly':
          const quarter = parseInt(partStr.replace('Q', ''))
          const startMonth = (quarter - 1) * 3
          startDate = new Date(year, startMonth, 1)
          endDate = new Date(year, startMonth + 3, 0, 23, 59, 59)

          let prevQuarter, prevQuarterYear
          if (quarter === 1) {
            prevQuarter = 4
            prevQuarterYear = year - 1
          } else {
            prevQuarter = quarter - 1
            prevQuarterYear = year
          }
          const prevStartMonth = (prevQuarter - 1) * 3
          prevStartDate = new Date(prevQuarterYear, prevStartMonth, 1)
          prevEndDate = new Date(prevQuarterYear, prevStartMonth + 3, 0, 23, 59, 59)
          currentPeriodStr = `${year}-Q${quarter}`
          prevPeriodStr = `${prevQuarterYear}-Q${prevQuarter}`
          comparisonLabel = `vs. Last Quarter (Q${prevQuarter} ${prevQuarterYear})`
          break
        case 'monthly':
        default:
          const month = parseInt(partStr) - 1
          startDate = new Date(year, month, 1)
          endDate = new Date(year, month + 1, 0, 23, 59, 59)

          prevStartDate = new Date(startDate)
          prevStartDate.setMonth(prevStartDate.getMonth() - 1)
          prevEndDate = new Date(prevStartDate.getFullYear(), prevStartDate.getMonth() + 1, 0, 23, 59, 59)
          currentPeriodStr = `${year}-${partStr}`
          prevPeriodStr = `${prevStartDate.getFullYear()}-${String(prevStartDate.getMonth() + 1).padStart(2, '0')}`
          comparisonLabel = `vs. Last Month (${prevStartDate.toLocaleString('default', { month: 'long', year: 'numeric' })})`
          break
      }
    } catch (e) {
      console.error('Error parsing date range for PDF report:', e)
      hideLoading()
      return null
    }

    // 3. Filter Sales Data
    let baseData = $store.getAllSalesData()
    if (selectedBranch !== 'ALL') {
      baseData = baseData.filter((s) => s.branches.includes(selectedBranch))
    }
    const currentData = baseData.filter((s) => s.date >= startDate && s.date <= endDate)
    const previousData = baseData.filter((s) => s.date >= prevStartDate && s.date <= prevEndDate)

    // 4. Calculate Sales KPIs
    const calculateTotals = (data: SalesSummary[]) => data.reduce((acc, s) => {
      acc.omzet += s.totalOmzet
      acc.transactions += s.totalTransactions
      return acc
    }, { omzet: 0, transactions: 0 })

    const currentTotals = calculateTotals(currentData)
    const previousTotals = calculateTotals(previousData)
    const currentAvgCheck = currentTotals.transactions > 0 ? currentTotals.omzet / currentTotals.transactions : 0
    const previousAvgCheck = previousTotals.transactions > 0 ? previousTotals.omzet / previousTotals.transactions : 0

    // 5. Fetch P&L Reports
    let currentNetProfit = 0
    let previousNetProfit = 0
    let currentPnlReport: any = null 
    
    const allHistoricalPnlReports = await (async () => {
      if (!currentUser) return []
      
      // --- [THIS IS THE FIX] ---
      const { isDemoDataActive } = $store.getAnalysisState().config;
      
      if (isDemoDataActive) {
        // DEMO MODE: Fetch from 'demo' collection
        try {
          const reportsRef = getPnlReportsCollectionRef('demo');
          const reportsSnap = await getDocs(reportsRef);
          return reportsSnap.docs.map((doc) => doc.data());
        } catch (err) {
          console.error('Error fetching DEMO P&L for PDF report:', err);
          return [];
        }
      } else {
        // REAL DATA MODE: Fetch by Branch ID
        try {
          const { userBranches } = $store.getAccountContext()
          const selectedBranchId = userBranches.find(b => b.branchName === selectedBranch)?.branchId || null
          if (selectedBranch === 'ALL' || !selectedBranchId) {
              console.warn('Cannot fetch P&L for "All Branches" or branch not found:', selectedBranch)
              return []
          }
          const reportsRef = collection(db, paths.branches(selectedBranchId).pnlReports.collectionPath)
          const reportsSnap = await getDocs(reportsRef)
          return reportsSnap.docs.map((doc) => doc.data())
        } catch (err) {
          console.error('Error fetching historical P&L for PDF report:', err)
          return []
        }
      }
      // --- [END FIX] ---
    })()

    const currentPnlReports = allHistoricalPnlReports.filter((report) => {
      const reportDate = new Date(report.period + '-02')
      return reportDate >= startDate && reportDate <= endDate
    })
    const previousPnlReports = allHistoricalPnlReports.filter((report) => {
      const reportDate = new Date(report.period + '-02')
      return reportDate >= prevStartDate && reportDate <= prevEndDate
    })

    currentPnlReport = currentPnlReports[0]
    
    currentNetProfit = currentPnlReports.reduce((sum, report) => {
      const metrics = calculateAllPnlMetrics(report.pnlData || {})
      return sum + (metrics['Pendapatan Bersih (Net Income)'] || 0)
    }, 0)

    previousNetProfit = previousPnlReports.reduce((sum, report) => {
      const metrics = calculateAllPnlMetrics(report.pnlData || {})
      return sum + (metrics['Pendapatan Bersih (Net Income)'] || 0)
    }, 0)

    // 6. Update the DOM
    const updateKpiCard = (cardIndex: number, currentValue: number, previousValue: number, isCurrency: boolean) => {
      const card = document.querySelector(`#pdf-preview-content .grid > div:nth-child(${cardIndex})`)
      if (!card) return;
      (card.querySelector('p:nth-of-type(1)') as HTMLElement).textContent = isCurrency ? formatCurrency(currentValue) : formatNumber(currentValue)
      const growthEl = card.querySelector('p:nth-of-type(2)') as HTMLElement
      if (previousValue > 0) {
        const growth = ((currentValue - previousValue) / previousValue) * 100
        growthEl.textContent = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}% ${comparisonLabel}`
        growthEl.className = `text-sm mt-1 font-medium ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`
      } else {
        growthEl.textContent = 'vs N/A'
        growthEl.className = 'text-sm mt-1 font-medium text-gray-500'
      }
    }
    const reportHeaderEl = document.querySelector('#pdf-preview-content .text-gray-500') as HTMLElement
    if (reportHeaderEl) {
      reportHeaderEl.textContent = `${selectedBranch} | ${startDate.toLocaleDateString('en-GB')} - ${endDate.toLocaleDateString('en-GB')}`
    }
    updateKpiCard(1, currentTotals.omzet, previousTotals.omzet, true)
    updateKpiCard(2, currentNetProfit, previousNetProfit, true)
    updateKpiCard(3, currentTotals.transactions, previousTotals.transactions, false)
    updateKpiCard(4, currentAvgCheck, previousAvgCheck, true)
    
    renderSimplePnlTable(currentPnlReport?.pnlData, 'pdf-pnl-data-table')

    // 8. Generate Charts
    if (selectedRange === 'monthly') {
      generateOmzetHarianChartFromSummaries(currentData, 'export-omzet-trend-chart')
    } else {
      generateOmzetMingguanChartFromSummaries(currentData, 'export-omzet-trend-chart', 'line')
    }
    const channelData = generateChannelDonutChart(currentData, 'export-channel-chart')
    const top5MenuData = generateTop5MenuChart(currentData, 'export-top-menu-chart')
    
    // 9. Build the AI Data Object
    const reportDataForAI = {
      context: { branch: selectedBranch, period: selectedPeriod, range: selectedRange, comparisonLabel: comparisonLabel },
      currentPeriod: { totalOmzet: currentTotals.omzet, totalTransactions: currentTotals.transactions, averageCheck: currentAvgCheck, netProfit: currentNetProfit },
      previousPeriod: { totalOmzet: previousTotals.omzet, totalTransactions: previousTotals.transactions, averageCheck: previousAvgCheck, netProfit: previousNetProfit },
      top5MenuItems: top5MenuData,
      salesByChannel: channelData,
      pnlReport: currentPnlReport || null,
      pnlTarget: null,
    }

    // Check cache for BOTH AI summaries
    const salesAiContainer = document.getElementById('pdf-sales-ai-summary')
    const pnlAiContainer = document.getElementById('pdf-pnl-ai-summary')
    const salesAiContentEl = salesAiContainer?.querySelector('p')
    const pnlAiContentEl = pnlAiContainer?.querySelector('p')

    if (salesAiContentEl && pnlAiContentEl) {
      try {
        const filters = (reportDataForAI as any).context
        const filtersHash = await generateSHA256(filters)
        const dataHash = await generateSHA256(reportDataForAI)

        const cachedSales = await findLiveCache(filtersHash, dataHash)
        if (cachedSales) {
          salesAiContentEl.innerHTML = marked.parse(cachedSales.summary)
        } else {
          salesAiContentEl.innerHTML = `<em>Click 'Generate AI Summary' to get new insights for the selected period.</em>`
        }

        const pnlFiltersHash = await generateSHA256({ ...filters, type: 'pnl' })
        const cachedPnl = await findLiveCache(pnlFiltersHash, dataHash)
        if (cachedPnl) {
          pnlAiContentEl.innerHTML = marked.parse(cachedPnl.summary)
        } else {
          pnlAiContentEl.innerHTML = `<em>Click 'Generate AI Summary' to get new P&L insights.</em>`
        }
      } catch (e) {
        console.error('Error checking AI cache:', e)
        salesAiContentEl.innerHTML = `<span class="text-red-500">Could not check for cached summary.</span>`
        pnlAiContentEl.innerHTML = `<span class="text-red-500">Could not check for cached summary.</span>`
      }
    }

    hideLoading()
    return reportDataForAI
  }

  async function setupPdfExportPage() {
    const refreshReport = async () => {
      currentReportDataForAI = await generatePdfReportData()
    }

    // 1. Get DOM Elements for the filters
    const branchSelect = document.getElementById('export-pdf-branch-select') as HTMLSelectElement
    const rangeSelect = document.getElementById('export-pdf-range-select') as HTMLSelectElement
    const periodSelect = document.getElementById('export-pdf-period-select') as HTMLSelectElement

    const filters = [
      document.getElementById('export-pdf-branch-select'),
      document.getElementById('export-pdf-range-select'),
      document.getElementById('export-pdf-period-select'),
    ]
    filters.forEach((el) => el?.addEventListener('change', refreshReport))

    if (!branchSelect || !rangeSelect || !periodSelect) return

    // --- [THIS IS THE FIX] ---
    // 2. Populate the Branch Selector with Demo or Real data
    const allSalesData: SalesSummary[] = $store.getAllSalesData()
    const { userBranches, selectedBrandId } = $store.getAccountContext()
    const { isDemoDataActive } = $store.getAnalysisState().config;

    let brandSalesData: SalesSummary[];

    if (isDemoDataActive) {
      // DEMO MODE: Use all data
      console.log('[Orchestrator] Demo mode active in setupPdfExportPage');
      brandSalesData = allSalesData;
    } else {
      // REAL DATA MODE: Filter by brand
      const filteredBranches = selectedBrandId
        ? userBranches.filter(b => b.brandId === selectedBrandId)
        : userBranches
      
      const filteredBranchNames = new Set(filteredBranches.map(b => b.branchName))
      
      brandSalesData = allSalesData.filter(s => 
        s.branches.some(bName => filteredBranchNames.has(bName))
      )
    }

    const branches = [...new Set(brandSalesData.flatMap((s) => s.branches))].sort()
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('')
    // --- [END FIX] ---

    // 3. Define the core logic to dynamically update the Period dropdown
    const updatePeriodSelector = () => {
      const range = rangeSelect.value
      const selectedBranch = branchSelect.value

      // Filter by branch selection
      let branchData = brandSalesData
      if (selectedBranch !== 'ALL') {
        branchData = brandSalesData.filter((s) => s.branches.includes(selectedBranch))
      }
      
      const allDates = branchData.map((s) => s.date)
      if (allDates.length === 0) {
        periodSelect.innerHTML = '<option>No data available for this brand</option>'
        return
      }

      const periods = new Set<string>()

      if (range === 'yearly') {
        allDates.forEach((d) => periods.add(String(d.getFullYear())))
      } else if (range === 'monthly') {
        allDates.forEach((d) => periods.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`))
      } else if (range === 'quarterly') {
        allDates.forEach((d) => {
          const quarter = Math.floor(d.getMonth() / 3) + 1
          periods.add(`${d.getFullYear()}-Q${quarter}`)
        })
      }

      const sortedPeriods = Array.from(periods).sort((a, b) => b.localeCompare(a))
      if (sortedPeriods.length === 0) {
        periodSelect.innerHTML = '<option>No data for this range</option>'
        return
      }

      const optionsHtml = sortedPeriods.map((p) => {
        if (range === 'yearly') return `<option value="${p}">${p}</option>`
        if (range === 'monthly') {
          const [year, month] = p.split('-')
          const label = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
          return `<option value="${p}">${label}</option>`
        }
        if (range === 'quarterly') return `<option value="${p}">${p.replace('-Q', ' Q')}</option>`
        return ''
      }).join('')

      periodSelect.innerHTML = optionsHtml
    }

    // 4. Attach Event Listeners to trigger updates
    branchSelect.addEventListener('change', () => {
      updatePeriodSelector()
      generatePdfReportData() 
    })
    rangeSelect.addEventListener('change', () => {
      updatePeriodSelector()
      generatePdfReportData() 
    })
    periodSelect.addEventListener('change', generatePdfReportData)

    // 5. Initial Population when the page first loads
    updatePeriodSelector()
    await generatePdfReportData()
  }

  async function setupExclusionControls(branchId: string | null) {
    if (!currentUser || !branchId) {
      console.warn('[Config] setupExclusionControls skipped, no branchId provided.');
      return;
    }

    // 1. Get sales data for THIS SPECIFIC BRANCH only
    const allSalesData: SalesSummary[] = $store.getAllSalesData()
    const { userBranches } = $store.getAccountContext()

    const targetBranch = userBranches.find(b => b.branchId === branchId);
    if (!targetBranch) {
        console.error('Branch not found in context:', branchId);
        return;
    }
    
    // Filter data to only show items relevant to this branch
    const branchSalesData = allSalesData.filter(s => s.branches.includes(targetBranch.branchName))

    // 2. Populate dropdowns with items from this branch
    const uniqueCategories = new Set<string>()
    const uniqueMenus = new Set<string>()
    const uniqueChannels = new Set<string>()

    branchSalesData.forEach((summary) => {
      if (summary.menuItemQuantities) {
        for (const category in summary.menuItemQuantities) {
          if (category !== 'Uncategorized') uniqueCategories.add(category)
          for (const menuName in summary.menuItemQuantities[category]) {
            uniqueMenus.add(menuName)
          }
        }
      }
      if (summary.revenueByVisitPurpose) {
        for (const channel in summary.revenueByVisitPurpose) {
          uniqueChannels.add(channel)
        }
      }
    })

    // 3. Re-Initialize SlimSelects (Safe Mode)
    const categorySelectEl = document.getElementById('hide-menu-category-select') as HTMLSelectElement
    const menuSelectEl = document.getElementById('hide-menu-select') as HTMLSelectElement
    const channelSelectEl = document.getElementById('hide-sales-channel-select') as HTMLSelectElement 
    
    if (!categorySelectEl || !menuSelectEl || !channelSelectEl) return;

    // [FIX] Properly destroy existing instances if they exist
    if ((categorySelectEl as any).slim) { (categorySelectEl as any).slim.destroy(); }
    if ((menuSelectEl as any).slim) { (menuSelectEl as any).slim.destroy(); }
    if ((channelSelectEl as any).slim) { (channelSelectEl as any).slim.destroy(); }

    // Create new instances and attach them to the DOM element so we can find them later
    const categorySelect = new SlimSelect({
      select: categorySelectEl,
       data: Array.from(uniqueCategories).sort().map((name) => ({ text: name, value: name })),
      settings: { placeholderText: 'Select categories...', closeOnSelect: false, allowDeselect: true },
    });
    (categorySelectEl as any).slim = categorySelect;

    const menuSelect = new SlimSelect({
      select: menuSelectEl,
      data: Array.from(uniqueMenus).sort().map((name) => ({ text: name, value: name })),
      settings: { placeholderText: 'Select menu items...', closeOnSelect: false, allowDeselect: true },
    });
    (menuSelectEl as any).slim = menuSelect;

    const channelSelect = new SlimSelect({
      select: channelSelectEl,
      data: Array.from(uniqueChannels).sort().map((name) => ({ text: name, value: name })),
      settings: { placeholderText: 'Select channels...', closeOnSelect: false, allowDeselect: true },
    });
    (channelSelectEl as any).slim = channelSelect;

    // 4. Load *BRANCH-specific* settings from Firestore
    try {
      const branchDocRef = doc(db, paths.branches(branchId).path);
      const docSnap = await getDoc(branchDocRef)
      
      // Clear current selections first
      categorySelect.setSelected([]);
      menuSelect.setSelected([]);
      channelSelect.setSelected([]);

      if (docSnap.exists()) {
        const data = docSnap.data() || {};
        const exclusions = data.exclusions || {}; 
        if (exclusions.hiddenCategories) categorySelect.setSelected(exclusions.hiddenCategories)
        if (exclusions.hiddenMenus) menuSelect.setSelected(exclusions.hiddenMenus)
        if (exclusions.hiddenChannels) channelSelect.setSelected(exclusions.hiddenChannels)
      }
    } catch (error) {
      console.error('Error loading exclusion settings:', error)
    }

    // 5. Update the save listener
    // We clone the button to remove old event listeners (cleanest way without named functions)
    const oldSaveBtn = document.getElementById('save-exclusions-btn');
    const saveBtn = oldSaveBtn?.cloneNode(true) as HTMLElement;
    oldSaveBtn?.parentNode?.replaceChild(saveBtn, oldSaveBtn);

    const feedbackEl = document.getElementById('global-targets-feedback')

    saveBtn?.addEventListener('click', async () => {
      if (!currentUser || !feedbackEl || !branchId) return

      const exclusionsToSave = {
        hiddenCategories: categorySelect.getSelected(),
        hiddenMenus: menuSelect.getSelected(),
        hiddenChannels: channelSelect.getSelected(),
      };

      try {
        const branchDocRef = doc(db, paths.branches(branchId).path);
        await setDoc(branchDocRef, { 
          exclusions: exclusionsToSave 
        }, { merge: true });

        feedbackEl.className = 'p-4 text-sm rounded-md bg-green-100 text-green-800'
        feedbackEl.textContent = 'Branch exclusion settings saved successfully!'
        feedbackEl.classList.remove('hidden')
      } catch (error) {
        feedbackEl.className = 'p-4 text-sm rounded-md bg-red-100 text-red-800'
        feedbackEl.textContent = 'Error saving exclusion settings. Please try again.'
        feedbackEl.classList.remove('hidden')
      }
      setTimeout(() => feedbackEl.classList.add('hidden'), 4000)
    })
  }

  async function loadGlobalTargets(branchId: string | null) {
    if (!currentUser || !branchId) return;
    showLoading({ message: 'Loading branch targets...' });

    try {
      const branchDocRef = doc(db, paths.branches(branchId).path);
      const docSnap = await getDoc(branchDocRef);

      // Reset ALL inputs
      const inputs = [
          'global-target-omzet', 'global-target-transactions', 'global-target-avg-check',
          'global-target-cogs', 'global-target-opex', 'global-target-non-opex', // Renamed/New
          'global-target-depreciation', 'global-target-interest', 'global-target-tax' // New
      ];
      inputs.forEach(id => {
          const el = document.getElementById(id) as HTMLInputElement;
          if (el) el.value = '';
      });

      if (docSnap.exists()) {
        const data = docSnap.data() || {};
        const targets = data.targets || {}; 

        if (targets.sales) {
          (document.getElementById('global-target-omzet') as HTMLInputElement).value = targets.sales.totalOmzet || '';
          (document.getElementById('global-target-transactions') as HTMLInputElement).value = targets.sales.totalTransactions || '';
          (document.getElementById('global-target-avg-check') as HTMLInputElement).value = targets.sales.avgCheck || '';
        }
        
        if (targets.pnl) {
          // [FIX] Map new fields
          (document.getElementById('global-target-cogs') as HTMLInputElement).value = targets.pnl.cogsPercent || '';
          
          // Use new opexPercent if available, fallback to wages+rent for backward compatibility
          const oldOpex = (targets.pnl.wagesPercent || 0) + (targets.pnl.rentPercent || 0);
          (document.getElementById('global-target-opex') as HTMLInputElement).value = targets.pnl.opexPercent || (oldOpex > 0 ? oldOpex : '');

          (document.getElementById('global-target-non-opex') as HTMLInputElement).value = targets.pnl.nonOpexPercent || '';
          (document.getElementById('global-target-depreciation') as HTMLInputElement).value = targets.pnl.depreciationPercent || '';
          (document.getElementById('global-target-interest') as HTMLInputElement).value = targets.pnl.interestPercent || '';
          (document.getElementById('global-target-tax') as HTMLInputElement).value = targets.pnl.taxPercent || '';
          
          updatePnlCalculations(); // Trigger calc
        }
      }
    } catch (error) {
      console.error('Error loading branch targets:', error);
      alert('Could not load branch targets.');
    } finally {
      hideLoading();
    }
  }

  function renderFilteredCards() {
    const gridContainer = document.getElementById('premium-data-cards-grid')
    const branchSelect = document.getElementById('premium-manage-data-branch-select') as HTMLSelectElement
    const prevBtn = document.getElementById('premium-data-prev-btn') as HTMLButtonElement
    const nextBtn = document.getElementById('premium-data-next-btn') as HTMLButtonElement
    const pageInfo = document.getElementById('premium-data-page-info')

    if (!gridContainer || !branchSelect || !prevBtn || !nextBtn || !pageInfo) return

    const selectedBranch = branchSelect.value
    const allKeys = Object.keys(aggregatedData).sort((a, b) => {
      const [, periodA] = a.split('|')
      const [, periodB] = b.split('|')
      return periodB.localeCompare(periodA)
    })

    const filteredKeys = selectedBranch === 'ALL' ? allKeys : allKeys.filter((key) => aggregatedData[key].branch === selectedBranch)

    const totalPages = Math.ceil(filteredKeys.length / CARDS_PER_PAGE)
    currentPage = Math.max(1, Math.min(currentPage, totalPages || 1))

    const startIndex = (currentPage - 1) * CARDS_PER_PAGE
    const pageKeys = filteredKeys.slice(startIndex, startIndex + CARDS_PER_PAGE)

    if (pageKeys.length === 0) {
      gridContainer.innerHTML = '<p class="text-gray-500 col-span-4 text-center mt-4">No data found for the selected branch.</p>'
    } else {
      gridContainer.innerHTML = pageKeys.map((key) => {
        const item = aggregatedData[key]
        const [year, month] = item.period.split('-')
        const formattedPeriod = new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' })

        const dataTypes = ['Sales Data', 'P&L Data']
        const typeKeys = ['salesData', 'pnlData']

        const count = typeKeys.filter((t) => item.types[t]?.exists).length
        const progress = (count / 2) * 100
        const status = progress === 100 ? 'Complete' : 'In Progress'
        const statusColor = progress === 100 ? 'green' : 'yellow'

        const checkmarkIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" /></svg>`
        const circleIcon = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-300" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" /></svg>`

        return `
          <div class="bg-white p-4 rounded-lg shadow-md border border-gray-200 flex flex-col">
              <div class="flex justify-between items-center mb-2">
                <span class="text-xs font-semibold bg-${statusColor}-100 text-${statusColor}-800 px-2 py-0.5 rounded-full">${status}</span>
                <button 
                  class="delete-premium-card-btn text-gray-400 hover:text-red-600" title="Delete ALL data for this period"
                  data-branch-name="${item.branch}" 
                  data-branch-id="${item.branchId}" 
                  data-period="${item.period}"
                  data-sales-id="${item.types.salesData?.id || ''}"
                  data-pnl-id="${item.types.pnlData?.id || ''}"
                  >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
                </button>
              </div>
              <h4 class="font-semibold text-gray-800">${item.branch}</h4>
              <p class="text-sm text-gray-500 mb-3">${formattedPeriod}</p>
              <div class="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <div class="w-full bg-gray-200 rounded-full h-1.5"><div class="bg-${statusColor}-500 h-1.5 rounded-full" style="width: ${progress}%"></div></div>
                <span>${count}/2</span>
              </div>
              <div class="text-xs space-y-1.5 mt-2 border-t pt-3">
                ${typeKeys.map((typeKey, index) => {
                  const exists = item.types[typeKey]?.exists
                  const dataId = item.types[typeKey]?.id || ''
                  const label = dataTypes[index]
                  
                  // NEW: Clickable Text Logic
                  const clickableContent = exists 
                    ? `<span class="cursor-pointer hover:text-indigo-600 hover:underline font-medium view-data-trigger"
                            data-type="${typeKey}"
                            data-id="${dataId}"
                            data-branch-id="${item.branchId}"
                            data-period="${item.period}"
                            data-branch-name="${item.branch}">
                         ${label}
                       </span>`
                    : label

                  return `
                  <div class="flex justify-between items-center group h-6">
                    <div class="flex items-center gap-2 ${exists ? 'text-gray-700' : 'text-gray-400'}">
                      ${exists ? checkmarkIcon : circleIcon} 
                      ${clickableContent}
                    </div>
                    ${exists ? `
                      <button 
                        class="delete-specific-data-btn text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete ${label} only"
                        data-type="${typeKey}"
                        data-id="${dataId}"
                        data-branch-id="${item.branchId}"
                        data-branch-name="${item.branch}"
                        data-period="${item.period}"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    ` : ''}
                  </div>`
                }).join('')}
              </div>
          </div>`
      }).join('')
    }

    pageInfo.textContent = `Page ${currentPage} of ${totalPages || 1}`
    prevBtn.disabled = currentPage === 1
    nextBtn.disabled = currentPage >= totalPages

    prevBtn.onclick = () => { if (currentPage > 1) { currentPage--; renderFilteredCards() } }
    nextBtn.onclick = () => { if (currentPage < totalPages) { currentPage++; renderFilteredCards() } }
  }

  async function ensureUserDocument(uid: string, email: string | null, role = 'user'): Promise<void> {
    const userRef = doc(db, 'users', uid)
    const userSnap = await getDoc(userRef)
    if (!userSnap.exists()) {
      try {
        await setDoc(userRef, { uid, email, createdAt: new Date(), role })
      } catch (error) {
        console.error('Error creating user document:', error)
      }
    }
  }

  async function deleteUserRecordPremium(userId: string): Promise<void> {
    if (userId === currentUser?.uid) {
      alert('For safety, you cannot delete your own user record from this interface.')
      return
    }
    try {
      await deleteDoc(doc(db, 'users', userId))
      alert('User Firestore record deleted.')
      await renderPremiumUserManagementView() // Refresh the premium view's list
    } catch (error: any) {
      console.error('Error deleting user record:', error)
      alert(`Error deleting user record: ${error.message}`)
    }
  }

  async function renderPremiumUserManagementView() {
    if (!currentUser) return
    const tbody = document.getElementById('premium-user-list-tbody')
    if (!tbody) return

    tbody.innerHTML = '<tr><td colspan="4" class="text-center p-4 text-gray-500">Loading users...</td></tr>'

    try {
      const querySnapshot = await getDocs(collection(db, 'users'))
      tbody.innerHTML = ''
      if (querySnapshot.empty) {
        tbody.innerHTML = '<tr><td colspan="4" class="text-center p-4 text-gray-500">No users found.</td></tr>'
        return
      }

      querySnapshot.forEach((docSnap) => {
        const user = docSnap.data()
        const tr = document.createElement('tr')
        tr.innerHTML = `
           <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${user.email}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}">
              ${user.role}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">${user.uid}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center gap-4">
              <button class="delete-user-btn-premium text-gray-400 hover:text-red-600" title="Delete User" data-id="${user.uid}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" /></svg>
              </button>
          </td>
        `
        tbody.appendChild(tr)
      })
    } catch (error) {
      console.error('Error loading users for premium view:', error)
      tbody.innerHTML = '<tr><td colspan="4" class="text-center p-4 text-red-500">Could not load user data.</td></tr>'
    }
  }

  async function renderPremiumManageDataView() {
    if (!currentUser) return
    const gridContainer = document.getElementById('premium-data-cards-grid')
    const branchSelect = document.getElementById('premium-manage-data-branch-select') as HTMLSelectElement
    if (!gridContainer || !branchSelect) return

    gridContainer.innerHTML = '<p class="text-gray-500 col-span-4">Loading compiled data...</p>'

    try {
      // --- THIS IS THE FIX ---
      // 1. Get user's branches *and* the selected brand
      const { userBranches, selectedBrandId } = $store.getAccountContext()

      // 2. Filter branches based on the selected brand
      const filteredBranches = selectedBrandId
        ? userBranches.filter(b => b.brandId === selectedBrandId)
        : userBranches
      // --- END FIX ---

      // 3. Query all branches in parallel, using the *filtered* list
      const allData = await Promise.all(
        filteredBranches.map(async (branch) => {
          const [salesSnap, salesTargetSnap, pnlSnap, pnlTargetSnap] = await Promise.all([
            getDocs(collection(db, paths.branches(branch.branchId).salesReports.collectionPath)),
            getDocs(collection(db, paths.branches(branch.branchId).salesTargets.collectionPath)),
            getDocs(collection(db, paths.branches(branch.branchId).pnlReports.collectionPath)),
            getDocs(collection(db, paths.branches(branch.branchId).pnlTargets.collectionPath)),
          ])
          return { branch, salesSnap, salesTargetSnap, pnlSnap, pnlTargetSnap }
        })
      )

      const localAggregatedData: Record<string, any> = {}
      const processSnap = (snap: any, type: string, loopBranchId: string) => { // loopBranchId is the ID of the branch folder we are looping
        snap.forEach((doc: any) => {
          const data = doc.data()
          
          const period = data.period || doc.id.split('_')[0] 
          if (!period || !/^\d{4}-\d{2}$/.test(period)) return 

          const validBranchName = data.branchName || loopBranchId;
          
          const key = `${validBranchName}|${period}`

          if (!localAggregatedData[key]) {
            localAggregatedData[key] = { branch: validBranchName, branchId: loopBranchId, period: period, types: {} }
          }
          localAggregatedData[key].types[type] = { id: doc.id, exists: true }
        })
      }

      // Process all branch data (now correctly filtered)
      allData.forEach(({ branch, salesSnap, salesTargetSnap, pnlSnap, pnlTargetSnap }) => {
        processSnap(salesSnap, 'salesData', branch.branchId)
        processSnap(salesTargetSnap, 'salesTarget', branch.branchId)
        processSnap(pnlSnap, 'pnlData', branch.branchId)
        processSnap(pnlTargetSnap, 'pnlTarget', branch.branchId)
      })

      aggregatedData = localAggregatedData // Update the shared state

      // 4. Populate the branch dropdown with the (now filtered) aggregated branches
      const branches = [...new Set(Object.values(aggregatedData).map((item) => item.branch))].sort()
      branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('')

      // --- Populate Upload Target Branch Selector (with filtered branches) ---
      const uploadTargetSelect = document.getElementById('premium-upload-target-branch-select') as HTMLSelectElement | null
      if (uploadTargetSelect) {
        // 5. Use the *filteredBranches* list here
        if (filteredBranches && filteredBranches.length > 0) {
          uploadTargetSelect.innerHTML = filteredBranches.map((b: any) => `<option value="${b.branchId}">${b.branchName}</option>`).join('')

          const defaultBranchId = $store.getSelectedBranchId() && filteredBranches.some((b: any) => b.branchId === $store.getSelectedBranchId())
            ? $store.getSelectedBranchId()
            : filteredBranches[0]!.branchId

          uploadTargetSelect.value = defaultBranchId!
          // Sync store so the upload handlers use the selected branch
          $store.setSelectedBranchId(defaultBranchId)
          try { localStorage.setItem('selectedBranchId', defaultBranchId!) } catch (err) { /* ignore */ }

          uploadTargetSelect.addEventListener('change', (e) => {
            const val = (e.target as HTMLSelectElement).value
            $store.setSelectedBranchId(val)
            try { localStorage.setItem('selectedBranchId', val) } catch (_err) { /* ignore */ }
          })
        } else {
          uploadTargetSelect.innerHTML = '<option value="">No branches available for this brand</option>'
          uploadTargetSelect.disabled = true
        }
      }
      // --- END FIX ---

      renderFilteredCards() // Initial render

      const newBranchSelect = branchSelect.cloneNode(true)
      branchSelect.parentNode?.replaceChild(newBranchSelect, branchSelect)
      newBranchSelect.addEventListener('change', () => {
        currentPage = 1 // Reset to first page on filter change
        renderFilteredCards()
      })
    } catch (error) {
      console.error('Error populating premium manage data view:', error)
      if (gridContainer) gridContainer.innerHTML = '<p class="text-red-500 col-span-4">Failed to load data. Please try again.</p>'
    }
  }

  const finSlideSaveBtn = document.getElementById('finslide-save-btn') as HTMLButtonElement | null;
    finSlideSaveBtn?.addEventListener('click', async () => {
        if (!currentEditingDeck || !deckHasChanges || !currentUser) {
            console.warn("[FINSLIDE_SAVE] Save conditions not met.");
            return;
        }

        const deckId = currentEditingDeck.id;
        const updatedSlides = currentEditingDeck.slides;

        if (finSlideSaveBtn) finSlideSaveBtn.disabled = true; // Disable immediately
        showLoading({ message: 'Saving changes...' });

        try {
            // --- TODO: Replace with actual Firebase Function call ---
            console.log(`[FINSLIDE_SAVE] Simulating call to updateFinSlideDeck with deckId: ${deckId}`);
            console.log("[FINSLIDE_SAVE] Updated Slides Data:", updatedSlides);
            // const updateDeck = httpsCallable(functions, 'updateFinSlideDeck');
            // await updateDeck({ deckId: deckId, slides: updatedSlides });
            await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate delay
            // --- End Simulation ---

            deckHasChanges = false; // Reset flag on successful save
            if (finSlideSaveBtn) {
                 finSlideSaveBtn.classList.add('disabled:opacity-50'); // Keep disabled visually
            }
            hideLoading();
            alert('Changes saved successfully!'); // Simple confirmation for now

        } catch (error) {
            hideLoading();
            if (finSlideSaveBtn) finSlideSaveBtn.disabled = false; // Re-enable on error
            console.error("[FINSLIDE_SAVE] Error saving deck:", error);
            alert(`Failed to save changes. Error: ${error instanceof Error ? error.message : String(error)}`);
        }
    });

  // --- New Upload Section Logic ---
  const esbBtnNew = document.getElementById('premium-upload-esb-btn-new')
  const mokaBtnNew = document.getElementById('premium-upload-moka-btn-new')
  const formatButtonsNew = [esbBtnNew, mokaBtnNew]

  esbBtnNew?.addEventListener('click', () => {
    selectedSalesFormat = 'ESB'
    formatButtonsNew.forEach((btn) => btn?.classList.remove('bg-indigo-100', 'border-indigo-500'))
    esbBtnNew.classList.add('bg-indigo-100', 'border-indigo-500')
  })

  mokaBtnNew?.addEventListener('click', () => {
    selectedSalesFormat = 'MOKA'
    formatButtonsNew.forEach((btn) => btn?.classList.remove('bg-indigo-100', 'border-indigo-500'))
    mokaBtnNew.classList.add('bg-indigo-100', 'border-indigo-500')
  })

  // --- New File Input Listeners ---
  const salesDataInputNew = document.getElementById('premium-upload-sales-data-input-new') as HTMLInputElement
  const pnlDataInputNew = document.getElementById('premium-upload-pnl-data-input-new') as HTMLInputElement

  salesDataInputNew?.addEventListener('change', (e) => handleFileUpload((e.target as HTMLInputElement).files?.[0], 'salesData'))
  pnlDataInputNew?.addEventListener('change', (e) => handleFileUpload((e.target as HTMLInputElement).files?.[0], 'pnlData'))

  const exportPdfActionBtn = document.getElementById('export-to-pdf-btn')
  exportPdfActionBtn?.addEventListener('click', handleExportToPdf)

  // In: apps/web/src/analysis/premium/orchestrator.ts
// REPLACE this event listener (inside setupPremiumAnalysisView):

  const exportFinSlideBtn = document.getElementById('export-to-finslide-btn')
  exportFinSlideBtn?.addEventListener('click', async () => {
    // Read filters from the PDF export section controls
    const branchSelect = document.getElementById('export-pdf-branch-select') as HTMLSelectElement
    const rangeSelect = document.getElementById('export-pdf-range-select') as HTMLSelectElement
    const periodSelect = document.getElementById('export-pdf-period-select') as HTMLSelectElement

    if (!branchSelect || !rangeSelect || !periodSelect || !currentUser) {
      alert('Could not find necessary filters or user is not logged in.')
      return
    }

    const selectedBranch = branchSelect.value
    const selectedRange = rangeSelect.value
    const selectedPeriod = periodSelect.value

    if (!selectedPeriod) {
      alert('Please select a valid period.')
      return
    }

    // --- [THIS IS THE FIX] ---
    // 1. Get the accountId from the store
    const { accountId } = $store.getAccountContext();

    // 2. Add a check for it
    if (!accountId) {
      alert('Error: Could not find your Account ID. Please reload.')
      return
    }
    // --- [END FIX] ---

    // Disable button immediately and show loading
    exportFinSlideBtn.setAttribute('disabled', 'true')
    showLoading({ message: 'Initiating FinSlide deck generation...' })

    try {
      const generateDeck = httpsCallable(functions, 'generateFinSlideDeck')

      // --- [THIS IS THE FIX] ---
      // 3. Pass the accountId in the payload
      const result = await generateDeck({ 
        branch: selectedBranch, 
        range: selectedRange, 
        period: selectedPeriod, 
        accountId: accountId // <-- Add this property
      })
      // --- [END FIX] ---

      const deckId = (result.data as any).deckId
      console.log(`[FINSLIDE_TRIGGER] Deck generation started with ID: ${deckId}`)

      hideLoading()
      alert(`Deck generation for ${selectedBranch} (${selectedPeriod} - ${selectedRange}) started. Please check the FinSlide Decks section later.`)
    } catch (error) {
      hideLoading()
      console.error('[FINSLIDE_TRIGGER] Error initiating FinSlide generation:', error)
      alert(`Failed to start deck generation. Error: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      // Re-enable button regardless of success or failure
      exportFinSlideBtn.removeAttribute('disabled')
    }
  })

  const finSlideBackBtn = document.getElementById('finslide-back-to-list-btn')
  finSlideBackBtn?.addEventListener('click', () => {
    const deckListPage = document.getElementById('premium-finslide-page')
    const editorPage = document.getElementById('finslide-editor-view')
    editorPage?.classList.add('hidden')
    deckListPage?.classList.remove('hidden')
    if (currentSlideChartInstance) {
        console.log("[FINSLIDE_BACK] Destroying chart instance.");
        currentSlideChartInstance.destroy();
        currentSlideChartInstance = null;
    }
    // Optionally clear the current editing state
    currentEditingDeck = null
    currentSlideIndex = 0
    // Refresh the list in case of changes (though none are saved yet)
    setupPremiumFinSlidePage()
  })

  const showExportPdf = () => {
    showContent(exportPdfContent, exportPdfNavBtn)
    if (!hasInitializedPdfPage) {
      setupPdfExportPage()
      hasInitializedPdfPage = true
    }
  }

  // --- Make "Browse File" Buttons Clickable ---
  const salesBrowseBtn = document.getElementById('sales-data-browse-btn')
  const salesUploadInput = document.getElementById('premium-upload-sales-data-input-new')

  salesBrowseBtn?.addEventListener('click', () => {
    (salesUploadInput as HTMLInputElement)?.click()
  })

  const pnlBrowseBtn = document.getElementById('pnl-data-browse-btn')
  const pnlUploadInput = document.getElementById('premium-upload-pnl-data-input-new')

  pnlBrowseBtn?.addEventListener('click', () => {
    (pnlUploadInput as HTMLInputElement)?.click()
  })

  // --- View Switching Logic ---
  const showContent = (contentElement: HTMLElement | null, buttonElement: HTMLElement | null) => {
    // 1. Hide the external Admin Panel if open
    document.getElementById('admin-panel')?.classList.add('hidden')

    // 2. Hide all main content areas
    allMainContent.forEach((el) => el.classList.add('hidden'))
    
    // 3. Show the target content
    contentElement?.classList.remove('hidden')

    // 4. Reset Sidebar Styling (Links)
    document.querySelectorAll('aside nav a').forEach((el) => el.classList.remove('bg-gray-100', 'font-semibold'))
    
    // 5. Reset Sidebar Styling (Accordion Headers)
    // [FIX] Ensure sub-menu headers lose their bold state when switching away
    document.querySelectorAll('aside nav .premium-submenu-toggle').forEach((el) => el.classList.remove('font-semibold'))

    // 6. Highlight the Active Button
    buttonElement?.classList.add('bg-gray-100', 'font-semibold')
  }

  const showWelcome = () => {
    const welcomeContent = document.getElementById('premium-welcome');
    // We don't have a sidebar button for welcome, so pass null for button
    showContent(welcomeContent, null);
    renderWelcomeView(); // Render the content
  }

  const showDashboard = () => {
    showContent(dashboardContent, dashboardBtn)
    if (!dashboardContent.hasAttribute('data-loaded')) {
      setupPremiumOverviewDashboard() // Call the new dashboard setup function
      dashboardContent.setAttribute('data-loaded', 'true')
    }
  }


  const showConfiguration = () => {
    showContent(configurationContent, configurationBtn)
    
    const { selectedBrandId, userBranches, userBrands } = $store.getAccountContext();
    const titleEl = configurationContent?.querySelector('h2') as HTMLElement;
    const headerContainer = configurationContent?.querySelector('.flex.justify-between > div') as HTMLElement;

    if (!selectedBrandId) {
      if (titleEl) titleEl.textContent = 'Configuration';
      alert('Please select a specific brand from the sidebar to edit its configuration.');
      return;
    }

    const brandName = userBrands.find(b => b.brandId === selectedBrandId)?.brandName || 'Brand';
    if (titleEl) titleEl.textContent = `Configuration: ${brandName}`;

    // --- Branch Selector Logic ---
    let branchSelectContainer = document.getElementById('config-branch-select-container');
    if (!branchSelectContainer) {
        branchSelectContainer = document.createElement('div');
        branchSelectContainer.id = 'config-branch-select-container';
        branchSelectContainer.className = 'mt-4 bg-gray-50 p-3 rounded-md border border-gray-200 flex items-center gap-3';
        branchSelectContainer.innerHTML = `
            <label for="config-branch-select" class="text-sm font-bold text-gray-700 whitespace-nowrap">Select Branch to Configure:</label>
            <select id="config-branch-select" class="block w-full md:w-64 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            </select>
        `;
        headerContainer.insertAdjacentElement('afterend', branchSelectContainer);
    }

    const branchesForBrand = userBranches.filter(b => b.brandId === selectedBrandId);
    const branchSelect = document.getElementById('config-branch-select') as HTMLSelectElement;
    
    branchSelect.innerHTML = branchesForBrand.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');

    // --- [NEW] Updated P&L Target Form HTML ---
    const pnlFormContainer = document.getElementById('global-pnl-target-form')?.parentElement;
    if (pnlFormContainer) {
        pnlFormContainer.innerHTML = `
            <h3 class="text-xl font-semibold text-gray-700 mb-1">Branch P&L Target</h3>
            <p class="text-sm text-gray-500 mb-6">Set target percentages. Profit margins are auto-calculated.</p>
            <form id="global-pnl-target-form" class="space-y-6">
                
                <div class="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-100">
                    <span class="font-bold text-blue-900">Pendapatan (Revenue)</span>
                    <span class="font-bold text-blue-900">100%</span>
                </div>

                <div>
                    <label for="global-target-cogs" class="block text-sm font-medium text-gray-600">(-) Harga Pokok Produksi (COGS)</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input type="number" id="global-target-cogs" class="pnl-calc-input block w-full rounded-md border-gray-300 pr-8 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="0">
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
                    </div>
                </div>

                <div class="flex items-center justify-between p-2 bg-gray-100 rounded-md">
                    <span class="font-semibold text-gray-700">(=) Laba Kotor (Gross Profit)</span>
                    <span id="global-calc-gp" class="font-bold text-gray-900">100%</span>
                </div>

                <div>
                    <label for="global-target-opex" class="block text-sm font-medium text-gray-600">(-) Beban Operasional (OPEX)</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input type="number" id="global-target-opex" class="pnl-calc-input block w-full rounded-md border-gray-300 pr-8 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="0">
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
                    </div>
                </div>

                <div class="flex items-center justify-between p-2 bg-gray-100 rounded-md">
                    <span class="font-semibold text-gray-700">(=) Net Operating Income</span>
                    <span id="global-calc-noi" class="font-bold text-gray-900">100%</span>
                </div>

                <div>
                    <label for="global-target-non-opex" class="block text-sm font-medium text-gray-600">(-) Beban Non Operasional</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input type="number" id="global-target-non-opex" class="pnl-calc-input block w-full rounded-md border-gray-300 pr-8 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="0">
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"><span class="text-gray-500 sm:text-sm">%</span></div>
                    </div>
                </div>

                <div class="flex items-center justify-between p-2 bg-indigo-50 rounded-md border border-indigo-100">
                    <span class="font-bold text-indigo-900">(=) EBITDA</span>
                    <span id="global-calc-ebitda" class="font-bold text-indigo-900">100%</span>
                </div>

                <div class="grid grid-cols-3 gap-2">
                    <div>
                        <label class="block text-xs font-medium text-gray-500 mb-1">Depresiasi</label>
                        <input type="number" id="global-target-depreciation" class="pnl-calc-input w-full text-xs border-gray-300 rounded-md" placeholder="0">
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 mb-1">Bunga</label>
                        <input type="number" id="global-target-interest" class="pnl-calc-input w-full text-xs border-gray-300 rounded-md" placeholder="0">
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-500 mb-1">Pajak</label>
                        <input type="number" id="global-target-tax" class="pnl-calc-input w-full text-xs border-gray-300 rounded-md" placeholder="0">
                    </div>
                </div>

                <div class="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <span class="text-lg font-bold text-green-900">(=) Net Income</span>
                    <span id="global-calc-net-income" class="text-xl font-bold text-green-700">100%</span>
                </div>

            </form>
        `;
    }
    // --- [END NEW HTML] ---


    const handleBranchChange = async (branchId: string) => {
        currentConfigBranchId = branchId;
        
        // This load function now needs to populate the NEW fields
        await loadGlobalTargets(branchId); 
        await setupExclusionControls(branchId); 
        
        // Trigger calculation update
        updatePnlCalculations();
    };

    branchSelect.onchange = (e) => {
        handleBranchChange((e.target as HTMLSelectElement).value);
    };

    if (branchesForBrand.length > 0) {
        branchSelect.value = branchesForBrand[0].branchId;
        handleBranchChange(branchesForBrand[0].branchId);
    }

    // Enable forms
    (document.getElementById('save-global-sales-target-btn') as HTMLButtonElement).disabled = false;
    (document.getElementById('save-global-pnl-target-btn') as HTMLButtonElement).disabled = false;
    (document.getElementById('save-exclusions-btn') as HTMLButtonElement).disabled = false;

    // Add listener for live calculations
    const pnlInputs = document.querySelectorAll('.pnl-calc-input');
    pnlInputs.forEach(input => {
        input.addEventListener('input', updatePnlCalculations);
    });
  }

  // --- [NEW] Helper for P&L Calculations ---
  function updatePnlCalculations() {
      const getVal = (id: string) => parseFloat((document.getElementById(id) as HTMLInputElement)?.value) || 0;
      
      const cogs = getVal('global-target-cogs');
      const opex = getVal('global-target-opex');
      const nonOpex = getVal('global-target-non-opex');
      const depr = getVal('global-target-depreciation');
      const interest = getVal('global-target-interest');
      const tax = getVal('global-target-tax');

      const gp = 100 - cogs;
      const noi = gp - opex;
      const ebitda = noi - nonOpex;
      const netIncome = ebitda - depr - interest - tax;

      const setVal = (id: string, val: number) => {
          const el = document.getElementById(id);
          if (el) el.textContent = `${val.toFixed(1)}%`;
      };

      setVal('global-calc-gp', gp);
      setVal('global-calc-noi', noi);
      setVal('global-calc-ebitda', ebitda);
      
      const niEl = document.getElementById('global-calc-net-income');
      if (niEl) {
          niEl.textContent = `${netIncome.toFixed(1)}%`;
          niEl.className = `text-xl font-bold ${netIncome >= 0 ? 'text-green-700' : 'text-red-600'}`;
      }
  }

  const showManageData = () => {
    showContent(manageDataContent, manageDataBtn)
    if (!hasLoadedManageData) {
      renderPremiumManageDataView()
      hasLoadedManageData = true
    }
  }

  

    manageDataContent?.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement
      
      // --- NEW: View Summary Logic ---
      const viewTrigger = target.closest('.view-data-trigger') as HTMLElement
      if (viewTrigger) {
        const type = viewTrigger.dataset.type
        const id = viewTrigger.dataset.id
        const branchId = viewTrigger.dataset.branchId
        const period = viewTrigger.dataset.period
        const branchName = viewTrigger.dataset.branchName

        if (!id) return

        if (type === 'salesData') {
            // Open View Choice Modal (Sales)
            openViewChoiceModal(id, `${branchName} - ${period}`, branchId)
        } 
        else if (type === 'pnlData' && branchId) {
            // Open P&L Modal
            showLoading({ message: 'Loading P&L Report...' })
            try {
                // Fetch the P&L document using the path helper
                const reportRef = doc(db, paths.branches(branchId).pnlReports.collectionPath, id)
                const reportSnap = await getDoc(reportRef)
                
                if (reportSnap.exists()) {
                    showPnlDataModal(reportSnap.data())
                } else {
                    alert('P&L Report document not found.')
                }
            } catch (error) {
                console.error('Error fetching P&L report:', error)
                alert('Failed to load P&L report.')
            } finally {
                hideLoading()
            }
        }
        return // Exit listener
      }
      // --- END View Summary Logic ---

      // 1. Check for MASTER Delete Button (Card Level)
      const masterDeleteBtn = target.closest('.delete-premium-card-btn')
      // 2. Check for SPECIFIC Delete Button (Row Level)
      const specificDeleteBtn = target.closest('.delete-specific-data-btn')

      const deleteButton = masterDeleteBtn || specificDeleteBtn
      
      if (deleteButton) {
        const branchId = deleteButton.getAttribute('data-branch-id');
        const branchName = deleteButton.getAttribute('data-branch-name'); 
        const period = deleteButton.getAttribute('data-period');
        
        // If specific, what type?
        const specificType = deleteButton.getAttribute('data-type'); // 'salesData' or 'pnlData'
        const specificId = deleteButton.getAttribute('data-id');

        // Get the branch context to verify branch
        const { getAccountContext } = await import('@/store/api')
        const { userBranches } = getAccountContext()
        const branch = userBranches.find(b => b.branchName === branchName)
        
        if (!branch) {
            alert(`Error: Could not find branch ID for ${branchName}`)
            return
        }

        if (!branchId || !period) return

        // --- Construct Confirmation Message ---
        let confirmMessage = '';
        if (specificType === 'salesData') {
            confirmMessage = `Delete SALES DATA only for ${branchName} (${period})?`;
        } else if (specificType === 'pnlData') {
            confirmMessage = `Delete P&L REPORT only for ${branchName} (${period})?`;
        } else {
            confirmMessage = `Are you sure you want to permanently delete ALL data (Sales & P&L) for ${branchName} - ${period}? This cannot be undone.`;
        }

        if (!confirm(confirmMessage)) return

        showLoading({ message: `Deleting data...` })

        try {
          const deleteUploadFunc = httpsCallable(functions, 'deleteUpload')
          const deletePnlReportFunc = httpsCallable(functions, 'deletePnlReport')
          const deleteSalesTargetFunc = httpsCallable(functions, 'deleteSalesTarget')
          const deletePnlTargetFunc = httpsCallable(functions, 'deletePnlTarget')
          
          const promises = []
          
          // --- Logic for Specific vs Master Delete ---
          if (specificType) {
            // Specific Delete
            if (specificType === 'salesData' && specificId) {
                promises.push(deleteUploadFunc({ uploadId: specificId, branchId: branchId }))
                // Optionally delete sales target too if you want them linked? 
                // For now, let's just delete the main data file.
            } else if (specificType === 'pnlData' && specificId) {
                promises.push(deletePnlReportFunc({ reportId: specificId, branchId: branchId }))
            }
          } else {
            // Master Delete (Original Logic)
            const salesDataId = deleteButton.getAttribute('data-sales-id')
            const salesTargetId = deleteButton.getAttribute('data-sales-target-id')
            const pnlDataId = deleteButton.getAttribute('data-pnl-id')
            const pnlTargetId = deleteButton.getAttribute('data-pnl-target-id')

            if (salesDataId) promises.push(deleteUploadFunc({ uploadId: salesDataId, branchId: branchId }))
            if (salesTargetId) promises.push(deleteSalesTargetFunc({ targetId: salesTargetId, branchId: branchId }))
            if (pnlDataId) promises.push(deletePnlReportFunc({ reportId: pnlDataId, branchId: branchId }))
            if (pnlTargetId) promises.push(deletePnlTargetFunc({ targetId: pnlTargetId, branchId: branchId }))
          }
          
          if (promises.length === 0) {
            throw new Error("No data IDs found to delete.")
          }

          // Run all delete operations in parallel
          await Promise.all(promises)
          
          // Clean up
          await clearSummariesCache()
          console.log('Data deleted. Cache cleared.')

          alert(`Successfully deleted data. Reloading app...`)
          window.location.reload()

        } catch (error) {
          console.error('Error deleting data:', error)
          alert('Failed to delete data. Please check the console for details.')
        } finally {
          hideLoading()
        }
      }
    })

  // --- Attach all event listeners ---
  dashboardBtn?.addEventListener('click', (e) => { e.preventDefault(); showDashboard() })
  manageDataBtn?.addEventListener('click', (e) => { e.preventDefault(); showManageData() })

  userManagementBtn?.addEventListener('click', (e) => { e.preventDefault(); navigateToAdminPanel() })
  configurationBtn?.addEventListener('click', (e) => { e.preventDefault(); showConfiguration() })
  exportPdfNavBtn?.addEventListener('click', (e) => { e.preventDefault(); showExportPdf() })
  configBackBtn?.addEventListener('click', (e) => { e.preventDefault(); showDashboard() })

  recommendationBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    showContent(recommendationContent, recommendationBtn);
    setupFinalyticsRecommendation();
  });

  highlightBtn?.addEventListener('click', (e) => {
  e.preventDefault();
  // 1. Switch the visible view
  showContent(highlightContent, highlightBtn); 
  // 2. Initialize the Business Highlight logic
  setupBusinessHighlightView(); 
});

  const saveSalesBtn = document.getElementById('save-global-sales-target-btn')
  const savePnlBtn = document.getElementById('save-global-pnl-target-btn')
  const feedbackEl = document.getElementById('global-targets-feedback')


  const premiumLogoutBtn = document.getElementById('premium-logout-btn')
  premiumLogoutBtn?.addEventListener('click', async (e) => {
    e.preventDefault()
    try {
      // 1. Clear the in-memory state (using the imported store)
      $store.resetAnalysisState()
      // 2. Clear the persistent IndexedDB cache (already imported)
      await clearSummariesCache()
      console.log('[Logout] Cleared all local data and cache.')
    } catch (error) {
      console.error('[Logout] Failed to clear local data:', error)
    } finally {
      // 3. Sign out the user (already imported)
      signOut(auth)
    }
  })

  saveSalesBtn?.addEventListener('click', async () => {
    if (!currentUser || !feedbackEl || !currentConfigBranchId) return

    const salesTargetData = {
      totalOmzet: parseFloat((document.getElementById('global-target-omzet') as HTMLInputElement).value) || 0,
      totalTransactions: parseInt((document.getElementById('global-target-transactions') as HTMLInputElement).value, 10) || 0,
      avgCheck: parseFloat((document.getElementById('global-target-avg-check') as HTMLInputElement).value) || 0,
    }

    try {
      // [FIX] Save to BRANCH document
      const branchDocRef = doc(db, paths.branches(currentConfigBranchId).path);
      await setDoc(branchDocRef, { targets: { sales: salesTargetData } }, { merge: true })
      
      feedbackEl.className = 'p-4 text-sm rounded-md bg-green-100 text-green-800'
      feedbackEl.textContent = 'Branch Sales Target saved successfully!'
      feedbackEl.classList.remove('hidden')
    } catch (error) {
      feedbackEl.className = 'p-4 text-sm rounded-md bg-red-100 text-red-800'
      feedbackEl.textContent = 'Error saving sales target. Please try again.'
      feedbackEl.classList.remove('hidden')
    }
    setTimeout(() => feedbackEl.classList.add('hidden'), 4000)
  })

  savePnlBtn?.addEventListener('click', async () => {
    if (!currentUser || !feedbackEl || !currentConfigBranchId) return

    // [FIX] Capture all new fields
    const pnlTargetData = {
      cogsPercent: parseFloat((document.getElementById('global-target-cogs') as HTMLInputElement).value) || 0,
      opexPercent: parseFloat((document.getElementById('global-target-opex') as HTMLInputElement).value) || 0,
      nonOpexPercent: parseFloat((document.getElementById('global-target-non-opex') as HTMLInputElement).value) || 0,
      depreciationPercent: parseFloat((document.getElementById('global-target-depreciation') as HTMLInputElement).value) || 0,
      interestPercent: parseFloat((document.getElementById('global-target-interest') as HTMLInputElement).value) || 0,
      taxPercent: parseFloat((document.getElementById('global-target-tax') as HTMLInputElement).value) || 0,
    }

    try {
      const branchDocRef = doc(db, paths.branches(currentConfigBranchId).path);
      await setDoc(branchDocRef, { targets: { pnl: pnlTargetData } }, { merge: true })
      
      feedbackEl.className = 'p-4 text-sm rounded-md bg-green-100 text-green-800'
      feedbackEl.textContent = 'Branch P&L Target saved successfully!'
      feedbackEl.classList.remove('hidden')
    } catch (error) {
      feedbackEl.className = 'p-4 text-sm rounded-md bg-red-100 text-red-800'
      feedbackEl.textContent = 'Error saving P&L target. Please try again.'
      feedbackEl.classList.remove('hidden')
    }
    setTimeout(() => feedbackEl.classList.add('hidden'), 4000)
  })

  const addUserModal = document.getElementById('add-user-modal')
  const addUserBtn = document.getElementById('premium-add-user-btn') // FIX: Select button by its new ID
  const addUserModalClose = document.getElementById('add-user-modal-close')
  const cancelAddUserBtn = document.getElementById('cancel-add-user-btn-premium')
  const addUserForm = document.getElementById('add-user-form-premium')

  // Open modal
  addUserBtn?.addEventListener('click', () => {
    addUserModal?.classList.remove('hidden')
  })

  // Close modal
  const closeModal = () => addUserModal?.classList.add('hidden')
  addUserModalClose?.addEventListener('click', closeModal)
  cancelAddUserBtn?.addEventListener('click', closeModal)

  // Handle form submission
  addUserForm?.addEventListener('submit', async (e) => {
    e.preventDefault()
    const feedbackEl = document.getElementById('create-user-feedback-premium')
    if (!feedbackEl) return

    const email = (document.getElementById('new-user-email-premium') as HTMLInputElement).value
    const password = (document.getElementById('new-user-password-premium') as HTMLInputElement).value
    const role = (document.getElementById('new-user-role-premium') as HTMLSelectElement).value

    let adminCreds = { email: '', password: '' }

    if (auth.currentUser?.email) {
      const adminPassword = prompt('To create a new user, please re-enter your admin password for confirmation:')
      if (!adminPassword) {
        feedbackEl.textContent = 'Admin password not provided. User creation cancelled.'
        feedbackEl.className = 'text-red-500 text-sm mb-4 text-center'
        feedbackEl.classList.remove('hidden')
        return
      }
      // Store credentials locally for re-login
      adminCreds = { email: auth.currentUser.email, password: adminPassword }
    } else {
      alert('Admin not signed in. Cannot create user.')
      return
    }

    feedbackEl.textContent = 'Creating user...'
    feedbackEl.className = 'text-blue-500 text-sm mb-4 text-center'
    feedbackEl.classList.remove('hidden')

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      await ensureUserDocument(userCredential.user.uid, userCredential.user.email, role)

      feedbackEl.textContent = 'User created successfully! Re-authenticating admin and refreshing list...'
      feedbackEl.className = 'text-green-500 text-sm mb-4 text-center'
    } catch (error: any) {
      console.error('Error creating user:', error)
      feedbackEl.textContent = `Error: ${error.message}`
      feedbackEl.className = 'text-red-500 text-sm mb-4 text-center'
    } finally {
      // --- FIX: This block ensures the admin is always logged back in ---
      try {
        await signInWithEmailAndPassword(auth, adminCreds.email, adminCreds.password)
        await renderPremiumUserManagementView() // Refresh the user list
        setTimeout(closeModal, 2000)
      } catch (reauthError) {
        console.error('Admin re-authentication failed:', reauthError)
        alert('Could not sign you back in as admin. Please log in again.')
        signOut(auth) // Log out completely to avoid being stuck as the new user
      }
    }
  })

  // Handle delete button clicks on the user list
  const userTbody = document.getElementById('premium-user-list-tbody')
  userTbody?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    const deleteButton = target.closest('.delete-user-btn-premium')
    if (deleteButton) {
      const userId = (deleteButton as HTMLElement).dataset.id
      if (userId && confirm('Are you sure you want to delete this user\'s Firestore data? This will NOT delete their login account.')) {
        deleteUserRecordPremium(userId)
      }
    }
  })

  document.getElementById('premium-back-to-main-menu-btn')?.addEventListener('click', (e) => { e.preventDefault(); showView('main-menu') })

  const generateAiSummaryBtn = document.getElementById('generate-ai-summary-btn')
  generateAiSummaryBtn?.addEventListener('click', async () => {
    const aiAccess = checkFeatureAccess('aiInsights')
    if (!aiAccess.allowed) {
      showUpgradePrompt('aiInsights', aiAccess.requiredTier!)
      return
    }

    const salesAiContainer = document.getElementById('pdf-sales-ai-summary')
    const pnlAiContainer = document.getElementById('pdf-pnl-ai-summary')

    if (!salesAiContainer || !pnlAiContainer || !currentReportDataForAI) {
      alert('Please select filters and generate a preview first.')
      return
    }

    const salesAiContentEl = salesAiContainer.querySelector('p')
    const pnlAiContentEl = pnlAiContainer.querySelector('p')
    if (!salesAiContentEl || !pnlAiContentEl) return

    salesAiContentEl.innerHTML = '<em>Generating Sales insights...</em>'
    pnlAiContentEl.innerHTML = '<em>Generating P&L insights...</em>'
    generateAiSummaryBtn.setAttribute('disabled', 'true')
    showLoading({ message: 'Generating AI summaries...' })

    try {
      // --- [THIS IS THE FIX] ---
      // 1. Define filters and hashes
      const filters = (currentReportDataForAI as any).context
      const dataHash = await generateSHA256(currentReportDataForAI)
      
      // We create two separate "filter" hashes to store two summaries
      const salesFiltersHash = await generateSHA256(filters)
      const pnlFiltersHash = await generateSHA256({ ...filters, type: 'pnl' }) // Add 'type' to make it unique

      // 2. Check cache for both
      const cachedSales = await findLiveCache(salesFiltersHash, dataHash)
      const cachedPnl = await findLiveCache(pnlFiltersHash, dataHash)

      let salesSummary = cachedSales?.summary
      let pnlSummary = cachedPnl?.summary

      // 3. Create prompts for summaries we *don't* have
      const aiPromises = []
      
      if (!salesSummary) {
        console.log('Generating new AI Sales summary...')
        // This is the original prompt for sales and charts
        const promptSales = viewPromptCreators['export-pdf'](currentReportDataForAI)
        aiPromises.push(getGeminiAnalysis(promptSales))
      } else {
        console.log('Using cached AI Sales summary.')
        aiPromises.push(Promise.resolve(null)) // Add a placeholder
      }
      
      if (!pnlSummary) {
        console.log('Generating new AI P&L summary...')
        // This is a new, specific prompt for P&L
        // We'll have to create it manually here
        const { pnlReport } = (currentReportDataForAI as any)
        const pnlPrompt = `
          Analyze the following Profit & Loss data for ${filters.branch} in ${filters.period}.
          P&L Data: ${JSON.stringify(pnlReport?.pnlData || {})}
          Metrics: ${JSON.stringify(calculateAllPnlMetrics(pnlReport?.pnlData || {}))}
          
          Provide a concise summary of the key P&L insights.
          - What is the net profit?
          - What are the main drivers of cost (COGS, OPEX)?
          - Are there any obvious areas for improvement?
          
          Respond in markdown.
        `
        aiPromises.push(getGeminiAnalysis(pnlPrompt))
      } else {
        console.log('Using cached AI P&L summary.')
        aiPromises.push(Promise.resolve(null)) // Add a placeholder
      }

      // 4. Run AI calls in parallel
      const [salesResult, pnlResult] = await Promise.all(aiPromises)

      // 5. Populate results
      if (salesResult) {
        salesSummary = salesResult.summaryText
        await createLiveCache({ filtersHash: salesFiltersHash, dataHash, summary: salesSummary, usageMetadata: salesResult.usageMetadata, filters })
      }
      
      if (pnlResult) {
        pnlSummary = pnlResult.summaryText
        await createLiveCache({ filtersHash: pnlFiltersHash, dataHash, summary: pnlSummary, usageMetadata: pnlResult.usageMetadata, filters: { ...filters, type: 'pnl' } })
      }

      salesAiContentEl.innerHTML = marked.parse(salesSummary || 'Error: Could not generate summary.')
      pnlAiContentEl.innerHTML = marked.parse(pnlSummary || 'Error: Could not generate P&L summary.')
      // --- [END FIX] ---

    } catch (error: any) {
      console.error('Error generating AI Summary:', error)
      salesAiContentEl.innerHTML = `<span class="text-red-500">Error: ${error.message}</span>`
      pnlAiContentEl.innerHTML = `<span class="text-red-500">Error: ${error.message}</span>`
    } finally {
      hideLoading()
      generateAiSummaryBtn.removeAttribute('disabled')
    }
  })

  const analysisMenu = document.getElementById('premium-analysis-menu')
  analysisMenu?.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement

    const toggleBtn = target.closest('.premium-submenu-toggle')
    if (toggleBtn) {
      const submenu = toggleBtn.nextElementSibling as HTMLElement
      const chevron = toggleBtn.querySelector('.chevron-icon')
      submenu?.classList.toggle('hidden')
      chevron?.classList.toggle('rotate-180')
      return
    }

    const link = target.closest('.premium-sidebar-link') as HTMLAnchorElement
    if (link) {
      e.preventDefault()
      const targetId = link.dataset.target
      if (!targetId) return

      document.getElementById('admin-panel')?.classList.add('hidden')

      allMainContent.forEach((el) => el.classList.add('hidden'))
      const targetContent = document.getElementById(targetId)
      targetContent?.classList.remove('hidden')

      document.querySelectorAll('aside nav a').forEach((el) => el.classList.remove('bg-gray-100', 'font-semibold'))

      link.classList.add('bg-gray-100', 'font-semibold')
      const parentToggle = link.closest('.submenu-container')?.querySelector('.premium-submenu-toggle')
      parentToggle?.classList.add('font-semibold')

      // --- TRIGGER THE SETUP FOR THE SPECIFIC VIEW ---
      if (targetId === 'premium-placeholder-general-keuangan') {
        await setupPremiumGeneralFinance()
      } else if (targetId === 'premium-placeholder-general-penjualan') {
        await setupPremiumGeneralSales()
      } else if (targetId === 'premium-placeholder-general-produk') {
        await setupPremiumProductChannel()
      } else if (targetId === 'premium-placeholder-waktu-keuangan') {
        await setupPremiumTimeFinance()
      } else if (targetId === 'premium-placeholder-waktu-penjualan') {
        await setupPremiumTimeSales()
      } else if (targetId === 'premium-placeholder-waktu-produk') {
        await setupPremiumTimeProductChannel()
      } else if (targetId === 'premium-placeholder-cabang-keuangan') {
        await setupPremiumBranchFinance()
      } else if (targetId === 'premium-placeholder-cabang-penjualan') {
        await setupPremiumBranchSales()
      } else if (targetId === 'premium-placeholder-cabang-produk') {
        await setupPremiumBranchProductChannel()
      } else if (targetId === 'premium-placeholder-projection') {
        await setupPremiumProjectionView()
      } else if (targetId === 'premium-finslide-page') {
        await setupPremiumFinSlidePage()
      } else if (targetId === 'premium-recommendation-content') {
        await setupFinalyticsRecommendation(); 
      } else if (targetId === 'premium-business-highlight-content') {
  await setupBusinessHighlightView();
}
    }
  })

  // Link download buttons to their respective functions
  downloadSalesTargetBtn?.addEventListener('click', downloadSalesTargetTemplate)
  downloadPnlDataBtn?.addEventListener('click', downloadPnlTemplate)
  downloadPnlTargetBtn?.addEventListener('click', downloadPnlTargetTemplate)

  // Branch Members navigation:  // --- Navigation Handlers ---
  // My Branches
  branchesBtn?.addEventListener('click', (e) => {
    e.preventDefault()
    // [FIX] Use showContent to handle visibility AND highlighting
    showContent(branchesContent, branchesBtn) 
    
    try {
      renderBranchList('premium-branches-container')
    } catch (err) {
      console.error('[PREMIUM] Failed to render My Branches:', err)
      alert('Failed to load My Branches. Please try again later.')
    }
  })

  // We only want to set up the subscription once.

// This "selector" tells the store to only notify us
// when the 'userBrands' array *actually* changes.

brandsBtn?.addEventListener('click', (e) => {
    e.preventDefault()
    showContent(brandsContent, brandsBtn)

    try {
      renderBrandList('premium-brands-content')
    } catch (err) {
      console.error('[PREMIUM] Failed to render My Brands:', err)
      alert('Failed to load My Brands. Please try again later.')
    }
  })

  // Fallback delegation (in case node is re-rendered)
  document.addEventListener('click', (evt) => {
    const t = evt.target as HTMLElement
    if (t && t.closest && t.closest('#premium-goto-branches-btn')) {
      evt.preventDefault()
      allMainContent.forEach((el) => el.classList.add('hidden'))
      branchesContent?.classList.remove('hidden')
      try {
        renderBranchList('premium-branches-container')
      } catch (err) {
        console.error('[PREMIUM] Failed to render My Branches (delegated):', err)
        alert('Failed to load My Branches. Please try again later.')
      }
    }
  })

  // Branch Members navigation: show section and render member list
  branchMembersBtn?.addEventListener('click', async (e) => {
    e.preventDefault()
    // [FIX] Use showContent to handle visibility AND highlighting
    showContent(branchMembersContent, branchMembersBtn)

    try {
      const { selectedBranchId, userBranches } = $store.getAccountContext()
      let effectiveBranchId = selectedBranchId

      if (!effectiveBranchId && userBranches && userBranches.length > 0) {
        effectiveBranchId = userBranches[0]!.branchId
        $store.setSelectedBranchId(effectiveBranchId)
        try { localStorage.setItem('selectedBranchId', effectiveBranchId) } catch (err) { /* ignore */ }
      }

      const branchMembersSelect = document.getElementById('premium-branch-members-branch-select') as HTMLSelectElement | null
      if (branchMembersSelect) {
        if (userBranches && userBranches.length > 0) {
          branchMembersSelect.innerHTML = userBranches.map((b: any) => `<option value="${b.branchId}">${b.branchName}</option>`).join('')

          const defaultBranchId = effectiveBranchId && userBranches.some((b: any) => b.branchId === effectiveBranchId)
            ? effectiveBranchId
            : userBranches[0]!.branchId

          branchMembersSelect.value = defaultBranchId

          branchMembersSelect.addEventListener('change', async (ev) => {
            const val = (ev.target as HTMLSelectElement).value
            $store.setSelectedBranchId(val)
            try { localStorage.setItem('selectedBranchId', val) } catch (_err) { /* ignore */ }
            const selBranch = userBranches.find((b: any) => b.branchId === val)
            const selName = selBranch?.branchName || val
            const uid = currentUser?.uid || ''
            try {
              await renderMemberList('premium-branch-members-container', val, selName, uid)
            } catch (err) {
              console.error('[PREMIUM] Failed to re-render Branch Members after selector change:', err)
            }
          })
        } else {
          branchMembersSelect.innerHTML = '<option value="">No branches available</option>'
          branchMembersSelect.disabled = true
        }
      }

      if (!effectiveBranchId) {
        alert('No branches found in your account. Please create a branch first or contact an admin.')
        return
      }

      const branch = userBranches.find((b: any) => b.branchId === effectiveBranchId)
      const branchName = branch?.branchName || effectiveBranchId
      const uid = currentUser?.uid || ''
      await renderMemberList('premium-branch-members-container', effectiveBranchId, branchName, uid)
    } catch (error) {
      console.error('[PREMIUM] Failed to render Branch Members:', error)
      alert('Failed to load Branch Members. Please try again later.')
    }
  })

  // Helper to handle all file uploads
  const handleFileUpload = async (file: File | undefined, type: 'salesData' | 'pnlData') => {
    if (!file) { alert('Please select a file.'); return }
    
    // Get the branch context
    const { selectedBranchId, userBranches } = $store.getAccountContext()
    if (!selectedBranchId) {
      alert('No branch selected. Please select a branch from the dropdown before uploading.')
      return
    }
    const selectedBranch = userBranches.find(b => b.branchId === selectedBranchId)
    if (!selectedBranch) {
      alert('Could not find details for the selected branch.')
      return
    }

    try {
      switch (type) {
        case 'salesData':
          if (!selectedSalesFormat) throw new Error('Sales data format (ESB/Moka) was not selected.')
          await handleSalesDataUpload(file, selectedSalesFormat) 
          break
        
        case 'pnlData':
          showLoading({ message: 'Analyzing P&L file with AI...' })
          const { getAiPnlSuggestions } = await import('@/uploads/pnl')
          const { showPnlConfirmationModal } = await import('@/uploads/pnl-confirmation-modal')
          
          const aiSuggestions = await getAiPnlSuggestions(file)
          
          showLoading({ message: 'AI analysis complete. Please confirm.' })
          
          showPnlConfirmationModal({
            fileName: file.name,
            branchId: selectedBranch.branchId,
            branchName: aiSuggestions.branchName || selectedBranch.branchName, 
            period: aiSuggestions.period,
            suggestions: aiSuggestions.suggestions,
            onSaveComplete: () => {
              renderPremiumManageDataView() 
            }
          })
          break
      }
      
      // If successful (only for sales data now):
      if (type === 'salesData') {
        
        // 1. Manually clear the IndexedDB cache.
        await clearSummariesCache()
        console.log('[DEBUG] handleFileUpload: Cache cleared.')

        // 2. Reset all init flags so the setup functions re-run
        resetAllAnalysisViewFlags() 

        // 3. Refresh the "Manage Data" view
        await renderPremiumManageDataView()
        
        // 4. [NEW] Unlock the sidebar menus now that we have data!
        updateUiBasedOnData()
        
        console.log('[DEBUG] handleFileUpload: Data refresh complete.')
        alert(`Upload successful! Dashboard features are now unlocked.`)
      }

    } catch (error: any) {
      alert(`Upload failed: ${error.message}`)
      console.error(`Error during ${type} upload:`, error)
    } finally {
      hideLoading()
    }
  }

  $store.setInitFlag('premiumAnalysisInitialized', true)
  
  // --- [JOB_13] BRAND CREATION GATE (THE CLEAN FIX) ---
  
  const { showView } = await import('@/core/views')
  const { userBrands } = $store.getAccountContext()

  if (userBrands.length === 0) {
    // User has no brands, force the NEW brand creation gate view
    console.log('[Orchestrator] User has 0 brands. Forcing create-brand-gate view.')
    showView('create-brand-gate')
    
    // We put the initialization logic *back* here
    const { initializeBrandManagement } = await import('@/brands/brand-management')
    
    // We add the setTimeout(..., 0) to prevent the race condition
    // This gives the DOM 1ms to render before we attach the listener.
    setTimeout(() => {
      console.log('[Orchestrator] DOM ready, initializing brand management logic...')
      initializeBrandManagement()
    }, 0)
    
  } else {
    // User has brands, proceed to the main app as normal
    console.log('[Orchestrator] User has brands. Showing premium-analysis view.')
    showView('premium-analysis')
    showWelcome()
  }
}

/**
 * Aggregates an array of daily summaries by date.
 * This is used when "All Branches" is selected to show a combined total.
 */
function aggregateSummariesByDay(summaries: SalesSummary[]): SalesSummary[] {
  const map = new Map<string, SalesSummary>();

  for (const summary of summaries) {
    // Use the date string (YYYY-MM-DD) as the key
    const dateStr = summary.date.toISOString().split('T')[0];
    
    if (!map.has(dateStr)) {
      // --- [THIS IS THE FIX] ---
      // We can't use JSON.parse(JSON.stringify()) because it kills Date objects.
      // Instead, we use structuredClone, which is a modern, safe way to deep-copy.
      // If structuredClone isn't available, we create a new object and manually
      // deep-copy only the parts we are going to change.
      
      let newSummary: SalesSummary;
      if (typeof structuredClone === 'function') {
        newSummary = structuredClone(summary);
      } else {
        // Fallback for older environments: shallow copy + manual deep copy of mutated parts
        newSummary = { ...summary };
        newSummary.branches = [...summary.branches];
        newSummary.revenueByVisitPurpose = { ...summary.revenueByVisitPurpose };
        newSummary.paymentMethods = { ...summary.paymentMethods };
        newSummary.menuItemQuantities = JSON.parse(JSON.stringify(summary.menuItemQuantities || {}));
        newSummary.menuItemRevenues = JSON.parse(JSON.stringify(summary.menuItemRevenues || {}));
        newSummary.trafficByHour = [...(summary.trafficByHour || [])];
        newSummary.hourlyRevenue = [...(summary.hourlyRevenue || [])];
      }
      
      map.set(dateStr, newSummary);
      // --- [END FIX] ---
      continue;
    }

    // If date already exists, merge this summary into it
    const existing = map.get(dateStr)!;

    existing.totalOmzet += summary.totalOmzet;
    existing.totalTransactions += summary.totalTransactions;
    existing.totalItemsSold += summary.totalItemsSold;
    
    // Combine branch names (unique)
    existing.branches = [...new Set([...existing.branches, ...summary.branches])];

    // --- Helper functions to deep-merge the nested data ---
    const mergeObjects = (target: Record<string, number>, source: Record<string, number>) => {
      if (source) {
        for (const key in source) {
          target[key] = (target[key] || 0) + source[key];
        }
      }
    };
    
    const mergeNestedObjects = (target: Record<string, Record<string, number>>, source: Record<string, Record<string, number>>) => {
      if (source) {
        for (const category in source) {
          if (!target[category]) target[category] = {};
          for (const item in source[category]) {
            target[category][item] = (target[category][item] || 0) + source[category][item];
          }
        }
      }
    };
    // --- End helpers ---

    // Merge all the data
    mergeObjects(existing.revenueByVisitPurpose, summary.revenueByVisitPurpose);
    mergeObjects(existing.paymentMethods, summary.paymentMethods);
    mergeNestedObjects(existing.menuItemQuantities, summary.menuItemQuantities);
    mergeNestedObjects(existing.menuItemRevenues, summary.menuItemRevenues);
    
    // Merge hourly data
    if (existing.trafficByHour && summary.trafficByHour) {
      for (let i = 0; i < 24; i++) {
        existing.trafficByHour[i] = (existing.trafficByHour[i] || 0) + (summary.trafficByHour[i] || 0);
      }
    }
    if (existing.hourlyRevenue && summary.hourlyRevenue) {
       for (let i = 0; i < 24; i++) {
        existing.hourlyRevenue[i] = (existing.hourlyRevenue[i] || 0) + (summary.hourlyRevenue[i] || 0);
      }
    }
    // (Add more for other properties if needed)

    // Recalculate APC
    existing.apc = existing.totalTransactions > 0 ? existing.totalOmzet / existing.totalTransactions : 0;
  }
  
  // This sort will now work because `a.date` is still a Date object.
  return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
}

async function generatePremiumGeneralSales() {
  if (!currentUser) return

  // 1. Get DOM Elements
  const branchSelect = document.getElementById('premium-sales-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-sales-range-select') as HTMLSelectElement
  const periodSelect = document.getElementById('premium-sales-period-select') as HTMLSelectElement

  // 2. Get Values
  const selectedValue = branchSelect.value // This is the Branch ID (Real) or Branch Name (Demo)
  const selectedRange = rangeSelect.value
  const selectedPeriod = periodSelect.value

  if (!selectedValue || !selectedRange || !selectedPeriod) return

  // 3. Update Store View Data
  $store.clearViewData('premium-placeholder-general-penjualan')
  $store.setActiveViewData('premium-placeholder-general-penjualan', {}, { selectedBranch: selectedValue, selectedRange, selectedPeriod })

  // 4. Fetch Global Targets (Optional/Background)
  try {
    const targetsDocRef = doc(db, `users/${currentUser.uid}/globalConfig`, 'targets')
    const docSnap = await getDoc(targetsDocRef)
    if (docSnap.exists() && docSnap.data().sales) {
      const globalSalesTargets = docSnap.data().sales
      const mappedTargets = {
        'Omzet Harian': globalSalesTargets.totalOmzet > 0 ? globalSalesTargets.totalOmzet / 30 : 0,
        'Total Transaksi Per Hari': globalSalesTargets.totalTransactions > 0 ? globalSalesTargets.totalTransactions / 30 : 0,
        'Average Check': globalSalesTargets.avgCheck || 0,
      }
      $store.setConfigValue('activeSalesTarget', mappedTargets)
    } else {
      $store.setConfigValue('activeSalesTarget', {})
    }
  } catch (error) {
    console.error('Error fetching global sales targets:', error)
    $store.setConfigValue('activeSalesTarget', {})
  }

  // 5. Get All Data & Context
  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config
  const { selectedBrandId, userBranches } = $store.getAccountContext()

  // 6. Filter Data by Brand Context
  let brandData: SalesSummary[];
  if (isDemoDataActive) {
    brandData = allSalesData;
  } else {
    // For Real Data: Filter to show only branches in the selected Brand
    const branchIds = new Set(
        selectedBrandId 
        ? userBranches.filter(b => b.brandId === selectedBrandId).map(b => b.branchId)
        : userBranches.map(b => b.branchId)
    );
    // Filter source data using the branchId property added to summaries
    brandData = allSalesData.filter(s => branchIds.has((s as any).branchId));
  }
  
  // 7. Filter Data by Date
  let startDate: Date, endDate: Date
  const [yearStr, part] = selectedPeriod.split('-')
  const year = parseInt(yearStr, 10)

  switch (selectedRange) {
    case 'yearly': 
      startDate = new Date(year, 0, 1)
      endDate = new Date(year, 11, 31, 23, 59, 59)
      break
    case 'quarterly': 
      const quarter = parseInt(part.replace('Q', ''))
      const startMonth = (quarter - 1) * 3
      startDate = new Date(year, startMonth, 1)
      endDate = new Date(year, startMonth + 3, 0, 23, 59, 59)
      break
    case 'monthly': 
      startDate = new Date(year, parseInt(part) - 1, 1)
      endDate = new Date(year, parseInt(part), 0, 23, 59, 59)
      break
    case 'weekly': 
      const dayPart = parseInt(selectedPeriod.split('-')[2])
      startDate = new Date(year, parseInt(part) - 1, dayPart)
      endDate = new Date(startDate)
      endDate.setDate(startDate.getDate() + 6)
      endDate.setHours(23, 59, 59)
      break
    default: return
  }

  const periodData = brandData.filter((s) => s.date >= startDate && s.date <= endDate)

  // 8. Filter Data by Branch Selection (The Critical Fix)
  let finalDataForCharts: SalesSummary[];
  
  if (selectedValue === 'ALL') {
    // If ALL, we aggregate everything visible in this brand/period
    finalDataForCharts = aggregateSummariesByDay(periodData);
  } else {
    // If specific branch, filter by ID (Real) or Name (Demo)
    finalDataForCharts = periodData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });
  }

  // 9. Generate KPIs
  generateRingkasanFromSummaries(finalDataForCharts, [], { 
    omzet: 'premium-sales-total-omzet', 
    check: 'premium-sales-total-check', 
    avgCheck: 'premium-sales-avg-check', 
    omzetGrowth: 'premium-sales-omzet-growth', 
    checkGrowth: 'premium-sales-check-growth', 
    avgCheckGrowth: 'premium-sales-avg-check-growth' 
  })

  // 10. Update Charts
  const omzetTitle = document.getElementById('premium-sales-omzet-title') as HTMLElement
  const chartContainers = { 
    yearly: document.getElementById('premium-sales-omzet-yearly-container'), 
    quarterly: document.getElementById('premium-sales-omzet-quarterly-container'), 
    monthly: document.getElementById('premium-sales-omzet-monthly-container'), 
    weekly: document.getElementById('premium-sales-omzet-weekly-container') 
  }
  
  Object.values(chartContainers).forEach((c) => c?.classList.add('hidden'))
  const activeContainer = chartContainers[selectedRange as keyof typeof chartContainers]
  activeContainer?.classList.remove('hidden')
  
  if (omzetTitle) {
    omzetTitle.textContent = `Omzet ${selectedRange.charAt(0).toUpperCase() + selectedRange.slice(1)}`
  }

  if (selectedRange === 'yearly') generateOmzetYearlyChart(finalDataForCharts, 'premium-sales-omzet-yearly-chart');
  else if (selectedRange === 'quarterly') generateOmzetQuarterlyChart(finalDataForCharts, 'premium-sales-omzet-quarterly-chart');
  else if (selectedRange === 'monthly') generateOmzetHarianChartFromSummaries(finalDataForCharts, 'premium-sales-omzet-monthly-chart');
  else if (selectedRange === 'weekly') generateOmzetWeeklyChart(finalDataForCharts, 'premium-sales-omzet-weekly-chart');

  generateTcApcHarianChartFromSummaries(finalDataForCharts, 'premium-sales-tc-apc-chart')
  
  // 11. Update Heatmaps
  const heatmapContainer = document.getElementById('premium-sales-heatmap-container')
  if (heatmapContainer) {
    heatmapContainer.innerHTML = '' 
    if (selectedRange === 'weekly') {
        generateOmzetHeatmapFromSummaries(finalDataForCharts, 'premium-sales-heatmap-container');
    } else if (selectedRange === 'monthly') {
        generateDailyOmzetHeatmapFromSummaries(finalDataForCharts, 'premium-sales-heatmap-container');
    } else {
        const dataByMonth = finalDataForCharts.reduce((acc, summary) => { 
            const monthKey = summary.date.toISOString().slice(0, 7); 
            if (!acc[monthKey]) acc[monthKey] = []; 
            acc[monthKey].push(summary); 
            return acc 
        }, {} as Record<string, SalesSummary[]>)
        
        heatmapContainer.innerHTML = '<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4"></div>'
        const gridWrapper = heatmapContainer.querySelector('div')
        
        Object.keys(dataByMonth).sort().forEach((month, index) => { 
            const monthContainerId = `heatmap-month-${index}`; 
            const monthContainer = document.createElement('div'); 
            monthContainer.id = monthContainerId; 
            gridWrapper?.appendChild(monthContainer); 
            generateDailyOmzetHeatmapFromSummaries(dataByMonth[month], monthContainerId) 
        })
    }
  }
  generateOmzetHeatmapFromSummaries(finalDataForCharts, 'premium-sales-hourly-heatmap-container')
  generateSalesTrendHourlyDailyChartFromSummaries(finalDataForCharts, 'premium-sales-trend-chart')
}

async function setupPremiumGeneralSales() {
  const pageId = 'premium-placeholder-general-penjualan'
  if ($store.getInitFlag('premiumGeneralSalesInitialized')) {
    await generatePremiumGeneralSales()
    return
  }
  if (!currentUser) return

  const branchSelect = document.getElementById('premium-sales-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-sales-range-select') as HTMLSelectElement
  const periodSelect = document.getElementById('premium-sales-period-select') as HTMLSelectElement

  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext();
  const allSalesData = $store.getAllSalesData();

  let brandSalesData: SalesSummary[];
  
  // [FIX] Populate Dropdown with IDs
  if (isDemoDataActive) {
    brandSalesData = allSalesData;
    const branches = [...new Set(allSalesData.flatMap((s) => s.branches))].sort();
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('');
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    const branchIds = new Set(filteredBranches.map(b => b.branchId));
    
    brandSalesData = allSalesData.filter(s => branchIds.has((s as any).branchId));
    
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + filteredBranches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
  }

  const updatePeriodSelector = () => {
    const range = rangeSelect.value
    const selectedValue = branchSelect.value;

    // [FIX] Filter by ID or Name
    let branchData = brandSalesData;
    if (selectedValue !== 'ALL') {
       branchData = brandSalesData.filter(s => {
         if (isDemoDataActive) return s.branches.includes(selectedValue);
         return (s as any).branchId === selectedValue;
       });
    }
    
    const allDates = branchData.map((s) => s.date)
    if (allDates.length === 0) {
      periodSelect.innerHTML = '<option>No data available for this selection</option>'
      return
    }

    const periods = new Set<string>()
    if (range === 'yearly') allDates.forEach((d) => periods.add(String(d.getFullYear())));
    else if (range === 'monthly') allDates.forEach((d) => periods.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`));
    else if (range === 'quarterly') allDates.forEach((d) => { const quarter = Math.floor(d.getMonth() / 3) + 1; periods.add(`${d.getFullYear()}-Q${quarter}`) });
    else if (range === 'weekly') {
      const getMonday = (d: Date) => { const date = new Date(d); const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1); return new Date(date.setDate(diff)); }
      allDates.forEach((d) => { const monday = getMonday(d); periods.add(`${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`) });
    }

    const sortedPeriods = Array.from(periods).sort((a, b) => b.localeCompare(a))
    if (sortedPeriods.length === 0) { periodSelect.innerHTML = '<option>No data for this range</option>'; return; }

    periodSelect.innerHTML = sortedPeriods.map((p) => {
      if (range === 'yearly') return `<option value="${p}">${p}</option>`;
      if (range === 'monthly') { const [y, m] = p.split('-'); return `<option value="${p}">${new Date(parseInt(y), parseInt(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`; }
      if (range === 'quarterly') return `<option value="${p}">${p.replace('-Q', ' Q')}</option>`;
      if (range === 'weekly') { const [y, m, d] = p.split('-'); const label = `Week of ${new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`; return `<option value="${p}">${label}</option>`; }
      return '';
    }).join('');

    generatePremiumGeneralSales() 
  }

  branchSelect.addEventListener('change', updatePeriodSelector)
  rangeSelect.addEventListener('change', updatePeriodSelector)
  periodSelect.addEventListener('change', generatePremiumGeneralSales)

  setupTierGatedAI({ pageId, promptKey: 'general-penjualan', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumGeneralSalesInitialized', true)
  updatePeriodSelector() 
}

async function generatePremiumProductChannel() {
  if (!currentUser) return
  const branchSelect = document.getElementById('premium-product-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-product-range-select') as HTMLSelectElement
  const periodSelect = document.getElementById('premium-product-period-select') as HTMLSelectElement
  const slimSelectInstance = $store.getUIComponent('premiumProductMenuSelect')

  const selectedValue = branchSelect.value
  const selectedRange = rangeSelect.value
  const selectedPeriod = periodSelect.value

  if (!selectedValue || !selectedRange || !selectedPeriod) return

  $store.clearViewData('premium-placeholder-general-produk')
  $store.setActiveViewData('premium-placeholder-general-produk', {}, { selectedBranch: selectedValue, selectedRange, selectedPeriod })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-general-produk')

  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config
  const { selectedBrandId, userBranches } = $store.getAccountContext()

  // [FIX] Filter Logic by ID
  let brandData: SalesSummary[];
  if (isDemoDataActive) {
    brandData = allSalesData;
  } else {
    const branchIds = new Set(
        selectedBrandId 
        ? userBranches.filter(b => b.brandId === selectedBrandId).map(b => b.branchId)
        : userBranches.map(b => b.branchId)
    );
    brandData = allSalesData.filter(s => branchIds.has((s as any).branchId));
  }
  
  let branchData = brandData;
  if (selectedValue !== 'ALL') {
    branchData = brandData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });
  }

  let startDate: Date, endDate: Date
  try {
    const [year, part, dayPart] = selectedPeriod.split('-')
    switch (selectedRange) {
      case 'yearly': startDate = new Date(parseInt(year), 0, 1); endDate = new Date(parseInt(year), 11, 31, 23, 59, 59); break;
      case 'quarterly': const q = parseInt(part.replace('Q', '')); const sm = (q - 1) * 3; startDate = new Date(parseInt(year), sm, 1); endDate = new Date(parseInt(year), sm + 3, 0, 23, 59, 59); break;
      case 'monthly': startDate = new Date(parseInt(year), parseInt(part) - 1, 1); endDate = new Date(parseInt(year), parseInt(part), 0, 23, 59, 59); break;
      case 'weekly': startDate = new Date(parseInt(year), parseInt(part) - 1, parseInt(dayPart)); endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59); break;
      default: startDate = new Date(); endDate = new Date();
    }
  } catch (e) { startDate = new Date(); endDate = new Date(); }

  const filteredData = branchData.filter((s) => s.date >= startDate && s.date <= endDate)

  if (slimSelectInstance) drawGeneralMenuTrendChart(filteredData, 'premium-product-menu-trend-chart', slimSelectInstance, { alsoStore })
  generateChannelDonutChart(filteredData, 'premium-product-channel-chart', { alsoStore })
  generateOrderByCategoryDonutChart(filteredData, 'premium-product-category-chart', { alsoStore })
  generateTop10MenuTable(filteredData, 'premium-product-top-quantity-container', 'quantity', { alsoStore })
  generateTop10MenuTable(filteredData, 'premium-product-top-revenue-container', 'revenue', { alsoStore })
}

async function setupPremiumProductChannel() {
  const pageId = 'premium-placeholder-general-produk'
  if ($store.getInitFlag('premiumProductChannelInitialized')) {
    await generatePremiumProductChannel()
    return
  }
  if (!currentUser) return

  const branchSelect = document.getElementById('premium-product-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-product-range-select') as HTMLSelectElement
  const periodSelect = document.getElementById('premium-product-period-select') as HTMLSelectElement

  const { isDemoDataActive } = $store.getAnalysisState().config
  const { userBranches, selectedBrandId } = $store.getAccountContext()
  const allSalesData = $store.getAllSalesData()

  let brandSalesData: SalesSummary[];

  // [FIX] Populate Dropdown with IDs
  if (isDemoDataActive) {
    brandSalesData = allSalesData;
    const branches = [...new Set(allSalesData.flatMap((s) => s.branches))].sort();
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('');
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    const branchIds = new Set(filteredBranches.map(b => b.branchId));
    
    brandSalesData = allSalesData.filter(s => branchIds.has((s as any).branchId));
    
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + filteredBranches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
  }

  // Setup Menu Multi-Select
  const menuSelectElement = document.getElementById('premium-product-menu-select') as HTMLSelectElement
  const allMenuItems = [...new Set(brandSalesData.flatMap((s) => Object.keys(s.menuItemQuantities || {}).flatMap((cat) => Object.keys(s.menuItemQuantities[cat]))))].sort()
  menuSelectElement.innerHTML = allMenuItems.map((name) => `<option value="${name}">${name}</option>`).join('')

  const slimSelectInstance = new SlimSelect({
    select: '#premium-product-menu-select',
    events: { afterChange: () => generatePremiumProductChannel() },
  })
  $store.setUIComponent('premiumProductMenuSelect', slimSelectInstance)

  const updatePeriodSelector = () => {
    const range = rangeSelect.value
    const selectedValue = branchSelect.value

    // [FIX] Filter by ID or Name
    let branchData = brandSalesData;
    if (selectedValue !== 'ALL') {
       branchData = brandSalesData.filter(s => {
         if (isDemoDataActive) return s.branches.includes(selectedValue);
         return (s as any).branchId === selectedValue;
       });
    }

    const allDates = branchData.map((s) => s.date)
    if (allDates.length === 0) {
      periodSelect.innerHTML = '<option>No data available</option>'
      generatePremiumProductChannel()
      return
    }

    const periods = new Set<string>()
    if (range === 'yearly') allDates.forEach((d) => periods.add(String(d.getFullYear())));
    else if (range === 'monthly') allDates.forEach((d) => periods.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`));
    else if (range === 'quarterly') allDates.forEach((d) => { const q = Math.floor(d.getMonth() / 3) + 1; periods.add(`${d.getFullYear()}-Q${q}`) });
    else if (range === 'weekly') {
      const getMonday = (d: Date) => { const date = new Date(d); const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1); return new Date(date.setDate(diff)); }
      allDates.forEach((d) => { const monday = getMonday(d); periods.add(`${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`) });
    }

    const sortedPeriods = Array.from(periods).sort((a, b) => b.localeCompare(a))
    if (sortedPeriods.length === 0) { periodSelect.innerHTML = '<option>No data for this range</option>'; generatePremiumProductChannel(); return; }

    periodSelect.innerHTML = sortedPeriods.map((p) => {
      if (range === 'yearly') return `<option value="${p}">${p}</option>`;
      if (range === 'monthly') { const [y, m] = p.split('-'); return `<option value="${p}">${new Date(parseInt(y), parseInt(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`; }
      if (range === 'quarterly') return `<option value="${p}">${p.replace('-Q', ' Q')}</option>`;
      if (range === 'weekly') { const [y, m, d] = p.split('-'); const label = `Week of ${new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`; return `<option value="${p}">${label}</option>`; }
      return '';
    }).join('');

    // Pre-select top 3 items
    const initialPeriod = sortedPeriods[0];
    let initialFilteredData: any[] = [];
    if (initialPeriod) {
       // ... (Keep existing filtering logic for initialPeriod) ...
       // Re-implementing simplified version to ensure it works
       let startDate: Date, endDate: Date;
       const [year, part, dayPart] = initialPeriod.split('-');
       switch (range) {
         case 'yearly': startDate = new Date(parseInt(year), 0, 1); endDate = new Date(parseInt(year), 11, 31, 23, 59, 59); break;
         case 'quarterly': const q = parseInt(part.replace('Q', '')); const sm = (q - 1) * 3; startDate = new Date(parseInt(year), sm, 1); endDate = new Date(parseInt(year), sm + 3, 0, 23, 59, 59); break;
         case 'monthly': startDate = new Date(parseInt(year), parseInt(part) - 1, 1); endDate = new Date(parseInt(year), parseInt(part), 0, 23, 59, 59); break;
         case 'weekly': startDate = new Date(parseInt(year), parseInt(part) - 1, parseInt(dayPart)); endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59); break;
         default: startDate = new Date(); endDate = new Date();
       }
       initialFilteredData = branchData.filter((s) => s.date >= startDate && s.date <= endDate);
    }

    const menuQuantities = new Map<string, number>()
    initialFilteredData.forEach((summary) => {
       if (!summary.menuItemQuantities) return
      for (const category in summary.menuItemQuantities) {
        for (const itemName in summary.menuItemQuantities[category]) {
          const quantity = summary.menuItemQuantities[category][itemName]
          menuQuantities.set(itemName, (menuQuantities.get(itemName) || 0) + quantity)
        }
      }
    })
    const top3MenuNames = Array.from(menuQuantities.entries()).sort(([, qtyA], [, qtyB]) => qtyB - qtyA).slice(0, 3).map(([name]) => name)
    slimSelectInstance.setSelected(top3MenuNames)
    
    generatePremiumProductChannel()
  }

  branchSelect.addEventListener('change', updatePeriodSelector)
  rangeSelect.addEventListener('change', updatePeriodSelector)
  periodSelect.addEventListener('change', generatePremiumProductChannel)

  setupTierGatedAI({ pageId, promptKey: 'general-produk-channel', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumProductChannelInitialized', true)
  updatePeriodSelector()
}

async function generatePremiumTimeFinance(allReports: any[]) {
  if (!currentUser) return
  const branchSelect = document.getElementById('premium-time-finance-branch-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-finance-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-finance-period-b-select') as HTMLSelectElement

  const selectedBranchValue = branchSelect.value // ID
  const periodA = periodASelect.value
  const periodB = periodBSelect.value

  if (!selectedBranchValue || !periodA || !periodB) return

  $store.clearViewData('premium-placeholder-waktu-keuangan')
  $store.setActiveViewData('premium-placeholder-waktu-keuangan', {}, { selectedBranch: selectedBranchValue, periodA, periodB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-waktu-keuangan')

  showLoading({ message: 'Fetching P&L data for comparison...', value: 30 })
  
  const { isDemoDataActive } = $store.getAnalysisState().config;

  // [FIX] Robust Report Finding (ID vs Name)
  const findReport = (period: string) => {
      if (isDemoDataActive) {
          return allReports.find((r) => r.period === period && r.branchName === selectedBranchValue);
      }
      return allReports.find((r) => r.period === period && r.branchId === selectedBranchValue);
  }

  const reportA = findReport(periodA)
  const reportB = findReport(periodB)

  // Populate Views
  generatePnlComparisonTable(reportA, reportB, 'premium-time-finance-pnl-comparison-container', { alsoStore })
  generateRatioComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-cogs-chart', metric: 'Harga Pokok Produksi', title: 'COGS', alsoStore })
  generateRatioComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-gpm-chart', metric: 'Laba Kotor (Gross Profit)', title: 'Gross Profit', alsoStore })
  
  // [FIX] Apply the "Multiple Category Array" fix here too for Advertising/Wages
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-wages-chart', mainCategory: ['Beban Operasional (OPEX)', 'Beban Non Operasional'], subCategory: 'Wages', title: 'Wages', alsoStore })
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-rent-chart', mainCategory: ['Beban Operasional (OPEX)', 'Beban Non Operasional'], subCategory: 'Rent', title: 'Rent', alsoStore })
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-advertising-chart', mainCategory: ['Beban Operasional (OPEX)', 'Beban Non Operasional'], subCategory: 'Advertising', title: 'Advertising', alsoStore })
  
  generateRatioComparisonChart(reportA, reportB, { canvasId: 'premium-time-finance-npm-chart', metric: 'Pendapatan Bersih (Net Income)', title: 'Net Income', alsoStore })

  hideLoading()
}


async function setupPremiumTimeFinance() {
  const pageId = 'premium-placeholder-waktu-keuangan'
  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()

  const allReports: any[] = []
  if (isDemoDataActive) {
    const reportsRef = getPnlReportsCollectionRef('demo') 
    const reportsSnap = await getDocs(reportsRef)
    reportsSnap.docs.forEach((doc) => allReports.push(doc.data()))
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches
    const allReportsSnapshots = await Promise.all(
      filteredBranches.map(branch => getDocs(getPnlReportsCollectionRef(branch.branchId)))
    )
    // [FIX] Inject branchId immediately for robust filtering later
    allReports.push(...allReportsSnapshots.flatMap((snapshot, i) => 
        snapshot.docs.map(doc => ({ ...doc.data(), branchId: filteredBranches[i].branchId }))
    ));
  }

  if ($store.getInitFlag('premiumTimeFinanceInitialized')) {
    await generatePremiumTimeFinance(allReports)
    return
  }
  if (!currentUser) return

  const branchSelect = document.getElementById('premium-time-finance-branch-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-finance-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-finance-period-b-select') as HTMLSelectElement

  // [FIX] Populate Branch Select with IDs
  let availableBranches: {id: string, name: string}[] = [];
  if (isDemoDataActive) {
    const names = [...new Set(allReports.map(r => r.branchName))].sort();
    availableBranches = names.map(n => ({ id: n, name: n }));
  } else {
    const uniqueBranchIds = new Set(allReports.map(r => r.branchId));
    availableBranches = userBranches
        .filter(b => uniqueBranchIds.has(b.branchId))
        .map(b => ({ id: b.branchId, name: b.branchName }))
        .sort((a, b) => a.name.localeCompare(b.name));
  }

  if (availableBranches.length === 0) {
    branchSelect.innerHTML = '<option>No P&L data found</option>';
    return;
  }

  branchSelect.innerHTML = availableBranches.map(b => `<option value="${b.id}">${b.name}</option>`).join('');

  const updatePeriodSelectors = async (allReportsData: any[]) => { 
    const selectedBranchValue = branchSelect.value; // ID or Name
    const container = document.getElementById('waktu-pnl-comparison-container')

    // [FIX] Filter reports by ID (Real) or Name (Demo)
    const reportsForBranch = allReportsData.filter(r => {
        if (isDemoDataActive) return r.branchName === selectedBranchValue;
        return r.branchId === selectedBranchValue;
    });

    const periods = [...new Set(reportsForBranch.map(r => r.period))].sort().reverse();

    if (periods.length < 2) {
      periodASelect.innerHTML = '<option>Not enough data</option>'
      periodBSelect.innerHTML = '<option>Not enough data</option>'
      if (container) container.innerHTML = '<p class="text-gray-500 p-4 text-center">This branch lacks sufficient P&L reports (need 2+ periods).</p>'
      return
    }

    const optionsHtml = periods.map((p) => {
      const label = new Date(p + '-02').toLocaleString('default', { month: 'long', year: 'numeric' })
      return `<option value="${p}">${label}</option>`
    }).join('')

    periodASelect.innerHTML = optionsHtml
    periodBSelect.innerHTML = optionsHtml
    periodASelect.value = periods[0]
    periodBSelect.value = periods[1] || periods[0]

    await generatePremiumTimeFinance(allReportsData) 
  }

  branchSelect.addEventListener('change', () => updatePeriodSelectors(allReports))
  periodASelect.addEventListener('change', () => generatePremiumTimeFinance(allReports))
  periodBSelect.addEventListener('change', () => generatePremiumTimeFinance(allReports))

  setupTierGatedAI({ pageId, promptKey: 'waktu-keuangan', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumTimeFinanceInitialized', true)
  await updatePeriodSelectors(allReports) 
}

// In: apps/web/src/analysis/premium/orchestrator.ts
// REPLACE this function:

// --- [FIXED] Time Comparison Sales ---

async function setupPremiumTimeSales() {
  const pageId = 'premium-placeholder-waktu-penjualan'
  if ($store.getInitFlag('premiumTimeSalesInitialized')) {
    await generatePremiumTimeSales()
    return
  }
  if (!currentUser) return

  const branchSelect = document.getElementById('premium-time-sales-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-time-sales-range-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-sales-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-sales-period-b-select') as HTMLSelectElement

  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()
  const allSalesData = $store.getAllSalesData()

  let brandSalesData: SalesSummary[];

  // [FIX] Populate Dropdown with IDs
  if (isDemoDataActive) {
    brandSalesData = allSalesData;
    const branches = [...new Set(allSalesData.flatMap((s) => s.branches))].sort();
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('');
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    const branchIds = new Set(filteredBranches.map(b => b.branchId));
    
    brandSalesData = allSalesData.filter(s => branchIds.has((s as any).branchId));
    
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + filteredBranches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
  }

  const updatePeriodSelectors = async () => {
    const range = rangeSelect.value
    const selectedValue = branchSelect.value;
    
    let branchData = brandSalesData
    if (selectedValue !== 'ALL') {
       branchData = brandSalesData.filter(s => {
         if (isDemoDataActive) return s.branches.includes(selectedValue);
         return (s as any).branchId === selectedValue;
       });
    }

    const allDates = branchData.map((s) => s.date)
    if (allDates.length === 0) {
      periodASelect.innerHTML = '<option>No data available</option>'
      periodBSelect.innerHTML = '<option>No data available</option>'
      return
    }

    const periods = new Set<string>()
    if (range === 'yearly') allDates.forEach((d) => periods.add(String(d.getFullYear())));
    else if (range === 'monthly') allDates.forEach((d) => periods.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`));
    else if (range === 'quarterly') allDates.forEach((d) => { const q = Math.floor(d.getMonth() / 3) + 1; periods.add(`${d.getFullYear()}-Q${q}`) });
    else if (range === 'weekly') {
      const getMonday = (d: Date) => { const date = new Date(d); const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1); return new Date(date.setDate(diff)); }
      allDates.forEach((d) => { const monday = getMonday(d); periods.add(`${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`) });
    }

    const sortedPeriods = Array.from(periods).sort((a, b) => b.localeCompare(a))
    if (sortedPeriods.length < 2) {
      periodASelect.innerHTML = '<option>Not enough data</option>'
      periodBSelect.innerHTML = '<option>Not enough data</option>'
      return
    }

    const optionsHtml = sortedPeriods.map((p) => {
      if (range === 'yearly') return `<option value="${p}">${p}</option>`;
      if (range === 'monthly') { const [y, m] = p.split('-'); return `<option value="${p}">${new Date(parseInt(y), parseInt(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`; }
      if (range === 'quarterly') return `<option value="${p}">${p.replace('-Q', ' Q')}</option>`;
      if (range === 'weekly') { const [y, m, d] = p.split('-'); const label = `Week of ${new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`; return `<option value="${p}">${label}</option>`; }
      return '';
    }).join('');

    periodASelect.innerHTML = optionsHtml
    periodBSelect.innerHTML = optionsHtml
    periodASelect.value = sortedPeriods[1] || sortedPeriods[0]
    periodBSelect.value = sortedPeriods[0]

    await generatePremiumTimeSales()
  }

  branchSelect.addEventListener('change', updatePeriodSelectors)
  rangeSelect.addEventListener('change', updatePeriodSelectors)
  periodASelect.addEventListener('change', generatePremiumTimeSales)
  periodBSelect.addEventListener('change', generatePremiumTimeSales)

  setupTierGatedAI({ pageId, promptKey: 'waktu-penjualan', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumTimeSalesInitialized', true)
  await updatePeriodSelectors()
}

async function generatePremiumTimeSales() {
  if (!currentUser) return
  const branchSelect = document.getElementById('premium-time-sales-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-time-sales-range-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-sales-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-sales-period-b-select') as HTMLSelectElement

  const selectedValue = branchSelect.value
  const selectedRange = rangeSelect.value
  const periodA = periodASelect.value
  const periodB = periodBSelect.value

  if (!selectedValue || !selectedRange || !periodA || !periodB) return

  $store.clearViewData('premium-placeholder-waktu-penjualan')
  $store.setActiveViewData('premium-placeholder-waktu-penjualan', {}, { selectedBranch: selectedValue, selectedRange, periodA, periodB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-waktu-penjualan')

  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config
  const { selectedBrandId, userBranches } = $store.getAccountContext()

  // [FIX] Filter by ID
  let brandData: SalesSummary[];
  if (isDemoDataActive) {
    brandData = allSalesData;
  } else {
    const branchIds = new Set(
        selectedBrandId 
        ? userBranches.filter(b => b.brandId === selectedBrandId).map(b => b.branchId)
        : userBranches.map(b => b.branchId)
    );
    brandData = allSalesData.filter(s => branchIds.has((s as any).branchId));
  }
  
  let branchData = brandData;
  if (selectedValue !== 'ALL') {
    branchData = brandData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });
  }

  const parsePeriod = (period: string, range: string) => {
    const [year, part, dayPart] = period.split('-')
    let startDate: Date, endDate: Date
    switch (range) {
      case 'yearly': startDate = new Date(parseInt(year), 0, 1); endDate = new Date(parseInt(year), 11, 31, 23, 59, 59); break
      case 'quarterly': const q = parseInt(part.replace('Q', '')); const sm = (q - 1) * 3; startDate = new Date(parseInt(year), sm, 1); endDate = new Date(parseInt(year), sm + 3, 0, 23, 59, 59); break
      case 'monthly': startDate = new Date(parseInt(year), parseInt(part) - 1, 1); endDate = new Date(parseInt(year), parseInt(part), 0, 23, 59, 59); break
      case 'weekly': startDate = new Date(parseInt(year), parseInt(part) - 1, parseInt(dayPart)); endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59); break
      default: startDate = new Date(); endDate = new Date()
    }
    return { startDate, endDate }
  }

  const rangeA = parsePeriod(periodA, selectedRange)
  const rangeB = parsePeriod(periodB, selectedRange)

  let periodAData = branchData.filter((s) => s.date >= rangeA.startDate && s.date <= rangeA.endDate)
  let periodBData = branchData.filter((s) => s.date >= rangeB.startDate && s.date <= rangeB.endDate)

  if (selectedValue === 'ALL') {
    periodAData = aggregateSummariesByDay(periodAData);
    periodBData = aggregateSummariesByDay(periodBData);
  }

  populateTimeComparisonKPIs(periodAData, periodBData, { alsoStore })
  generatePremiumTimeComparisonChart(periodAData, periodBData, selectedRange, 'premium-time-sales-omzet-chart', 'totalOmzet', { alsoStore })
  generatePremiumTimeComparisonChart(periodAData, periodBData, selectedRange, 'premium-time-sales-tc-apc-chart', 'apc', { alsoStore })
  generateOmzetHeatmapFromSummaries(periodBData, 'premium-time-sales-heatmap-container', { alsoStore })
}

// --- [FIXED] Time Comparison Product ---

async function setupPremiumTimeProductChannel() {
  const pageId = 'premium-placeholder-waktu-produk'
  if ($store.getInitFlag('premiumTimeProductInitialized')) {
    await generatePremiumTimeProductChannel()
    return
  }
  if (!currentUser) return

  const branchSelect = document.getElementById('premium-time-product-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-time-product-range-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-product-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-product-period-b-select') as HTMLSelectElement

  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()
  const allSalesData = $store.getAllSalesData()

  let brandSalesData: SalesSummary[];

  // [FIX] Populate Dropdown with IDs
  if (isDemoDataActive) {
    brandSalesData = allSalesData;
    const branches = [...new Set(allSalesData.flatMap((s) => s.branches))].sort();
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + branches.map((b) => `<option value="${b}">${b}</option>`).join('');
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches;
    const branchIds = new Set(filteredBranches.map(b => b.branchId));
    
    brandSalesData = allSalesData.filter(s => branchIds.has((s as any).branchId));
    
    branchSelect.innerHTML = `<option value="ALL">All Branches (Brand)</option>` + filteredBranches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
  }

  const updatePeriodSelectors = async () => {
    const range = rangeSelect.value
    const selectedValue = branchSelect.value;
    
    let branchData = brandSalesData
    if (selectedValue !== 'ALL') {
       branchData = brandSalesData.filter(s => {
         if (isDemoDataActive) return s.branches.includes(selectedValue);
         return (s as any).branchId === selectedValue;
       });
    }

    const allDates = branchData.map((s) => new Date(s.date))
    if (allDates.length === 0) {
      periodASelect.innerHTML = '<option>No data available</option>'
      periodBSelect.innerHTML = '<option>No data available</option>'
      return
    }

    const periods = new Set<string>()
    if (range === 'yearly') allDates.forEach((d) => { if (!isNaN(d.getTime())) periods.add(String(d.getFullYear())) });
    else if (range === 'monthly') allDates.forEach((d) => { if (!isNaN(d.getTime())) periods.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`) });
    else if (range === 'quarterly') allDates.forEach((d) => { if (isNaN(d.getTime())) return; const quarter = Math.floor(d.getMonth() / 3) + 1; periods.add(`${d.getFullYear()}-Q${quarter}`) });
    else if (range === 'weekly') {
      const getMonday = (d: Date) => { const date = new Date(d); const day = date.getDay(); const diff = date.getDate() - day + (day === 0 ? -6 : 1); return new Date(date.setDate(diff)); }
      allDates.forEach((d) => { if (isNaN(d.getTime())) return; const monday = getMonday(d); periods.add(`${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`) });
    }

    const sortedPeriods = Array.from(periods).sort((a, b) => b.localeCompare(a))
    if (sortedPeriods.length < 2) {
      periodASelect.innerHTML = '<option>Not enough data</option>'
      periodBSelect.innerHTML = '<option>Not enough data</option>'
      return
    }

    const optionsHtml = sortedPeriods.map((p) => {
      if (range === 'yearly') return `<option value="${p}">${p}</option>`;
      if (range === 'monthly') { const [y, m] = p.split('-'); return `<option value="${p}">${new Date(parseInt(y), parseInt(m) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`; }
      if (range === 'quarterly') return `<option value="${p}">${p.replace('-Q', ' Q')}</option>`;
      if (range === 'weekly') { const [y, m, d] = p.split('-'); const label = `Week of ${new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}`; return `<option value="${p}">${label}</option>`; }
      return '';
    }).join('');

    periodASelect.innerHTML = optionsHtml
    periodBSelect.innerHTML = optionsHtml
    periodASelect.value = sortedPeriods[1] || sortedPeriods[0]
    periodBSelect.value = sortedPeriods[0]

    // Pre-select menu items
    const initialPeriodB = sortedPeriods[0];
    let initialDataB: any[] = [];
    if (initialPeriodB) {
        const parsePeriod = (period: string, range: string) => { const [year, part, dayPart] = period.split('-'); let startDate: Date, endDate: Date; switch (range) { case 'yearly': startDate = new Date(parseInt(year), 0, 1); endDate = new Date(parseInt(year), 11, 31, 23, 59, 59); break; case 'quarterly': const q = parseInt(part.replace('Q', '')); const sm = (q - 1) * 3; startDate = new Date(parseInt(year), sm, 1); endDate = new Date(parseInt(year), sm + 3, 0, 23, 59, 59); break; case 'monthly': startDate = new Date(parseInt(year), parseInt(part) - 1, 1); endDate = new Date(parseInt(year), parseInt(part), 0, 23, 59, 59); break; case 'weekly': startDate = new Date(parseInt(year), parseInt(part) - 1, parseInt(dayPart)); endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59); break; default: startDate = new Date(); endDate = new Date() } return { startDate, endDate } }
        const rangeB = parsePeriod(initialPeriodB, range)
        initialDataB = branchData.filter((s) => s.date >= rangeB.startDate && s.date <= rangeB.endDate)
    }
    const menuQuantities = new Map<string, number>()
    initialDataB.forEach((summary) => {
      if (!summary.menuItemQuantities) return
      for (const category in summary.menuItemQuantities) {
        for (const itemName in summary.menuItemQuantities[category]) {
          const quantity = summary.menuItemQuantities[category][itemName]
          menuQuantities.set(itemName, (menuQuantities.get(itemName) || 0) + quantity)
        }
      }
    })
    const top3MenuNames = Array.from(menuQuantities.entries()).sort(([, qtyA], [, qtyB]) => qtyB - qtyA).slice(0, 3).map(([name]) => name)
    const slimSelectInstance = $store.getUIComponent('premiumTimeProductMenuSelect')
    if (slimSelectInstance) slimSelectInstance.setSelected(top3MenuNames)
  }

  // Setup Menu Select
  const menuSelectElement = document.getElementById('premium-time-product-menu-select') as HTMLSelectElement
  const allMenuItems = [...new Set(brandSalesData.flatMap((s) => Object.keys(s.menuItemQuantities || {}).flatMap((cat) => Object.keys(s.menuItemQuantities[cat]))))].sort()
  menuSelectElement.innerHTML = allMenuItems.map((name) => `<option value="${name}">${name}</option>`).join('')

  const slimSelectInstance = new SlimSelect({
    select: '#premium-time-product-menu-select',
    events: { afterChange: () => generatePremiumTimeProductChannel() },
  })
  $store.setUIComponent('premiumTimeProductMenuSelect', slimSelectInstance)

  branchSelect.addEventListener('change', updatePeriodSelectors)
  rangeSelect.addEventListener('change', updatePeriodSelectors)
  periodASelect.addEventListener('change', generatePremiumTimeProductChannel)
  periodBSelect.addEventListener('change', generatePremiumTimeProductChannel)

  setupTierGatedAI({ pageId, promptKey: 'waktu-produk-channel', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumTimeProductInitialized', true)
  await updatePeriodSelectors()
}

async function generatePremiumTimeProductChannel() {
  if (!currentUser) return
  const branchSelect = document.getElementById('premium-time-product-branch-select') as HTMLSelectElement
  const rangeSelect = document.getElementById('premium-time-product-range-select') as HTMLSelectElement
  const periodASelect = document.getElementById('premium-time-product-period-a-select') as HTMLSelectElement
  const periodBSelect = document.getElementById('premium-time-product-period-b-select') as HTMLSelectElement
  const slimSelectInstance = $store.getUIComponent('premiumTimeProductMenuSelect')

  const selectedValue = branchSelect.value
  const selectedRange = rangeSelect.value
  const periodA = periodASelect.value
  const periodB = periodBSelect.value

  if (!selectedValue || !selectedRange || !periodA || !periodB) return

  $store.clearViewData('premium-placeholder-waktu-produk')
  $store.setActiveViewData('premium-placeholder-waktu-produk', {}, { selectedBranch: selectedValue, selectedRange, periodA, periodB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-waktu-produk')

  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config
  const { selectedBrandId, userBranches } = $store.getAccountContext()

  // [FIX] Filter by ID
  let brandData: SalesSummary[];
  if (isDemoDataActive) {
    brandData = allSalesData;
  } else {
    const branchIds = new Set(
        selectedBrandId 
        ? userBranches.filter(b => b.brandId === selectedBrandId).map(b => b.branchId)
        : userBranches.map(b => b.branchId)
    );
    brandData = allSalesData.filter(s => branchIds.has((s as any).branchId));
  }
  
  const parsePeriod = (period: string, range: string) => {
    const [year, part, dayPart] = period.split('-')
    let startDate: Date, endDate: Date
    switch (range) {
      case 'yearly': startDate = new Date(parseInt(year), 0, 1); endDate = new Date(parseInt(year), 11, 31, 23, 59, 59); break
      case 'quarterly': const q = parseInt(part.replace('Q', '')); const sm = (q - 1) * 3; startDate = new Date(parseInt(year), sm, 1); endDate = new Date(parseInt(year), sm + 3, 0, 23, 59, 59); break
      case 'monthly': startDate = new Date(parseInt(year), parseInt(part) - 1, 1); endDate = new Date(parseInt(year), parseInt(part), 0, 23, 59, 59); break
      case 'weekly': startDate = new Date(parseInt(year), parseInt(part) - 1, parseInt(dayPart)); endDate = new Date(startDate); endDate.setDate(startDate.getDate() + 6); endDate.setHours(23, 59, 59); break;
      default: startDate = new Date(); endDate = new Date()
    }
    return { startDate, endDate }
  }

  const rangeA = parsePeriod(periodA, selectedRange)
  const rangeB = parsePeriod(periodB, selectedRange)

  const rawPeriodAData = brandData.filter((s) => s.date >= rangeA.startDate && s.date <= rangeA.endDate)
  const rawPeriodBData = brandData.filter((s) => s.date >= rangeB.startDate && s.date <= rangeB.endDate)

  // [FIX] Aggregate if ALL, else filter by ID
  let periodAData: SalesSummary[];
  let periodBData: SalesSummary[];

  if (selectedValue === 'ALL') {
    periodAData = aggregateSummariesByDay(rawPeriodAData);
    periodBData = aggregateSummariesByDay(rawPeriodBData);
  } else {
    periodAData = rawPeriodAData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });
    periodBData = rawPeriodBData.filter((s) => {
        if (isDemoDataActive) return s.branches.includes(selectedValue);
        return (s as any).branchId === selectedValue;
    });
  }

  if (slimSelectInstance) {
    generatePremiumMenuTrendComparisonChart(periodAData, periodBData, selectedRange, 'premium-time-product-menu-trend-chart', slimSelectInstance.getSelected(), { alsoStore })
  }
  generateChannelComparisonChart(periodAData, periodBData, 'premium-time-product-channel-chart', { alsoStore })
  generateCategoryComparisonChart(periodAData, periodBData, 'premium-time-product-category-chart', { alsoStore })
  generateTop10ComparisonTable(periodAData, periodBData, 'premium-time-product-top-quantity-container', 'quantity', { alsoStore })
  generateTop10ComparisonTable(periodAData, periodBData, 'premium-time-product-top-revenue-container', 'revenue', { alsoStore })
}

// In: apps/web/src/analysis/premium/orchestrator.ts
// Replace this entire function

async function generatePremiumBranchFinance(allReports: any[]) {
  if (!currentUser) return
  const periodSelect = document.getElementById('premium-branch-finance-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-finance-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-finance-branch-b-select') as HTMLSelectElement

  const selectedPeriod = periodSelect.value
  const branchA = branchASelect.value // ID
  const branchB = branchBSelect.value // ID

  if (!selectedPeriod || !branchA || !branchB) return

  // Store View Data
  $store.clearViewData('premium-placeholder-cabang-keuangan')
  $store.setActiveViewData('premium-placeholder-cabang-keuangan', {}, { selectedPeriod, branchA, branchB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-cabang-keuangan')

  const { isDemoDataActive } = $store.getAnalysisState().config;
  let reportA, reportB;
  
  if (isDemoDataActive) {
      // Demo mode uses Names directly
      reportA = allReports.find(r => r.period === selectedPeriod && r.branchName === branchA);
      reportB = allReports.find(r => r.period === selectedPeriod && r.branchName === branchB);
  } else {
      // Real mode uses IDs
      // [FIX] Find report by ID
      const rawReportA = allReports.find(r => r.period === selectedPeriod && r.branchId === branchA);
      const rawReportB = allReports.find(r => r.period === selectedPeriod && r.branchId === branchB);

      // [FIX] Patch the Branch Name in the report object so charts display correctly
      // The chart functions typically use report.branchName for labels
      const { userBranches } = $store.getAccountContext();
      const nameA = userBranches.find(b => b.branchId === branchA)?.branchName || 'Branch A';
      const nameB = userBranches.find(b => b.branchId === branchB)?.branchName || 'Branch B';

      if (rawReportA) reportA = { ...rawReportA, branchName: nameA };
      if (rawReportB) reportB = { ...rawReportB, branchName: nameB };
  }

  generateBranchPnlComparisonTable(reportA, reportB, 'premium-branch-finance-pnl-comparison-container', { alsoStore })
  generateBranchRatioComparisonChart(reportA, reportB, { canvasId: 'premium-branch-finance-cogs-chart', metric: 'Harga Pokok Produksi', title: 'COGS', alsoStore })
  generateBranchRatioComparisonChart(reportA, reportB, { canvasId: 'premium-branch-finance-gpm-chart', metric: 'Laba Kotor (Gross Profit)', title: 'Gross Profit', alsoStore })
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { canvasId: 'premium-branch-finance-wages-chart', mainCategory: 'Beban Operasional (OPEX)', subCategory: 'Wages', title: 'Wages', alsoStore })
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { canvasId: 'premium-branch-finance-rent-chart', mainCategory: 'Beban Operasional (OPEX)', subCategory: 'Rent', title: 'Rent', alsoStore })
  // [FIX] Look in BOTH categories for Advertising
  generatePremiumSubCategoryComparisonChart(reportA, reportB, { 
    canvasId: 'premium-branch-finance-advertising-chart', 
    mainCategory: ['Beban Non Operasional', 'Beban Operasional (OPEX)'], // <--- Changed to Array
    subCategory: 'Advertising', 
    title: 'Advertising', 
    alsoStore 
  })
  generateBranchRatioComparisonChart(reportA, reportB, { canvasId: 'premium-branch-finance-npm-chart', metric: 'Pendapatan Bersih (Net Income)', title: 'Net Income', alsoStore })
}

async function setupPremiumBranchFinance() {
  const pageId = 'premium-placeholder-cabang-keuangan'
  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()

  const allReports: any[] = []
  
  if (isDemoDataActive) {
    const reportsRef = getPnlReportsCollectionRef('demo') 
    const reportsSnap = await getDocs(reportsRef)
    reportsSnap.docs.forEach((doc) => allReports.push(doc.data()))
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches
    
    // [FIX] Fetch and Inject Branch ID
    // We map over branches to fetch their specific reports
    const promises = filteredBranches.map(async (branch) => {
        const reportsRef = getPnlReportsCollectionRef(branch.branchId);
        const snapshot = await getDocs(reportsRef);
        
        // We map the docs immediately, injecting the known branchId
        return snapshot.docs.map(doc => {
            const data = doc.data();
            // Force the branchId from the loop context if missing in doc
            return { ...data, branchId: branch.branchId }; 
        });
    });

    const results = await Promise.all(promises);
    allReports.push(...results.flat());
  }

  if ($store.getInitFlag('premiumBranchFinanceInitialized')) {
    await generatePremiumBranchFinance(allReports)
    return
  }
  if (!currentUser) return

  const periodSelect = document.getElementById('premium-branch-finance-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-finance-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-finance-branch-b-select') as HTMLSelectElement

  // 1. Group by Period
  const reportsByPeriod = allReports.reduce((acc, report) => {
    const period = report.period
    if (!acc[period]) acc[period] = []
    acc[period].push(report)
    return acc
  }, {} as Record<string, any[]>)

  // 2. Filter: Only periods with >= 2 distinct branches
  const availablePeriods = Object.keys(reportsByPeriod)
    .filter((period) => {
      // Use IDs if available to count unique branches safely
      const branchesInPeriod = new Set(reportsByPeriod[period].map((r) => r.branchId || r.branchName))
      return branchesInPeriod.size >= 2
    })
    .sort()
    .reverse()

  if (availablePeriods.length === 0) {
    periodSelect.innerHTML = '<option>No periods with 2+ branches</option>'
    branchASelect.innerHTML = ''
    branchBSelect.innerHTML = ''
    branchASelect.disabled = true
    branchBSelect.disabled = true
    return
  }

  periodSelect.innerHTML = availablePeriods.map((p) => {
    const label = new Date(p + '-02').toLocaleString('default', { month: 'long', year: 'numeric' })
    return `<option value="${p}">${label}</option>`
  }).join('')

  const updateBranchSelectors = async () => {
    const selectedPeriod = periodSelect.value
    const reportsForPeriod = allReports.filter(r => r.period === selectedPeriod);
    
    let availableBranches: {id: string, name: string}[] = [];
    
    if (isDemoDataActive) {
        const names = [...new Set(reportsForPeriod.map(r => r.branchName))].sort();
        availableBranches = names.map(n => ({ id: n, name: n }));
    } else {
        // Real reports have branchId (injected above)
        availableBranches = reportsForPeriod.map(r => ({ id: r.branchId, name: r.branchName })).sort((a, b) => a.name.localeCompare(b.name));
    }

    if (availableBranches.length < 2) {
      branchASelect.innerHTML = '<option>Not enough data</option>'
      branchBSelect.innerHTML = '<option>Not enough data</option>'
      branchASelect.disabled = true; 
      branchBSelect.disabled = true;
      return
    }

    branchASelect.disabled = false; branchBSelect.disabled = false;
    const optionsHtml = availableBranches.map(b => `<option value="${b.id}">${b.name}</option>`).join('')
    branchASelect.innerHTML = optionsHtml; branchBSelect.innerHTML = optionsHtml

    branchASelect.value = availableBranches[0].id
    branchBSelect.value = availableBranches[1]?.id || availableBranches[0].id

    await generatePremiumBranchFinance(allReports) 
  }

  periodSelect.addEventListener('change', () => updateBranchSelectors())
  branchASelect.addEventListener('change', () => generatePremiumBranchFinance(allReports))
  branchBSelect.addEventListener('change', () => generatePremiumBranchFinance(allReports))

  setupTierGatedAI({ pageId, promptKey: 'cabang-keuangan', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumBranchFinanceInitialized', true)
  await updateBranchSelectors() 
}

// --- [FIXED] Branch Comparison Sales (ID-Based) ---

async function setupPremiumBranchSales() {
  const pageId = 'premium-placeholder-cabang-penjualan'
  if ($store.getInitFlag('premiumBranchSalesInitialized')) {
    await generatePremiumBranchSales()
    return
  }
  if (!currentUser) return

  const periodSelect = document.getElementById('premium-branch-sales-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-sales-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-sales-branch-b-select') as HTMLSelectElement

  // 1. Get Data & Context
  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()

  // 2. Build a Name->ID Map for accurate counting
  const branchNameMap = new Map<string, string>();
  userBranches.forEach(b => {
      branchNameMap.set(b.branchName, b.branchId);
      branchNameMap.set(b.branchId, b.branchId); 
  });

  let brandSalesData: SalesSummary[];

  if (isDemoDataActive) {
    brandSalesData = allSalesData;
  } else {
    // Filter data for current brand
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches
    const filteredBranchNames = new Set(filteredBranches.map(b => b.branchName || b.branchId))

    brandSalesData = allSalesData.filter(s => 
      s.branches.some(bName => filteredBranchNames.has(bName))
    )
  }

  // 3. Populate Periods (Strict >= 2 Branches Logic)
  const periodBranchCounts = brandSalesData.reduce((acc, s) => {
    const p = s.date.toISOString().slice(0, 7);
    if (!acc[p]) acc[p] = new Set();
    
    // Resolve to ID to ensure we count unique slots
    const rawName = s.branches[0];
    const id = isDemoDataActive ? rawName : (branchNameMap.get(rawName) || rawName);
    
    acc[p].add(id);
    return acc;
  }, {} as Record<string, Set<string>>);

  const sortedPeriods = Object.keys(periodBranchCounts)
    .filter(p => periodBranchCounts[p].size >= 2)
    .sort().reverse();

  if (sortedPeriods.length === 0) {
    periodSelect.innerHTML = '<option>No periods with 2+ branches</option>'
    branchASelect.disabled = true;
    branchBSelect.disabled = true;
    return
  }

  periodSelect.innerHTML = sortedPeriods.map((p) => {
    const [year, month] = p.split('-')
    const label = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
    return `<option value="${p}">${label}</option>`
  }).join('')

  // 4. Update Selectors
  const updateBranchSelectors = async () => {
    const selectedPeriod = periodSelect.value
    
    // Find branches active in this specific period
    const branchesForPeriod = [...new Set(
      brandSalesData
        .filter((s) => s.date.toISOString().startsWith(selectedPeriod))
        .flatMap((s) => s.branches)
    )].sort()

    const options = branchesForPeriod.map(name => {
        const id = isDemoDataActive ? name : (branchNameMap.get(name) || name);
        return { id, name };
    });

    if (options.length < 2) {
      branchASelect.innerHTML = `<option>Not enough data</option>`
      branchBSelect.innerHTML = `<option>Not enough data</option>`
      branchASelect.disabled = true;
      branchBSelect.disabled = true;
      return
    }

    branchASelect.disabled = false;
    branchBSelect.disabled = false;

    const optionsHtml = options.map((b) => `<option value="${b.id}">${b.name}</option>`).join('')
    branchASelect.innerHTML = optionsHtml; branchBSelect.innerHTML = optionsHtml
    
    branchASelect.value = options[0].id
    branchBSelect.value = options[1]?.id || options[0].id

    await generatePremiumBranchSales()
  }

  periodSelect.addEventListener('change', updateBranchSelectors)
  branchASelect.addEventListener('change', generatePremiumBranchSales)
  branchBSelect.addEventListener('change', generatePremiumBranchSales)

  // [FIX] Cleaned up AI setup (removed console.log)
  setupTierGatedAI({ pageId, promptKey: 'cabang-penjualan', promptDataFormatter: (viewData) => viewData.data })
  
  $store.setInitFlag('premiumBranchSalesInitialized', true)
  await updateBranchSelectors()
}

async function generatePremiumBranchSales() {
  if (!currentUser) return

  const periodSelect = document.getElementById('premium-branch-sales-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-sales-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-sales-branch-b-select') as HTMLSelectElement

  const selectedPeriod = periodSelect.value
  const branchA = branchASelect.value // ID
  const branchB = branchBSelect.value // ID

  if (!selectedPeriod || !branchA || !branchB) return

  // [FIX] Initialize storage for AI
  $store.clearViewData('premium-placeholder-cabang-penjualan')
  $store.setActiveViewData('premium-placeholder-cabang-penjualan', {}, { selectedPeriod, branchA, branchB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-cabang-penjualan')

  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config;
  
  if (isDemoDataActive) {
    const periodData = allSalesData.filter(s => s.date.toISOString().startsWith(selectedPeriod));
    // [FIX] Pass alsoStore
    generatePremiumBranchComparisonChart(periodData, branchA, branchB, 'premium-branch-sales-omzet-chart', 'totalOmzet', { alsoStore });
    generatePremiumBranchComparisonChart(periodData, branchA, branchB, 'premium-branch-sales-tc-apc-chart', 'apc', { alsoStore });
    populateBranchComparisonKPIs(periodData, branchA, branchB, { alsoStore });
  
  } else {
    const { userBranches } = $store.getAccountContext();
    const nameA = userBranches.find(b => b.branchId === branchA)?.branchName || 'Branch A';
    const nameB = userBranches.find(b => b.branchId === branchB)?.branchName || 'Branch B';

    const rawPeriodData = allSalesData.filter(s => 
        s.date.toISOString().startsWith(selectedPeriod) && 
        ((s as any).branchId === branchA || (s as any).branchId === branchB)
    );

    const mappedPeriodData = rawPeriodData.map(s => {
        const sId = (s as any).branchId;
        let targetName = '';
        if (sId === branchA) targetName = nameA;
        else if (sId === branchB) targetName = nameB;
        
        if (targetName && !s.branches.includes(targetName)) {
            return { ...s, branches: [...s.branches, targetName] };
        }
        return s;
    });

    // [FIX] Pass alsoStore
    generatePremiumBranchComparisonChart(mappedPeriodData, nameA, nameB, 'premium-branch-sales-omzet-chart', 'totalOmzet', { alsoStore });
    generatePremiumBranchComparisonChart(mappedPeriodData, nameA, nameB, 'premium-branch-sales-tc-apc-chart', 'apc', { alsoStore });
    populateBranchComparisonKPIs(mappedPeriodData, nameA, nameB, { alsoStore });
  }
}

// --- [FIXED] Branch Comparison Product (ID-Based) ---

async function setupPremiumBranchProductChannel() {
  const pageId = 'premium-placeholder-cabang-produk'
  if ($store.getInitFlag('premiumBranchProductInitialized')) {
    await generatePremiumBranchProductChannel()
    return
  }
  if (!currentUser) return

  const periodSelect = document.getElementById('premium-branch-product-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-product-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-product-branch-b-select') as HTMLSelectElement

  // 1. Get Data & Context
  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config;
  const { userBranches, selectedBrandId } = $store.getAccountContext()

  // 2. Name->ID Map
  const branchNameMap = new Map<string, string>();
  userBranches.forEach(b => {
      branchNameMap.set(b.branchName, b.branchId);
      branchNameMap.set(b.branchId, b.branchId); 
  });

  let brandSalesData: SalesSummary[];

  if (isDemoDataActive) {
    brandSalesData = allSalesData;
  } else {
    const filteredBranches = selectedBrandId
      ? userBranches.filter(b => b.brandId === selectedBrandId)
      : userBranches
    const filteredBranchNames = new Set(filteredBranches.map(b => b.branchName || b.branchId))

    brandSalesData = allSalesData.filter(s => 
      s.branches.some(bName => filteredBranchNames.has(bName))
    )
  }

  // 2. Populate Menu Select (Global)
  const menuSelectElement = document.getElementById('premium-branch-product-menu-select') as HTMLSelectElement
  const allMenuItems = [...new Set(brandSalesData.flatMap((s) => Object.keys(s.menuItemQuantities || {}).flatMap((cat) => Object.keys(s.menuItemQuantities[cat]))))].sort()
  menuSelectElement.innerHTML = allMenuItems.map((name) => `<option value="${name}">${name}</option>`).join('')

  const slimSelectInstance = new SlimSelect({
    select: '#premium-branch-product-menu-select',
    events: { afterChange: () => generatePremiumBranchProductChannel() },
  })
  $store.setUIComponent('premiumBranchProductMenuSelect', slimSelectInstance)

  // 3. Populate Periods (Strict >= 2 Branches)
  const periodBranchCounts = brandSalesData.reduce((acc, s) => {
    const p = s.date.toISOString().slice(0, 7);
    if (!acc[p]) acc[p] = new Set();
    const rawName = s.branches[0];
    const id = isDemoDataActive ? rawName : (branchNameMap.get(rawName) || rawName);
    acc[p].add(id);
    return acc;
  }, {} as Record<string, Set<string>>);

  const sortedPeriods = Object.keys(periodBranchCounts)
    .filter(p => periodBranchCounts[p].size >= 2)
    .sort().reverse();

  if (sortedPeriods.length === 0) {
    periodSelect.innerHTML = '<option>No periods with 2+ branches</option>'
    branchASelect.disabled = true;
    branchBSelect.disabled = true;
    return
  }

  periodSelect.innerHTML = sortedPeriods.map((p) => {
    const [year, month] = p.split('-')
    const label = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
    return `<option value="${p}">${label}</option>`
  }).join('')

  // 4. Update Branch Selectors
  const updateBranchSelectors = async () => {
    const selectedPeriod = periodSelect.value
    
    const branchesForPeriod = [...new Set(
      brandSalesData
        .filter((s) => s.date.toISOString().startsWith(selectedPeriod))
        .flatMap((s) => s.branches)
    )].sort()

    const options = branchesForPeriod.map(name => {
        const id = isDemoDataActive ? name : (branchNameMap.get(name) || name);
        return { id, name };
    });

    if (options.length < 2) {
      branchASelect.innerHTML = `<option>Not enough data</option>`
      branchBSelect.innerHTML = `<option>Not enough data</option>`
      branchASelect.disabled = true;
      branchBSelect.disabled = true;
      return
    }

    branchASelect.disabled = false;
    branchBSelect.disabled = false;

    const optionsHtml = options.map((b) => `<option value="${b.id}">${b.name}</option>`).join('')
    branchASelect.innerHTML = optionsHtml
    branchBSelect.innerHTML = optionsHtml

    const initialBranchA = options[0].id
    const initialBranchB = options[1]?.id || options[0].id
    branchASelect.value = initialBranchA
    branchBSelect.value = initialBranchB

    // Update SlimSelect defaults
    const periodData = brandSalesData.filter((s) => s.date.toISOString().startsWith(selectedPeriod))
    
    // Careful filtering by ID for defaults
    const branchAData = periodData.filter((s) => {
        const sId = isDemoDataActive ? s.branches[0] : (branchNameMap.get(s.branches[0]) || s.branches[0]);
        return sId === initialBranchA;
    });

    const menuQuantities = new Map<string, number>()
    branchAData.forEach((summary) => {
      if (!summary.menuItemQuantities) return
      for (const category in summary.menuItemQuantities) {
        if (summary.menuItemQuantities[category]) {
          for (const itemName in summary.menuItemQuantities[category]) {
            const quantity = summary.menuItemQuantities[category][itemName]
            menuQuantities.set(itemName, (menuQuantities.get(itemName) || 0) + quantity)
          }
        }
      }
    })

    const top3MenuNames = Array.from(menuQuantities.entries())
      .sort(([, qtyA], [, qtyB]) => qtyB - qtyA)
      .slice(0, 3)
      .map(([name]) => name)

    slimSelectInstance.setSelected(top3MenuNames, false)
    await generatePremiumBranchProductChannel()
  }

  periodSelect.addEventListener('change', updateBranchSelectors)
  branchASelect.addEventListener('change', generatePremiumBranchProductChannel)
  branchBSelect.addEventListener('change', generatePremiumBranchProductChannel)

  setupTierGatedAI({ pageId, promptKey: 'cabang-produk-channel', promptDataFormatter: (viewData) => viewData.data })
  $store.setInitFlag('premiumBranchProductInitialized', true)
  await updateBranchSelectors()
}

async function generatePremiumBranchProductChannel() {
  if (!currentUser) return
  const periodSelect = document.getElementById('premium-branch-product-period-select') as HTMLSelectElement
  const branchASelect = document.getElementById('premium-branch-product-branch-a-select') as HTMLSelectElement
  const branchBSelect = document.getElementById('premium-branch-product-branch-b-select') as HTMLSelectElement
  const slimSelectInstance = $store.getUIComponent('premiumBranchProductMenuSelect')

  const selectedPeriod = periodSelect.value
  const branchA = branchASelect.value
  const branchB = branchBSelect.value

  if (!selectedPeriod || !branchA || !branchB) return

  $store.clearViewData('premium-placeholder-cabang-produk')
  $store.setActiveViewData('premium-placeholder-cabang-produk', {}, { selectedPeriod, branchA, branchB })
  const alsoStore = createAlsoStoreFn($store, 'premium-placeholder-cabang-produk')

  const allSalesData = $store.getAllSalesData()
  const { isDemoDataActive } = $store.getAnalysisState().config;
  
  // 1. Get Base Data (Filtered by Brand/Period)
  let periodData = allSalesData.filter(s => s.date.toISOString().startsWith(selectedPeriod));
  if (!isDemoDataActive) {
      const { selectedBrandId, userBranches } = $store.getAccountContext();
      if (selectedBrandId) {
          const branchIds = new Set(userBranches.filter(b => b.brandId === selectedBrandId).map(b => b.branchId));
          periodData = periodData.filter(s => branchIds.has((s as any).branchId));
      }
  }

  // 2. Filter by Branch ID
  let branchAData: SalesSummary[], branchBData: SalesSummary[];
  
  if (isDemoDataActive) {
      branchAData = periodData.filter(s => s.branches.includes(branchA));
      branchBData = periodData.filter(s => s.branches.includes(branchB));
  } else {
      branchAData = periodData.filter(s => (s as any).branchId === branchA);
      branchBData = periodData.filter(s => (s as any).branchId === branchB);
  }

  // 3. Resolve Names for Charts
  const { userBranches } = $store.getAccountContext();
  const nameA = isDemoDataActive ? branchA : (userBranches.find(b => b.branchId === branchA)?.branchName || branchA);
  const nameB = isDemoDataActive ? branchB : (userBranches.find(b => b.branchId === branchB)?.branchName || branchB);

  // 4. Populate
  if (slimSelectInstance) {
    drawBranchMenuTrendChart(periodData, nameA, nameB, 'premium-branch-product-menu-trend-chart', slimSelectInstance, { alsoStore })
  }
  generateBranchChannelComparisonChart(periodData, nameA, nameB, 'premium-branch-product-channel-chart', { alsoStore })
  generateCategoryComparisonChart(branchAData, branchBData, 'premium-branch-product-category-chart', { alsoStore })
  generateTop10ComparisonTable(branchAData, branchBData, 'premium-branch-product-top-quantity-container', 'quantity', { alsoStore })
  generateTop10ComparisonTable(branchAData, branchBData, 'premium-branch-product-top-revenue-container', 'revenue', { alsoStore })
}


function resetAllAnalysisViewFlags() {
  console.log('[Orchestrator] Resetting all analysis view init flags...')
  $store.setInitFlag('premiumGeneralSalesInitialized', false)
  $store.setInitFlag('premiumProductChannelInitialized', false)
  $store.setInitFlag('premiumTimeFinanceInitialized', false)
  $store.setInitFlag('premiumTimeSalesInitialized', false)
  $store.setInitFlag('premiumTimeProductInitialized', false)
  $store.setInitFlag('premiumBranchFinanceInitialized', false)
  $store.setInitFlag('premiumBranchSalesInitialized', false)
  $store.setInitFlag('premiumBranchProductInitialized', false)
  // Add any other view flags here if they exist
}

// In: apps/web/src/analysis/premium/orchestrator.ts
// ADD these new definitions:

// --- Developer Notes Configuration ---
const DEVELOPER_NOTES = [
  {
    version: 'v1.0.0',
    date: '2025-11-19',
    title: 'Official Launch: AI Analytics Suite',
    items: [
      '<strong>AI-Powered P&L Digitization:</strong> Upload raw Excel reports and let our AI automatically categorize revenue and expenses. Includes a new editor to add, remove, or modify items before saving.',
      '<strong>Multi-Brand Ecosystem:</strong> Full support for managing multiple brands. You can now group branches under specific brands and filter your entire dashboard by Brand.',
      '<strong>Advanced Branch Management:</strong> Rename branches and move them between brands using the new "Assign to Brand" feature in the My Branches menu.',
      '<strong>Executive PDF Export:</strong> Generate professional, single-page PDF reports with auto-formatted layouts, side-by-side charts, and AI-generated insights.',
      '<strong>Real-Time Data Engine:</strong> Enhanced data processing ensures your Sales and P&L uploads update the dashboard instantly without cache delays.',
    ]
  }
];

/**
 * Renders the Welcome View with the Welcome Message and Developer Notes.
 */
function renderWelcomeView() {
  const container = document.getElementById('premium-welcome');
  if (!container) return;

  const userEmail = currentUser?.email || 'User';
  
  // Build the Notes HTML
  const notesHtml = DEVELOPER_NOTES.map(note => `
    <div class="border-l-4 border-indigo-500 pl-4 py-1">
      <div class="flex justify-between items-center mb-1">
        <h4 class="font-bold text-gray-800">${note.title}</h4>
        <span class="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">${note.version}</span>
      </div>
      <p class="text-xs text-gray-500 mb-2">${note.date}</p>
      <ul class="list-disc pl-4 text-sm text-gray-600 space-y-1">
        ${note.items.map(item => `<li>${item}</li>`).join('')}
      </ul>
    </div>
  `).join('');

  container.innerHTML = `
    <div class="max-w-4xl mx-auto space-y-8 pt-10">
      
      <div class="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center relative overflow-hidden">
        <div class="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-purple-600"></div>
        
        <h1 class="text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
          Welcome to Finalytics
        </h1>
        <p class="text-lg text-gray-500 mb-8">
          Logged in as <span class="font-medium text-indigo-600">${userEmail}</span>
        </p>

        <div class="flex justify-center">
          <button id="welcome-go-dashboard-btn" class="group relative inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-indigo-600 rounded-full shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 ease-in-out transform hover:-translate-y-1">
            <span>Go to Dashboard</span>
            <svg class="ml-2 -mr-1 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      <div class="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 class="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <svg class="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-4 8h4"></path></svg>
          Developer Notes & Updates
        </h3>
        <div class="space-y-6 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
          ${notesHtml}
        </div>
      </div>

    </div>
  `;

  // Attach Listener
  document.getElementById('welcome-go-dashboard-btn')?.addEventListener('click', (e) => {
    e.preventDefault();
    // Assuming showDashboard is defined in your scope or imported
    // Since showDashboard is defined inside setupPremiumAnalysisView closure in your current code,
    // we might need to expose it or trigger the click on the dashboard nav button.
    
    const dashboardBtn = document.getElementById('premium-goto-dashboard-btn');
    if (dashboardBtn) dashboardBtn.click();
  });
}

// --- CUSTOM REPORT BUILDER LOGIC ---

interface ReportBlock {
  id: string;
  type: string;
  branchId: string;
  branchName: string; 
  period: string;
  aiAnalysis?: string;
  isAiLoading?: boolean;
  textContent?: string;
  subChartTypes?: string[];
  // [NEW] Layout Properties
  width: 'half' | 'full'; 
}

let reportBlocks: ReportBlock[] = [];

// --- TEMPLATE CONFIGURATION (Updated for Separated Charts) ---
// --- TEMPLATE CONFIGURATION ---
const REPORT_TEMPLATES: Record<string, { desc: string, blocks: string[] }> = {
    'sales_focus': {
        desc: "Focus on revenue, transactions, and product performance.",
        blocks: [
            'omzet_trend',      
            'tc_apc_trend',     
            'top_10_rev',       
            'channel_sales',    
            'category_sales'    
        ]
    },
    'financial_health': {
        desc: "Deep dive into P&L, margins, and cost ratios.",
        blocks: ['pnl_overview', 'net_profit_margin', 'cogs_ratio', 'operational_ratio']
    },
    'operational': {
        desc: "Analyze peak times, busy days, and item volume.",
        blocks: ['heatmap_daily', 'heatmap_hourly', 'top_10_qty']
    },
    'executive_summary': {
        desc: "A complete overview including Sales, P&L, and Operations.",
        blocks: [
            'text_block', 
            'omzet_trend', 
            'pnl_overview', 
            'page_break', 
            'top_products', 
            'heatmap_daily',
            'net_profit_margin'
        ]
    }
};

// [REPLACE in apps/web/src/analysis/premium/orchestrator.ts]

async function setupFinalyticsRecommendation() {
  const container = document.getElementById('premium-recommendation-content');
  if (!container) return;

  if (container.hasAttribute('data-initialized')) {
      // return; 
  }
  container.setAttribute('data-initialized', 'true');

  // --- Elements ---
  const addBtn = document.getElementById('rec-add-block-btn');
  const templateBtn = document.getElementById('rec-template-btn'); 
  const exportBtn = document.getElementById('rec-export-pdf-btn');
  
  // Modals
  const modal = document.getElementById('rec-add-modal');
  const templateModal = document.getElementById('rec-template-modal');
  
  // Add Block Modal Elements
  const closeModalBtn = document.getElementById('rec-modal-close');
  const confirmAddBtn = document.getElementById('rec-modal-add-btn');
  const modalBranchSelect = document.getElementById('rec-modal-branch') as HTMLSelectElement;
  const modalPeriodSelect = document.getElementById('rec-modal-period') as HTMLSelectElement;
  const modalTypeSelect = document.getElementById('rec-modal-type') as HTMLSelectElement;
  const modalSubchartsGroup = document.getElementById('rec-modal-subcharts-group');
  const modalSubchartsSelect = document.getElementById('rec-modal-subcharts') as HTMLSelectElement;
  const contextGroup = document.getElementById('rec-modal-context-group');

  // Template Modal Elements
  const templateApplyBtn = document.getElementById('rec-template-apply-btn');
  const templateSelect = document.getElementById('rec-template-select') as HTMLSelectElement;
  const templateBranchSelect = document.getElementById('rec-template-branch') as HTMLSelectElement;
  const templatePeriodSelect = document.getElementById('rec-template-period') as HTMLSelectElement;
  const templateDesc = document.getElementById('rec-template-desc');

  // --- Helper Functions ---
  const updatePeriodForBranch = async (branchId: string, periodSelect: HTMLSelectElement) => {
      const allSalesData = $store.getAllSalesData();
      const { isDemoDataActive } = $store.getAnalysisState().config;
      let branchData = allSalesData;
      if (!isDemoDataActive) branchData = allSalesData.filter(s => (s as any).branchId === branchId);
      const periods = [...new Set(branchData.map(s => s.date.toISOString().slice(0, 7)))].sort().reverse();
      if (periods.length === 0) periodSelect.innerHTML = '<option disabled>No data found</option>';
      else periodSelect.innerHTML = periods.map(p => {
          const d = new Date(p + '-02');
          return `<option value="${p}">${d.toLocaleString('default', { month: 'long', year: 'numeric' })}</option>`;
      }).join('');
  };

  const populateContextSelectors = async (branchSelect: HTMLSelectElement, periodSelect: HTMLSelectElement) => {
    const { userBranches, selectedBrandId } = $store.getAccountContext();
    const { isDemoDataActive } = $store.getAnalysisState().config;
    let branches = userBranches;
    if (!isDemoDataActive && selectedBrandId) branches = userBranches.filter(b => b.brandId === selectedBrandId);
    
    const options = branches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
    branchSelect.innerHTML = options;
    
    if (branches.length > 0) {
        branchSelect.value = branches[0].branchId;
        await updatePeriodForBranch(branches[0].branchId, periodSelect);
    }

    const newSelect = branchSelect.cloneNode(true) as HTMLSelectElement;
    branchSelect.parentNode?.replaceChild(newSelect, branchSelect);
    newSelect.addEventListener('change', async () => await updatePeriodForBranch(newSelect.value, periodSelect));
  };

  // --- Listeners: Add Block ---
  addBtn?.addEventListener('click', () => {
      populateContextSelectors(modalBranchSelect, modalPeriodSelect);
      modalTypeSelect.value = 'omzet_trend';
      modalTypeSelect.dispatchEvent(new Event('change'));
      modal?.classList.remove('hidden');
  });

  closeModalBtn?.addEventListener('click', () => modal?.classList.add('hidden'));

  modalTypeSelect?.addEventListener('change', () => {
      const val = modalTypeSelect.value;
      if (val === 'page_break') {
          modalBranchSelect.disabled = true;
          modalPeriodSelect.disabled = true;
          contextGroup?.classList.add('opacity-50', 'pointer-events-none');
      } else {
          modalBranchSelect.disabled = false;
          modalPeriodSelect.disabled = false;
          contextGroup?.classList.remove('opacity-50', 'pointer-events-none');
      }
      if (val === 'group_block') modalSubchartsGroup?.classList.remove('hidden');
      else modalSubchartsGroup?.classList.add('hidden');
  });

  confirmAddBtn?.addEventListener('click', () => {
      let subCharts: string[] = [];
      if (modalTypeSelect.value === 'group_block') {
          subCharts = Array.from(modalSubchartsSelect.selectedOptions).map(opt => opt.value);
          if (subCharts.length < 2) { alert("Select at least 2 charts."); return; }
          if (subCharts.length > 4) { alert("Max 4 charts."); return; }
      }

      const type = modalTypeSelect.value;
      const defaultWidth = (type === 'text_block' || type === 'group_block' || type === 'page_break') ? 'full' : 'half';

      const block: ReportBlock = {
          id: `block_${Date.now()}`,
          type: type,
          branchId: modalBranchSelect.disabled ? '' : modalBranchSelect.value,
          branchName: modalBranchSelect.disabled ? '' : modalBranchSelect.options[modalBranchSelect.selectedIndex].text,
          period: modalBranchSelect.disabled ? '' : modalPeriodSelect.value,
          textContent: '', 
          isAiLoading: false,
          subChartTypes: subCharts,
          width: defaultWidth
      };
      
      reportBlocks.push(block);
      renderReportCanvas();
      modal?.classList.add('hidden');
  });

  // --- Listeners: Template ---
  if (templateBtn) {
      templateBtn.addEventListener('click', async () => {
          if (!templateModal) return;
          await populateContextSelectors(templateBranchSelect, templatePeriodSelect);
          templateModal.classList.remove('hidden');
      });
  }

  const closeTemplate = () => templateModal?.classList.add('hidden');
  document.getElementById('rec-template-close')?.addEventListener('click', closeTemplate);

  templateSelect?.addEventListener('change', () => {
      const t = REPORT_TEMPLATES[templateSelect.value];
      if (t && templateDesc) templateDesc.textContent = t.desc;
  });

  templateApplyBtn?.addEventListener('click', () => {
      const selectedTemplateKey = templateSelect.value;
      const branchId = templateBranchSelect.value;
      const branchName = templateBranchSelect.options[templateBranchSelect.selectedIndex]?.text || '';
      const period = templatePeriodSelect.value;

      if (!branchId || !period) { alert("Please select a branch and period."); return; }

      const template = REPORT_TEMPLATES[selectedTemplateKey];
      if (!template) return;

      if (confirm("This will clear your current report and load the template. Continue?")) {
          reportBlocks = []; 
          template.blocks.forEach(type => {
              const isFull = ['text_block', 'page_break', 'group_block'].includes(type);
              reportBlocks.push({
                  id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                  type: type,
                  branchId,
                  branchName,
                  period,
                  textContent: type === 'text_block' ? `## Executive Summary\nAnalysis for ${branchName} - ${period}.\n\n(Click to edit this note)` : '',
                  isAiLoading: false,
                  width: isFull ? 'full' : 'half'
              });
          });
          renderReportCanvas();
          closeTemplate();
      }
  });

  // --- [UPDATED] DIRECT PDF DOWNLOAD LOGIC ---
  
  exportBtn?.addEventListener('click', async () => {
      if (reportBlocks.length === 0) {
          alert("Please add at least one block to the report.");
          return;
      }
      
      showLoading({ message: 'Generating PDF Report...' });

      try {
          // Generate PDF Logic (Defined in orchestrator.ts)
          const pdfDoc = await generatePdfDocument();
          
          // Direct Download
          pdfDoc.save('Finalytics_Custom_Report.pdf');
          
      } catch (e) {
          console.error("PDF Generation Error:", e);
          alert('Failed to generate PDF. Please ensure all data is loaded.');
      } finally {
          hideLoading();
      }
  });
}

// --- Helpers ---
// --- [FIX] BLOCK MANAGEMENT FUNCTIONS ---




// --- Helpers ---
function getBlockData(block: ReportBlock) {
    const allSalesData = $store.getAllSalesData();
    const { isDemoDataActive } = $store.getAnalysisState().config;
    return allSalesData.filter(s => {
        const p = s.date.toISOString().slice(0, 7);
        if (p !== block.period) return false;
        if (isDemoDataActive) return s.branches.includes(block.branchId);
        return (s as any).branchId === block.branchId;
    });
}

async function getPnlDataForBlock(block: ReportBlock) {
    const { isDemoDataActive } = $store.getAnalysisState().config;
    let reports: any[] = [];
    if (isDemoDataActive) {
        const reportsRef = getPnlReportsCollectionRef('demo');
        const snap = await getDocs(reportsRef);
        reports = snap.docs.map(d => d.data());
        return reports.find(r => r.period === block.period && r.branchName === block.branchName);
    } else {
        const reportsRef = getPnlReportsCollectionRef(block.branchId);
        const snap = await getDocs(reportsRef);
        reports = snap.docs.map(d => d.data());
        return reports.find(r => r.period === block.period);
    }
}




function getChartTitle(type: string) {
    const map: Record<string, string> = {
        'group_block': 'Comparative Analysis',
        // Sales
        'omzet_trend': 'Omzet Trend (Revenue)',
        'tc_apc_trend': 'Transaction Count & Avg Check',
        'top_products': 'Top 5 Products (Chart)',
        'channel_sales': 'Sales by Channel',
        'category_sales': 'Category Breakdown',
        // Advanced
        'heatmap_hourly': 'Hourly Sales Intensity',
        'heatmap_daily': 'Daily Sales Intensity',
        'top_10_qty': 'Top 10 Products (By Quantity)',
        'top_10_rev': 'Top 10 Products (By Revenue)',
        // Financial
        'pnl_overview': 'P&L Overview',
        'net_profit_margin': 'Net Profit Margin Trend',
        'cogs_ratio': 'Cost of Goods Sold (COGS) %',
        'operational_ratio': 'Operational Expenses (OPEX) %',
        // Structure
        'text_block': 'Custom Note',
        'page_break': 'Page Break',
        'ai_text_block': 'AI Analysis'
    };
    return map[type] || 'Chart';
}


function renderTop10Table(summaries: any[], containerId: string, metric: 'quantity' | 'revenue') {
    const container = document.getElementById(containerId);
    if (!container) return;

    const itemMap = new Map<string, { category: string, value: number }>();

    summaries.forEach(s => {
        const source = metric === 'quantity' ? (s.menuItemQuantities || {}) : (s.menuItemRevenues || {});
        Object.entries(source).forEach(([category, items]: [string, any]) => {
            if (!items) return;
            Object.entries(items).forEach(([name, val]: [string, any]) => {
                const numVal = Number(val) || 0;
                if (numVal > 0) {
                    if (!itemMap.has(name)) {
                        itemMap.set(name, { category, value: 0 });
                    }
                    itemMap.get(name)!.value += numVal;
                }
            });
        });
    });

    const top10 = Array.from(itemMap.entries())
        .sort((a, b) => b[1].value - a[1].value)
        .slice(0, 10); // [FIX] Strictly 10 for Tables

    if (top10.length === 0) {
        container.innerHTML = '<p class="text-gray-400 text-center py-10 text-sm">No data found.</p>';
        return;
    }

    let html = `
    <div class="overflow-x-auto h-full">
        <table class="min-w-full divide-y divide-gray-200 text-xs">
            <thead class="bg-gray-50">
                <tr>
                    <th class="px-3 py-2 text-left font-medium text-gray-500">#</th>
                    <th class="px-3 py-2 text-left font-medium text-gray-500">Menu Name</th>
                    <th class="px-3 py-2 text-left font-medium text-gray-500">Category</th>
                    <th class="px-3 py-2 text-right font-medium text-gray-500">${metric === 'quantity' ? 'Qty' : 'Rev'}</th>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
    `;

    top10.forEach(([name, data], index) => {
        const valDisplay = metric === 'quantity' ? formatNumber(data.value) : formatCurrency(data.value);
        html += `
            <tr>
                <td class="px-3 py-2 text-gray-500">${index + 1}</td>
                <td class="px-3 py-2 font-medium text-gray-900 truncate max-w-[140px]" title="${name}">${name}</td>
                <td class="px-3 py-2 text-gray-500 truncate max-w-[100px]">${data.category}</td>
                <td class="px-3 py-2 text-right font-mono text-gray-900">${valDisplay}</td>
            </tr>
        `;
    });

    html += `</tbody></table></div>`;
    container.innerHTML = html;
}


// =========================================================
// === CUSTOM REPORT BUILDER (FINAL: PDF ALIGNMENT FIXED) ===
// =========================================================

import { getAiOptions, buildAiPrompt } from './ai-prompts';

// --- 1. GLOBAL HELPERS ---

function loadMontserratFont() {
    if (!document.getElementById('font-montserrat')) {
        const link = document.createElement('link');
        link.id = 'font-montserrat';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap';
        document.head.appendChild(link);
    }
}

function formatBigCurrency(val: number) {
    if (val >= 1_000_000_000) return `Rp ${(val / 1_000_000_000).toFixed(2)} M`;
    if (val >= 1_000_000) return `Rp ${(val / 1_000_000).toFixed(1)} jt`;
    return formatCurrency(val);
}

function getBlockKPIs(type: string, data: any[]): { label: string, value: string, sub?: string }[] {
    if (!data) return [];

    // --- SALES METRICS ---
    if (type === 'omzet_trend') {
        const total = data.reduce((s, d) => s + d.totalOmzet, 0);
        const bestDay = data.reduce((p, c) => (p.totalOmzet > c.totalOmzet ? p : c), data[0] || { totalOmzet: 0, date: new Date() });
        const dateObj = new Date(bestDay.date);
        
        // Date Format: 6 September 2025
        const dateStr = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        
        return [
            { label: 'Total Revenue', value: formatBigCurrency(total) },
            // [UPDATED]: Date is main Value, Amount is Subtext (Bottom line, smaller font)
            { label: 'Peak Day', value: dateStr, sub: formatBigCurrency(bestDay.totalOmzet) }
        ];
    }

    // ... (Keep the rest of the function exactly the same) ...
    if (type === 'tc_apc_trend') {
        const tc = data.reduce((s, d) => s + d.totalTransactions, 0);
        const apc = tc > 0 ? data.reduce((s, d) => s + d.totalOmzet, 0) / tc : 0;
        return [
            { label: 'Total Visits', value: formatNumber(tc) },
            { label: 'Avg Ticket', value: shortenCurrency(apc) }
        ];
    }
    if (['top_products', 'top_10_qty', 'top_10_rev'].includes(type)) {
        const map = new Map<string, number>();
        data.forEach(s => {
            const source = (type === 'top_10_rev' || type === 'top_products') ? s.menuItemRevenues : s.menuItemQuantities;
            if (source) Object.values(source).forEach(c => Object.entries(c).forEach(([k, v]) => map.set(k, (map.get(k) || 0) + Number(v))));
        });
        const top = Array.from(map.entries()).sort((a, b) => b[1] - a[1])[0];
        return top ? [{ label: 'Top Performer', value: top[0], sub: `${type.includes('rev') ? formatBigCurrency(top[1]) : formatNumber(top[1])}` }] : [];
    }
    if (type === 'channel_sales') {
        const map = new Map<string, number>();
        data.forEach(s => {
            if(s.revenueByVisitPurpose) Object.entries(s.revenueByVisitPurpose).forEach(([k, v]) => map.set(k, (map.get(k) || 0) + Number(v)));
        });
        const top = Array.from(map.entries()).sort((a, b) => b[1] - a[1])[0];
        return top ? [{ label: 'Dominant Channel', value: top[0], sub: `${((top[1] / data.reduce((s,d)=>s+d.totalOmzet,0))*100).toFixed(0)}% Share` }] : [];
    }

    // --- P&L METRICS ---
    if (type === 'pnl_overview') {
        const pnl = Array.isArray(data) ? (data[0] || {}) : data;
        const rev = pnl.totalRevenue || pnl.revenue || 0;
        const net = pnl.netProfit || pnl.net_profit || 0;
        return [
            { label: 'Revenue', value: formatBigCurrency(rev) },
            { label: 'Net Profit', value: formatBigCurrency(net), sub: rev > 0 ? `${((net/rev)*100).toFixed(1)}% Margin` : '0%' }
        ];
    }

    return [];
}

function moveBlock(index: number, direction: 'up' | 'down') {
    if (direction === 'up' && index > 0) {
        [reportBlocks[index], reportBlocks[index - 1]] = [reportBlocks[index - 1], reportBlocks[index]];
    } else if (direction === 'down' && index < reportBlocks.length - 1) {
        [reportBlocks[index], reportBlocks[index + 1]] = [reportBlocks[index + 1], reportBlocks[index]];
    }
    renderReportCanvas();
}
(window as any).moveBlock = moveBlock;


// --- 2. LAYOUT ENGINE (Updated for Text Centering) ---

async function renderReportCanvas() {
    loadMontserratFont(); 

    const canvas = document.getElementById('rec-builder-canvas');
    if (!canvas) return;

    if (reportBlocks.length === 0) {
        canvas.className = "flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-50";
        canvas.innerHTML = `<div class="text-gray-400 text-center" style="font-family: 'Montserrat', sans-serif;"><p class="text-xl font-bold">Your report is empty.</p><p class="text-sm">Add a chart to start.</p></div>`;
        return;
    }

    canvas.className = "flex flex-col gap-12 p-12 bg-gray-200 min-h-screen items-center overflow-auto";
    canvas.innerHTML = '';

    // [FIX] Changed to for...of loop to handle async/await for exclusions
    for (const [index, block] of reportBlocks.entries()) {
        const page = document.createElement('div');
        page.id = `card_${block.id}`;
        page.style.fontFamily = "'Montserrat', sans-serif";
        page.className = "report-page bg-white w-[794px] aspect-[1/1.414] shadow-2xl relative flex flex-col overflow-hidden group transition-all"; 

        const controlsHtml = `
            <div class="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 block-controls print:hidden">
                <button class="bg-gray-100 p-2 rounded-full hover:bg-indigo-100 text-gray-500 hover:text-indigo-600 shadow-sm" onclick="moveBlock(${index}, 'up')">&uarr;</button>
                <button class="bg-gray-100 p-2 rounded-full hover:bg-indigo-100 text-gray-500 hover:text-indigo-600 shadow-sm" onclick="moveBlock(${index}, 'down')">&darr;</button>
                <button class="delete-block-btn bg-white border border-red-100 p-2 rounded-full text-red-400 hover:bg-red-50 hover:text-red-600 shadow-sm" data-idx="${index}">&times;</button>
            </div>
        `;

        const contentId = `content_${block.id}`;
        
        // 1. Get Raw Data
        let salesData = getBlockData(block); 
        
        // 2. [FIX] Apply Exclusions for KPIs (The missing link!)
        if (block.branchId) {
            try {
                const exclusions = await getBranchExclusions(block.branchId);
                salesData = applyExclusionsToData(salesData, exclusions);
            } catch (e) {
                console.error("Error fetching exclusions for KPI:", e);
            }
        }

        // 3. Calculate KPIs with Filtered Data
        const kpis = getBlockKPIs(block.type, salesData);
        
        const isTable = ['top_10_qty', 'top_10_rev'].includes(block.type);
        const chartHeightClass = isTable ? 'h-[50%]' : 'h-[25%]';
        const aiHeightClass = isTable ? 'h-[20%]' : 'h-[45%]'; 

        // KPI Column
        const kpiHtml = kpis.map(k => `
            <div class="flex flex-col border-l-4 border-indigo-500 pl-6 min-w-[150px]">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 whitespace-nowrap text-left">${k.label}</p>
                <p class="text-3xl font-black text-indigo-900 tracking-tight leading-none break-words text-left">${k.value}</p>
                ${k.sub ? `<p class="text-xs font-bold text-gray-500 mt-2 text-left">${k.sub}</p>` : ''}
            </div>
        `).join('');

        // AI Section
        let aiHtml = '';
        if (block.isAiLoading) {
            aiHtml = `<div class="space-y-4 animate-pulse w-full"><div class="h-4 bg-gray-200 rounded w-full"></div><div class="h-4 bg-gray-200 rounded w-full"></div><div class="h-4 bg-gray-200 rounded w-full"></div></div>`;
        } else if (block.aiAnalysis) {
             const customStyle = `
                <style>
                    .ai-content h3 {
                        font-family: 'Montserrat', sans-serif !important;
                        font-size: 16px !important;
                        font-weight: 900 !important;
                        color: #312e81 !important;
                        margin-top: 16px !important;
                        margin-bottom: 4px !important;
                        text-transform: uppercase !important;
                        letter-spacing: 0.05em !important;
                    }
                    .ai-content p {
                        font-family: 'Montserrat', sans-serif !important;
                        font-size: 12px !important;
                        font-weight: 500 !important;
                        line-height: 1.6 !important;
                        color: #4b5563 !important;
                        margin-bottom: 12px !important;
                        text-align: justify !important;
                    }
                    .ai-content strong {
                        color: #111827 !important;
                        font-weight: 800 !important;
                    }
                    .ai-content ul {
                        list-style-type: disc !important;
                        padding-left: 1.5em !important;
                        margin-bottom: 12px !important;
                    }
                    .ai-content li {
                        font-size: 12px !important;
                        color: #4b5563 !important;
                        margin-bottom: 4px !important;
                    }
                </style>
            `;
            aiHtml = `
                ${customStyle}
                <div class="ai-content h-full overflow-y-auto pr-2 w-full">
                    ${marked.parse(block.aiAnalysis)}
                </div>`;
        } else {
            const options = getAiOptions(block.type);
            const buttonsHtml = options.map(opt => `
                <button class="generate-ai-btn flex flex-col items-center justify-center gap-2 bg-white border border-gray-200 hover:border-indigo-300 hover:shadow-md hover:bg-indigo-50/50 p-4 rounded-xl transition-all group w-full h-full" data-idx="${index}" data-intent="${opt.intent}">
                    <span class="text-2xl group-hover:scale-110 transition-transform">${opt.icon}</span>
                    <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center group-hover:text-indigo-600 w-full">${opt.label}</span>
                </button>
            `).join('');

            aiHtml = `
                <div class="flex flex-col h-full w-full items-center justify-center">
                    <p class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 text-center w-full block">Select Strategic Lens:</p>
                    <div class="grid grid-cols-3 gap-4 flex-1 w-full">
                        ${buttonsHtml}
                    </div>
                </div>`;
        }

        page.innerHTML = `
            ${controlsHtml}
            
            <div class="h-[15%] px-12 py-8 flex items-center justify-between border-b border-gray-100 w-full">
                <div class="flex-1 flex flex-col justify-center">
                    <div class="flex items-center gap-3 mb-2">
                        <span class="inline-flex items-center justify-center h-6 px-3 pb-[1px] text-[10px] font-black bg-indigo-50 text-indigo-600 rounded uppercase tracking-wide whitespace-nowrap leading-none">
                            ${block.branchName}
                        </span>
                        <span class="text-[10px] text-gray-400">&bull;</span>
                        <span class="text-[10px] text-gray-500 font-bold uppercase tracking-wide whitespace-nowrap">${block.period}</span>
                    </div>
                    <h2 class="text-4xl font-black text-gray-900 tracking-tight leading-none">${getChartTitle(block.type)}</h2>
                </div>
                <div class="text-right flex-1 flex justify-end items-center">
                    <span class="text-[10px] font-bold text-gray-300 uppercase tracking-widest text-right block w-full">Finalytics Intelligence</span>
                </div>
            </div>

            <div class="${chartHeightClass} px-12 py-6 relative overflow-hidden flex items-center justify-center bg-white transition-all w-full">
                <div id="${contentId}" class="w-full h-full relative"></div>
            </div>

            <div class="h-[15%] px-12 py-6 bg-gray-50 border-t border-gray-100 flex items-center justify-start gap-16 overflow-x-auto w-full">
                ${kpiHtml}
            </div>

            <div class="${aiHeightClass} px-12 py-8 bg-white border-t border-gray-100 relative flex flex-col transition-all w-full">
                 <div class="absolute top-0 left-12 -translate-y-1/2 bg-indigo-600 text-white h-8 px-6 rounded-full shadow-xl z-10 flex items-center justify-center">
                    <span class="text-[10px] font-black uppercase tracking-widest leading-none pb-[1px]">Strategic Findings</span>
                </div>
                <div class="flex-1 overflow-hidden pt-4 w-full">
                    ${aiHtml}
                </div>
            </div>
        `;

        canvas.appendChild(page);
        renderBlockContent(block, contentId);
    }

    attachEventListeners();
}

function attachEventListeners() {
    document.querySelectorAll('.delete-block-btn').forEach(btn => btn.addEventListener('click', (e) => {
        const idx = parseInt((e.target as HTMLElement).closest('button')?.dataset.idx || '0');
        reportBlocks.splice(idx, 1);
        renderReportCanvas();
    }));
    document.querySelectorAll('.generate-ai-btn').forEach(btn => btn.addEventListener('click', (e) => {
        const btnEl = (e.target as HTMLElement).closest('button');
        const idx = parseInt(btnEl?.dataset.idx || '0');
        const intent = btnEl?.dataset.intent || 'general_review';
        generateBlockInsight(idx, intent);
    }));
}


// --- 3. CHART RENDERER ---

async function renderBlockContent(block: ReportBlock, containerId: string) {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Check if this is a Sales/Operations block that needs filtered data
    if (['omzet_trend', 'tc_apc_trend', 'top_products', 'channel_sales', 'category_sales', 
         'heatmap_hourly', 'heatmap_daily', 'top_10_qty', 'top_10_rev'].includes(block.type)) {
        
        // 1. Get Raw Data
        let data = getBlockData(block);

        // 2. [FIX] Fetch and Apply Exclusions
        // This ensures hidden items (like "BAGI PLASTIK") are removed before rendering tables/charts
        if (block.branchId) {
            try {
                // Use the helper we created earlier
                const exclusions = await getBranchExclusions(block.branchId);
                data = applyExclusionsToData(data, exclusions);
            } catch (err) {
                console.error("Error applying exclusions to block:", err);
            }
        }

        if (data.length === 0) { 
            container.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 italic text-2xl font-medium">No data available.</div>'; 
            return; 
        }

        if (['omzet_trend', 'tc_apc_trend', 'top_products', 'channel_sales', 'category_sales'].includes(block.type)) {
            const canvas = document.createElement('canvas');
            canvas.id = `canvas_${block.id}`;
            container.appendChild(canvas);

            if (block.type === 'omzet_trend') generateOmzetHarianChartFromSummaries(data, canvas.id);
            else if (block.type === 'tc_apc_trend') generateTcApcHarianChartFromSummaries(data, canvas.id);
            else if (block.type === 'top_products') generateTop5MenuChart(data, canvas.id);
            else if (block.type === 'channel_sales') generateChannelDonutChart(data, canvas.id);
            else if (block.type === 'category_sales') generateOrderByCategoryDonutChart(data, canvas.id);
        }
        else if (block.type === 'heatmap_hourly') generateOmzetHeatmapFromSummaries(data, containerId); 
        else if (block.type === 'heatmap_daily') generateDailyOmzetHeatmapFromSummaries(data, containerId); 
        else if (block.type === 'top_10_qty') renderTop10Table(data, containerId, 'quantity'); 
        else if (block.type === 'top_10_rev') renderTop10Table(data, containerId, 'revenue'); 
    }
    else {
        // P&L Blocks (Logic remains the same)
        const canvas = document.createElement('canvas');
        canvas.id = `canvas_${block.id}`;
        container.appendChild(canvas);
        const pnlReport = await getPnlDataForBlock(block);
        
        if (!pnlReport) { 
            container.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400 italic text-2xl font-medium">No P&L data found.</div>'; 
            return; 
        }
        
        const reportArray = [pnlReport]; 
        if (block.type === 'pnl_overview') generatePnlOverviewChart(reportArray, canvas.id, {}); 
        else if (block.type === 'net_profit_margin') generateFinancialRatioChart(reportArray, { canvasId: canvas.id, metric: 'Pendapatan Bersih (Net Income)', title: 'Net Profit Margin' });
        else if (block.type === 'cogs_ratio') generateFinancialRatioChart(reportArray, { canvasId: canvas.id, metric: 'Harga Pokok Produksi', title: 'COGS %' });
        else if (block.type === 'operational_ratio') generateFinancialRatioChart(reportArray, { canvasId: canvas.id, metric: 'Beban Operasional (OPEX)', title: 'OPEX %' });
    }
}


// --- 4. AI GENERATOR ---

async function generateBlockInsight(index: number, intent: string = 'general_review') {
    const sourceBlock = reportBlocks[index];
    const salesData = getBlockData(sourceBlock); 
    
    // Validation
    let pnlReport = null;
    const pnlTypes = ['pnl_overview', 'net_profit_margin', 'cogs_ratio', 'operational_ratio'];
    if (pnlTypes.includes(sourceBlock.type)) {
        pnlReport = await getPnlDataForBlock(sourceBlock);
        if (!pnlReport) { alert("No P&L data found."); return; }
    } else if (salesData.length === 0) {
        alert("No sales data available."); return;
    }

    reportBlocks[index].isAiLoading = true;
    renderReportCanvas(); 

    try {
        const prompt = buildAiPrompt({
            blockType: sourceBlock.type,
            intent: intent,
            branchName: sourceBlock.branchName,
            period: sourceBlock.period,
            salesData: salesData,
            pnlData: pnlReport?.pnlData, 
            chartTitle: getChartTitle(sourceBlock.type)
        });

        const result = await getGeminiAnalysis(prompt);
        reportBlocks[index].aiAnalysis = result.summaryText;
        reportBlocks[index].isAiLoading = false;
        renderReportCanvas();

    } catch (error) {
        console.error("AI Error", error);
        alert("Failed to generate insights.");
        reportBlocks[index].isAiLoading = false;
        renderReportCanvas(); 
    }
}

// --- 5. PDF GENERATOR (ROBUST: Fixes Blank Charts) ---

const generatePdfDocument = async () => {
    loadMontserratFont(); 
    const canvasContainer = document.getElementById('rec-builder-canvas');
    if (!canvasContainer) throw new Error("Canvas not found");

    // [FIX START]: Inject CSS rule to force inline-block on images in the rendering container
    // This targets the specific behavior where Tailwind preflight sets images to 'block', 
    // causing layout shifts in html2canvas.
    const style = document.createElement('style');
    document.head.appendChild(style);
    try {
        style.sheet?.insertRule('body > div:last-child img { display: inline-block; }');
    } catch (e) {
        // Fallback if insertRule fails
        style.innerHTML = 'body > div:last-child img { display: inline-block; }';
    }
    // [FIX END]

    const sandbox = document.createElement('div');
    sandbox.style.position = 'fixed';
    sandbox.style.top = '-10000px';
    sandbox.style.left = '-10000px';
    sandbox.style.width = '794px'; 
    sandbox.style.height = '1123px';
    sandbox.style.background = '#ffffff'; 
    sandbox.style.fontFamily = "'Montserrat', sans-serif"; 
    document.body.appendChild(sandbox);

    const { jsPDF } = jspdf;
    const pdf = new jsPDF('p', 'mm', 'a4'); 
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    try {
        const reportPages = canvasContainer.querySelectorAll('.report-page');

        for (let i = 0; i < reportPages.length; i++) {
            const originalPage = reportPages[i] as HTMLElement;
            const clonedPage = originalPage.cloneNode(true) as HTMLElement;
            
            // Cleanup UI controls
            clonedPage.querySelectorAll('.block-controls, .generate-ai-btn').forEach(el => el.remove());
            clonedPage.classList.remove('shadow-2xl', 'hover:ring-4', 'ring-indigo-500/20', 'group');
            clonedPage.style.transform = 'none';

            // Swap Canvases for Images
            const origCanvases = originalPage.querySelectorAll('canvas');
            const clonedCanvases = clonedPage.querySelectorAll('canvas');
            const imageLoadPromises: Promise<void>[] = [];

            origCanvases.forEach((orig, idx) => {
                const clone = clonedCanvases[idx];
                if (clone) {
                    const p = new Promise<void>((resolve) => {
                        const img = document.createElement('img');
                        img.onload = () => resolve(); 
                        img.onerror = () => resolve(); 
                        img.src = orig.toDataURL('image/png', 1.0);
                        img.style.width = '100%';
                        img.style.height = '100%';
                        img.style.objectFit = 'contain';
                        // Apply inline-block here too just to be safe
                        img.style.display = 'inline-block'; 
                        clone.parentNode?.replaceChild(img, clone);
                    });
                    imageLoadPromises.push(p);
                }
            });

            // Swap Textareas for Divs
            const origText = originalPage.querySelectorAll('textarea');
            const clonedText = clonedPage.querySelectorAll('textarea');
            origText.forEach((orig, idx) => {
                const clone = clonedText[idx];
                if (clone) {
                    const p = document.createElement('div');
                    p.className = "text-2xl text-gray-700 leading-relaxed font-medium p-0 whitespace-pre-wrap";
                    p.style.fontFamily = "'Montserrat', sans-serif";
                    p.textContent = orig.value;
                    clone.parentNode?.replaceChild(p, clone);
                }
            });

            sandbox.innerHTML = ''; 
            sandbox.appendChild(clonedPage); 

            // Wait for images
            await Promise.all(imageLoadPromises);
            await new Promise(resolve => setTimeout(resolve, 500)); 

            // Capture
            const canvas = await html2canvas(clonedPage, { 
                scale: 2, 
                useCORS: true, 
                width: 794,
                windowWidth: 794,
                backgroundColor: '#ffffff' 
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);

            if (i > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        }
    } finally {
        // [FIX END]: Clean up the style tag and sandbox strictly
        if (document.body.contains(sandbox)) {
            document.body.removeChild(sandbox);
        }
        if (document.head.contains(style)) {
            // Remove the injected style so it doesn't affect the rest of the app
            style.remove();
        }
    }

    return pdf;
};

// --- BUSINESS HIGHLIGHT (STORYTELLING ENGINE) ---

let currentSlides: string[] = [];
let currentSlideIndex = 0;
let currentHighlightData: any[] = []; 

// [INSERT THESE HELPER FUNCTIONS BEFORE setupBusinessHighlightView IN apps/web/src/analysis/premium/orchestrator.ts]

/**
 * Injects the animation keyframes into the document head ONCE.
 * This ensures animations work on every slide, not just the first one.
 */
function injectWrappedStyles() {
    const styleId = 'wrapped-animation-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulseSlow { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.9; } }
        
        .anim-fade-up { animation: fadeInUp 0.6s ease-out forwards; opacity: 0; }
        .anim-scale { animation: scaleIn 0.6s ease-out forwards; opacity: 0; }
        .anim-slide { animation: slideInRight 0.6s ease-out forwards; opacity: 0; }
        .anim-pulse { animation: pulseSlow 3s infinite ease-in-out; }
        
        .anim-delay-1 { animation-delay: 0.2s; }
        .anim-delay-2 { animation-delay: 0.4s; }
        .anim-delay-3 { animation-delay: 0.6s; }
    `;
    document.head.appendChild(style);
}

/**
 * Dynamically creates the Full-Screen Stage View Modal.
 * [UPDATED] Full height (90vh) with top/bottom padding, strictly 9:16 aspect ratio.
 */
function ensureHighlightModal() {
    // [FIX] Always remove the old modal first to ensure CSS/Structure updates apply immediately
    const existing = document.getElementById('highlight-stage-view');
    if (existing) existing.remove();

    const modalHtml = `
        <div id="highlight-stage-view" 
             style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 2147483647; background-color: rgba(0,0,0,0.95); backdrop-filter: blur(5px);"
             class="hidden flex flex-col items-center justify-center">
            
            <button id="highlight-close-btn" class="absolute top-6 right-6 text-white/50 hover:text-white p-2 transition-colors z-50">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <div id="highlight-slide-container-wrapper" class="relative h-[90vh] w-auto max-w-full aspect-[9/16] bg-gray-900 rounded-3xl shadow-2xl overflow-hidden ring-1 ring-white/20">
                
                <div id="highlight-slide-content" class="w-full h-full"></div>

                <div class="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center gap-4">
                    
                    <div id="highlight-dots" class="flex gap-2"></div>

                    <div class="flex items-center gap-6 w-full justify-between px-4">
                        <button id="highlight-prev-btn" class="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
                        </button>
                        
                        <button id="highlight-export-pdf-btn" class="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-xs font-bold uppercase tracking-wider backdrop-blur-md transition-all border border-white/10">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                            Save as PDF
                        </button>

                        <button id="highlight-next-btn" class="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

// [INSERT THESE HELPERS BEFORE setupBusinessHighlightView]

/**
 * Fetches exclusion settings (hidden menus/categories) for a specific branch.
 */
async function getBranchExclusions(branchId: string) {
    try {
        const branchDocRef = doc(db, paths.branches(branchId).path);
        const docSnap = await getDoc(branchDocRef);
        return docSnap.exists() ? (docSnap.data().exclusions || {}) : {};
    } catch (error) {
        console.error("Error fetching exclusions:", error);
        return {};
    }
}

/**
 * Filters raw sales summaries based on exclusion rules.
 * Recalculates Total Omzet by subtracting hidden items.
 */
function applyExclusionsToData(data: any[], exclusions: any) {
    if (!exclusions || Object.keys(exclusions).length === 0) return data;

    const { hiddenCategories = [], hiddenMenus = [], hiddenChannels = [] } = exclusions;

    // If nothing is hidden, return original data to save performance
    if (hiddenCategories.length === 0 && hiddenMenus.length === 0 && hiddenChannels.length === 0) return data;

    return data.map(summary => {
        // Deep clone to avoid mutating the store
        const s = JSON.parse(JSON.stringify(summary));
        
        // We need to recreate the date object because JSON.parse kills it
        s.date = new Date(summary.date); 

        let deductedRevenue = 0;

        // 1. Filter Menu Items & Categories
        if (s.menuItemRevenues) {
            for (const category in s.menuItemRevenues) {
                // Remove entire Category
                if (hiddenCategories.includes(category)) {
                    Object.values(s.menuItemRevenues[category]).forEach((val: any) => deductedRevenue += Number(val));
                    delete s.menuItemRevenues[category];
                    if (s.menuItemQuantities) delete s.menuItemQuantities[category];
                    continue; 
                }

                // Remove specific Menus
                for (const item in s.menuItemRevenues[category]) {
                    if (hiddenMenus.includes(item)) {
                        deductedRevenue += Number(s.menuItemRevenues[category][item]);
                        delete s.menuItemRevenues[category][item];
                        if (s.menuItemQuantities && s.menuItemQuantities[category]) {
                            delete s.menuItemQuantities[category][item];
                        }
                    }
                }
            }
        }

        // 2. Filter Channels (Visual only, usually doesn't affect Total Omzet unless we want it to)
        if (s.revenueByVisitPurpose) {
            for (const channel in s.revenueByVisitPurpose) {
                if (hiddenChannels.includes(channel)) {
                    delete s.revenueByVisitPurpose[channel];
                }
            }
        }

        // 3. Adjust Total Omzet
        s.totalOmzet = Math.max(0, s.totalOmzet - deductedRevenue);
        
        // Recalculate Average Check (heuristic)
        s.apc = s.totalTransactions > 0 ? s.totalOmzet / s.totalTransactions : 0;

        return s;
    });
}

export async function setupBusinessHighlightView() {
    const container = document.getElementById('premium-business-highlight-content');
    if (!container) return;

    // Ensure our custom styles and modal exist
    injectWrappedStyles();
    ensureHighlightModal();

    if (container.hasAttribute('data-initialized')) return;
    container.setAttribute('data-initialized', 'true');

    // --- Selectors ---
    const branchSelect = document.getElementById('highlight-branch-select') as HTMLSelectElement;
    const rangeSelect = document.getElementById('highlight-range-select') as HTMLSelectElement;
    const periodSelect = document.getElementById('highlight-period-select') as HTMLSelectElement;
    const kpiGrid = document.getElementById('highlight-kpi-grid');
    
    // --- Init Logic ---
    const populateSelectors = async () => {
        const { userBranches, selectedBrandId } = $store.getAccountContext();
        const { isDemoDataActive } = $store.getAnalysisState().config;
        
        let branches = userBranches;
        if (!isDemoDataActive && selectedBrandId) {
            branches = userBranches.filter(b => b.brandId === selectedBrandId);
        }
        const branchOptions = branches.map(b => `<option value="${b.branchId}">${b.branchName}</option>`).join('');
        branchSelect.innerHTML = branchOptions;
        if (branches.length > 0) branchSelect.value = branches[0].branchId;

        const updatePeriod = () => {
            const allSalesData = $store.getAllSalesData();
            let branchData = allSalesData;
            const selectedBranch = branchSelect.value;
            if (!isDemoDataActive) {
                branchData = allSalesData.filter(s => (s as any).branchId === selectedBranch);
            }

            const range = rangeSelect.value;
            const periods = new Set<string>();
            const dates = branchData.map(s => s.date);

            if (range === 'yearly') dates.forEach(d => periods.add(String(d.getFullYear())));
            else dates.forEach(d => periods.add(d.toISOString().slice(0, 7)));

            const sorted = Array.from(periods).sort().reverse();
            periodSelect.innerHTML = sorted.map(p => `<option value="${p}">${p}</option>`).join('');
            
            if(sorted.length > 0) refreshDashboard();
            else if (kpiGrid) kpiGrid.innerHTML = `<div class="col-span-4 text-center py-12 text-gray-400 italic">No data found for this branch.</div>`;
        };

        branchSelect.addEventListener('change', updatePeriod);
        rangeSelect.addEventListener('change', updatePeriod);
        periodSelect.addEventListener('change', refreshDashboard);
        updatePeriod(); 
    };

    // --- Helper: Friendly Number Formatter ---
    const formatBigCurrency = (val: number) => {
        if (val >= 1_000_000_000) return `Rp ${(val / 1_000_000_000).toFixed(2)} M`;
        if (val >= 1_000_000) return `Rp ${(val / 1_000_000).toFixed(1)} jt`;
        return formatCurrency(val);
    };

    // [REPLACE the refreshDashboard function INSIDE setupBusinessHighlightView]

    async function refreshDashboard() {
        const branchId = branchSelect.value;
        const period = periodSelect.value;
        const range = rangeSelect.value;

        if (!branchId || !period) return;

        showLoading({ message: 'Applying configuration...' });

        try {
            // 1. Get Base Data
            const allSalesData = $store.getAllSalesData();
            const { isDemoDataActive } = $store.getAnalysisState().config;
            let rawData = allSalesData;
            
            if (!isDemoDataActive) {
                rawData = allSalesData.filter(s => (s as any).branchId === branchId);
            }

            // 2. Filter by Time
            let periodData = [];
            if (range === 'yearly') {
                periodData = rawData.filter(s => String(s.date.getFullYear()) === period);
            } else {
                periodData = rawData.filter(s => s.date.toISOString().startsWith(period));
            }

            if (periodData.length === 0) {
                if(kpiGrid) kpiGrid.innerHTML = `<div class="text-center py-12 text-gray-400 italic">No data found for this period.</div>`;
                hideLoading();
                return;
            }

            // 3. [NEW] Fetch & Apply Exclusions
            // We fetch this fresh every time to ensure the latest config is used
            const exclusions = await getBranchExclusions(branchId);
            currentHighlightData = applyExclusionsToData(periodData, exclusions);

            // 4. Render
            renderKpiGrid();

        } catch (error) {
            console.error("Error refreshing dashboard:", error);
        } finally {
            hideLoading();
        }
    }

    const renderKpiGrid = () => {
        if (!kpiGrid) return;

        // Metrics Calculation
        const totalOmzet = currentHighlightData.reduce((sum, d) => sum + d.totalOmzet, 0);
        const totalTrans = currentHighlightData.reduce((sum, d) => sum + d.totalTransactions, 0);
        
        // Top Product
        const productMap = new Map<string, number>();
        currentHighlightData.forEach(s => {
            if (s.menuItemQuantities) Object.values(s.menuItemQuantities).forEach(cat => Object.entries(cat).forEach(([k, v]) => productMap.set(k, (productMap.get(k)||0) + Number(v))));
        });
        const topProduct = Array.from(productMap.entries()).sort((a,b) => b[1]-a[1])[0] || ['None', 0];

        // Busiest Day
        const dayMap = new Map<string, number>();
        currentHighlightData.forEach(s => {
            const day = s.date.toLocaleDateString('en-US', { weekday: 'long' });
            dayMap.set(day, (dayMap.get(day)||0) + s.totalOmzet);
        });
        const busiestDay = Array.from(dayMap.entries()).sort((a,b) => b[1]-a[1])[0] || ['None', 0];

        // Render Cards (Row Layout)
        kpiGrid.innerHTML = `
            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-purple-200 hover:shadow-lg transition-all cursor-pointer group flex items-center justify-between" onclick="openDetailModal('revenue')">
                <div>
                    <div class="flex items-center gap-3 mb-2">
                        <div class="p-2 bg-purple-50 text-purple-600 rounded-lg"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg></div>
                        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Money In</span>
                    </div>
                    <h3 class="text-3xl font-black text-gray-800">${formatBigCurrency(totalOmzet)}</h3>
                </div>
                <div class="text-right">
                    <span class="text-sm font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">View Trend &rarr;</span>
                </div>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all cursor-pointer group flex items-center justify-between" onclick="openDetailModal('time')">
                <div>
                    <div class="flex items-center gap-3 mb-2">
                        <div class="p-2 bg-blue-50 text-blue-600 rounded-lg"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg></div>
                        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Peak Time</span>
                    </div>
                    <h3 class="text-3xl font-black text-gray-800">${busiestDay[0]}</h3>
                </div>
                <div class="text-right">
                    <span class="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">See Heatmap &rarr;</span>
                </div>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all cursor-pointer group flex items-center justify-between" onclick="openDetailModal('product')">
                <div class="flex-1 min-w-0 pr-4"> <div class="flex items-center gap-3 mb-2">
                        <div class="p-2 bg-pink-50 text-pink-600 rounded-lg"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg></div>
                        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Fan Favorite</span>
                    </div>
                    <h3 class="text-2xl font-black text-gray-800 truncate" title="${topProduct[0]}">${topProduct[0]}</h3>
                    <p class="text-sm text-gray-500 font-medium">${formatNumber(topProduct[1])} sold</p>
                </div>
                <div class="text-right flex-shrink-0">
                    <span class="text-sm font-bold text-pink-600 bg-pink-50 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">Top 10 &rarr;</span>
                </div>
            </div>

            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-green-200 hover:shadow-lg transition-all cursor-pointer group flex items-center justify-between" onclick="openDetailModal('traffic')">
                <div>
                    <div class="flex items-center gap-3 mb-2">
                        <div class="p-2 bg-green-50 text-green-600 rounded-lg"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z"></path></svg></div>
                        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Visits</span>
                    </div>
                    <h3 class="text-3xl font-black text-gray-800">${formatNumber(totalTrans)}</h3>
                </div>
                <div class="text-right">
                    <span class="text-sm font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">Analyze &rarr;</span>
                </div>
            </div>
        `;
    };

    // --- 2. Modal Logic (Storytelling Drill-Down) ---
    const detailModal = document.getElementById('highlight-detail-modal');
    const modalTitle = document.getElementById('highlight-modal-title');
    const modalDesc = document.getElementById('highlight-modal-desc');
    const modalChart = document.getElementById('highlight-modal-chart-container');

    (window as any).openDetailModal = (type: string) => {
        if (!detailModal || !modalChart || !modalTitle || !modalDesc) return;
        
        detailModal.classList.remove('hidden');
        modalChart.innerHTML = '<canvas id="highlight-detail-canvas"></canvas>';
        const canvasId = 'highlight-detail-canvas';

        if (type === 'revenue') {
            modalTitle.textContent = "Revenue Story";
            modalDesc.textContent = "This chart shows exactly when money came in. Look for spikes—did you run a promo then? Or was it a payday weekend? Understanding these peaks helps you plan better for next month.";
            generateOmzetHarianChartFromSummaries(currentHighlightData, canvasId);
        } 
        else if (type === 'traffic') {
            modalTitle.textContent = "Traffic vs. Spending";
            modalDesc.textContent = "Are people visiting more but spending less? Or visiting less but spending big? The bars show the crowd size, and the orange line shows how much they spent on average.";
            generateTcApcHarianChartFromSummaries(currentHighlightData, canvasId);
        }
        else if (type === 'product') {
            modalTitle.textContent = "Your Best Sellers (Top 10)";
            modalDesc.textContent = "These 10 items are carrying your business. Make sure you never run out of stock for these! Consider bundling the top item with a lower-performing one to boost sales.";
            const menuMap = new Map<string, number>();
            currentHighlightData.forEach(s => {
                if(s.menuItemQuantities) Object.values(s.menuItemQuantities).forEach(cat => Object.entries(cat).forEach(([k,v]) => menuMap.set(k, (menuMap.get(k)||0)+Number(v))));
            });
            const top10 = Array.from(menuMap.entries()).sort((a,b)=>b[1]-a[1]).slice(0, 10);
            createChart(canvasId, 'bar', {
                labels: top10.map(i=>i[0]),
                datasets: [{ label: 'Quantity Sold', data: top10.map(i=>i[1]), backgroundColor: '#db2777', barPercentage: 0.6 }]
            }, { indexAxis: 'y', maintainAspectRatio: false });
        }
        else if (type === 'time') {
            modalTitle.textContent = "When are you busiest?";
            modalDesc.textContent = "Darker squares mean more money. If your Tuesday afternoons are light, maybe that's the time for a Happy Hour promo. If Friday nights are dark, double your staff!";
            modalChart.innerHTML = ''; 
            modalChart.id = 'highlight-detail-heatmap'; 
            generateDailyOmzetHeatmapFromSummaries(currentHighlightData, 'highlight-detail-heatmap');
        }
    };

    document.getElementById('highlight-modal-close')?.addEventListener('click', () => {
        detailModal?.classList.add('hidden');
        if(modalChart) modalChart.id = 'highlight-modal-chart-container'; 
    });

    // --- 3. Slideshow Logic (Engaging & Insightful) ---
    document.getElementById('highlight-generate-btn')?.addEventListener('click', async () => {
        if (currentHighlightData.length === 0) { alert("Please select data first."); return; }
        
        showLoading({ message: 'Crafting your story...' });

        // --- Data Calculation ---
        const totalOmzet = currentHighlightData.reduce((sum, d) => sum + d.totalOmzet, 0);
        const totalTrans = currentHighlightData.reduce((sum, d) => sum + d.totalTransactions, 0);
        const avgCheck = totalTrans > 0 ? totalOmzet / totalTrans : 0;
        
        // Product
        const productMap = new Map<string, number>();
        currentHighlightData.forEach(s => {
            if (s.menuItemQuantities) Object.values(s.menuItemQuantities).forEach(cat => Object.entries(cat).forEach(([k, v]) => productMap.set(k, (productMap.get(k)||0) + Number(v))));
        });
        const topProduct = Array.from(productMap.entries()).sort((a,b) => b[1]-a[1])[0] || ['None', 0];

        // Day Analysis (Weekend vs Weekday)
        const dayMap = new Map<string, number>();
        let weekendSales = 0;
        currentHighlightData.forEach(s => {
            const day = s.date.toLocaleDateString('en-US', { weekday: 'long' });
            dayMap.set(day, (dayMap.get(day)||0) + s.totalOmzet);
            if (['Saturday', 'Sunday'].includes(day)) weekendSales += s.totalOmzet;
        });
        const weekendShare = totalOmzet > 0 ? (weekendSales / totalOmzet) * 100 : 0;

        // Hour Analysis (Peak Hour)
        const hourMap = new Array(24).fill(0);
        currentHighlightData.forEach(s => {
            if (s.hourlyRevenue && s.hourlyRevenue.length === 24) {
                s.hourlyRevenue.forEach((val, idx) => hourMap[idx] += val);
            }
        });
        const maxHourVal = Math.max(...hourMap);
        const peakHourIdx = hourMap.indexOf(maxHourVal);
        const peakHourStr = `${peakHourIdx}:00 - ${peakHourIdx + 1}:00`;

        const periodName = periodSelect.value;
        const branchName = branchSelect.options[branchSelect.selectedIndex].text;

        // --- Slide Construction (No Inline <style>, Uses Global CSS) ---
        const containerStyle = "width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center; background: linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #000000 100%); color: white; font-family: system-ui, sans-serif; overflow: hidden; position: relative;";
        const bgPattern = `<div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background-image: radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 30px 30px; opacity: 0.3; pointer-events: none;"></div>`;

        // Slide 1: Intro
        const slide1 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <div class="anim-fade-up">
                    <p style="color: #a5b4fc; font-size: 1.25rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 1rem;">Finalytics Wrapped</p>
                    <h1 style="font-size: 3.5rem; font-weight: 900; color: white; margin-bottom: 1.5rem; line-height: 1.1; text-shadow: 0 4px 20px rgba(0,0,0,0.5);">Your ${periodName}<br>Story</h1>
                </div>
                <div class="anim-scale anim-delay-2" style="width: 8rem; height: 8rem; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 2rem 0; box-shadow: 0 0 50px rgba(255,255,255,0.3);">
                    <span style="font-size: 3.5rem;">🚀</span>
                </div>
                <p class="anim-fade-up anim-delay-3" style="color: white; font-size: 1.5rem; font-weight: 500; opacity: 0.9;">${branchName}</p>
            </div>`;

        // Slide 2: Revenue
        const slide2 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <p class="anim-slide" style="color: #a5b4fc; font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2.5rem;">Total Revenue</p>
                <div class="anim-scale anim-delay-1">
                    <h1 style="font-size: 4rem; font-weight: 900; color: #FCD34D; margin-bottom: 1rem; line-height: 1; text-shadow: 0 0 30px rgba(252, 211, 77, 0.4);">${formatBigCurrency(totalOmzet)}</h1>
                </div>
                <p class="anim-fade-up anim-delay-2" style="color: white; font-size: 1.25rem; opacity: 0.9; max-width: 28rem; margin: 1rem auto; line-height: 1.5;">
                    That's the fuel that powered your growth this month.
                </p>
            </div>`;

        // Slide 3: The Golden Hour (FIXED FONT SIZE)
        const slide3 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <p class="anim-slide" style="color: #a5b4fc; font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2rem;">The Golden Hour</p>
                <div class="anim-scale anim-delay-1" style="margin-bottom: 2rem;">
                    <div style="font-size: 4rem; margin-bottom: 0.5rem;">⏰</div>
                    <h1 style="font-size: 2.75rem; font-weight: 900; color: white; white-space: nowrap;">${peakHourStr}</h1>
                </div>
                <div class="anim-fade-up anim-delay-2" style="background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 1.5rem; padding: 2rem; max-width: 26rem;">
                    <p style="font-size: 1.15rem; color: white; line-height: 1.6;">
                        This was your busiest time. <span style="color: #FCD34D; font-weight: 700;">Maximizing staff</span> here boosts sales!
                    </p>
                </div>
            </div>`;

        // Slide 4: Weekend vs Weekday
        const isWeekendWarrior = weekendShare > 45;
        const slide4 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <p class="anim-slide" style="color: #a5b4fc; font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 3rem;">Weekend Vibe</p>
                <div style="display: flex; gap: 2rem; align-items: flex-end; margin-bottom: 2rem;">
                    <div class="anim-fade-up anim-delay-1" style="display: flex; flex-direction: column; align-items: center;">
                        <div style="width: 5rem; height: ${Math.max(weekendShare * 2, 20)}px; max-height: 12rem; background: #FCD34D; border-radius: 1rem 1rem 0 0; margin-bottom: 1rem; box-shadow: 0 0 20px rgba(252, 211, 77, 0.3);"></div>
                        <p style="font-size: 1.25rem; font-weight: 800; color: white;">${weekendShare.toFixed(0)}%</p>
                        <p style="font-size: 0.8rem; color: #a5b4fc; text-transform: uppercase;">Weekend</p>
                    </div>
                    <div class="anim-fade-up anim-delay-2" style="display: flex; flex-direction: column; align-items: center;">
                        <div style="width: 5rem; height: ${Math.max((100-weekendShare) * 2, 20)}px; max-height: 12rem; background: rgba(255,255,255,0.2); border-radius: 1rem 1rem 0 0; margin-bottom: 1rem;"></div>
                        <p style="font-size: 1.25rem; font-weight: 800; color: white;">${(100-weekendShare).toFixed(0)}%</p>
                        <p style="font-size: 0.8rem; color: #a5b4fc; text-transform: uppercase;">Weekday</p>
                    </div>
                </div>
                <p class="anim-fade-up anim-delay-3" style="font-size: 1.25rem; font-weight: 700; color: white;">
                    ${isWeekendWarrior ? "🔥 You are a Weekend Warrior!" : "⚖️ Steady traffic all week!"}
                </p>
            </div>`;

        // Slide 5: Top Product
        const slide5 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <p class="anim-slide" style="color: #a5b4fc; font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2rem;">The Fan Favorite</p>
                <div class="anim-scale anim-delay-1 anim-pulse" style="margin-bottom: 2rem;">
                    <div style="font-size: 3rem; margin-bottom: 0.5rem;">👑</div>
                    <h1 style="font-size: 2.5rem; font-weight: 900; color: white; line-height: 1.1; max-width: 20ch;">${topProduct[0]}</h1>
                </div>
                <div class="anim-fade-up anim-delay-2" style="background: rgba(255,255,255,0.1); border: 2px solid rgba(255,255,255,0.2); border-radius: 1.5rem; padding: 2rem; min-width: 16rem;">
                    <p style="font-size: 3rem; font-weight: 800; color: #FCD34D; margin-bottom: 0.5rem; line-height: 1;">${formatNumber(topProduct[1])}</p>
                    <p style="font-size: 1rem; color: white; opacity: 0.8; font-weight: 500;">Units Sold</p>
                </div>
            </div>`;

        // Slide 6: Summary
        const slide6 = `
            <div style="${containerStyle}">
                ${bgPattern}
                <h1 class="anim-fade-up" style="font-size: 2.5rem; font-weight: 900; color: white; margin-bottom: 1.5rem;">The Breakdown</h1>
                
                <div class="anim-scale anim-delay-1" style="background: white; color: #111827; border-radius: 1.5rem; padding: 2rem; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); width: 100%; max-width: 22rem; transform: rotate(-2deg); border: 6px solid white;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; border-bottom: 2px solid #f3f4f6; padding-bottom: 1rem;">
                        <span style="font-weight: 900; color: #4f46e5; font-size: 1.25rem; letter-spacing: -0.02em;">Finalytics</span>
                        <span style="font-size: 0.8rem; color: #6b7280; font-weight: 600; background: #f3f4f6; padding: 2px 8px; rounded: 4px;">${periodName}</span>
                    </div>
                    <div style="text-align: left; display: flex; flex-direction: column; gap: 1.5rem;">
                        <div>
                            <p style="font-size: 0.75rem; color: #6b7280; text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em;">Revenue</p>
                            <p style="font-size: 1.75rem; font-weight: 800; color: #111827;">${formatBigCurrency(totalOmzet)}</p>
                        </div>
                        <div>
                            <p style="font-size: 0.75rem; color: #6b7280; text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em;">Top Item</p>
                            <p style="font-size: 1.25rem; font-weight: 700; color: #db2777; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${topProduct[0]}</p>
                        </div>
                         <div style="display: flex; justify-content: space-between;">
                             <div><p style="font-size: 0.75rem; color: #6b7280; text-transform: uppercase; font-weight: 700;">Visits</p><p style="font-weight: 800; font-size: 1.125rem;">${formatNumber(totalTrans)}</p></div>
                             <div><p style="font-size: 0.75rem; color: #6b7280; text-transform: uppercase; font-weight: 700;">APC</p><p style="font-weight: 800; font-size: 1.125rem;">${shortenCurrency(avgCheck)}</p></div>
                        </div>
                    </div>
                    <div style="margin-top: 2rem; padding-top: 1rem; border-top: 2px solid #f3f4f6; text-align: center;">
                        <p style="font-size: 0.7rem; color: #9ca3af; font-weight: 500;">Generated by Finalytics AI</p>
                    </div>
                </div>
            </div>`;

        currentSlides = [slide1, slide2, slide3, slide4, slide5, slide6];
        currentSlideIndex = 0;

        renderSlide();
        document.getElementById('highlight-stage-view')?.classList.remove('hidden');
        hideLoading();
    });

    const renderSlide = () => {
        const slideContainer = document.getElementById('highlight-slide-content');
        const dotsContainer = document.getElementById('highlight-dots');
        
        if (slideContainer) {
            // Simply replacing innerHTML triggers CSS animations because we removed the style tags from the string.
            // The global styles in <head> will match the new elements immediately.
            slideContainer.innerHTML = currentSlides[currentSlideIndex];
        }
        
        if (dotsContainer) {
            dotsContainer.innerHTML = currentSlides.map((_, i) => `
                <div class="w-2 h-2 rounded-full transition-all ${i === currentSlideIndex ? 'bg-white w-6' : 'bg-white/40'}"></div>
            `).join('');
        }
    };

    const nextBtn = document.getElementById('highlight-next-btn');
    const prevBtn = document.getElementById('highlight-prev-btn');
    const closeBtn = document.getElementById('highlight-close-btn');
    const exportPdfBtn = document.getElementById('highlight-export-pdf-btn');

    nextBtn?.addEventListener('click', () => { if (currentSlideIndex < currentSlides.length - 1) { currentSlideIndex++; renderSlide(); } });
    prevBtn?.addEventListener('click', () => { if (currentSlideIndex > 0) { currentSlideIndex--; renderSlide(); } });
    closeBtn?.addEventListener('click', () => { document.getElementById('highlight-stage-view')?.classList.add('hidden'); });

    exportPdfBtn?.addEventListener('click', async () => {
        showLoading({ message: 'Saving your story...' });
        try {
            const { jsPDF } = jspdf;
            const pdf = new jsPDF('p', 'mm', [1080, 1920]); 
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            const sandbox = document.createElement('div');
            sandbox.style.position = 'fixed'; sandbox.style.top = '-10000px'; sandbox.style.width = '540px'; sandbox.style.height = '960px'; 
            document.body.appendChild(sandbox);

            for (let i = 0; i < currentSlides.length; i++) {
                // Strip regex to remove animation classes for clean PDF
                let slideHtml = currentSlides[i].replace(/anim-[\w-]+/g, ''); 
                sandbox.innerHTML = slideHtml;
                
                // Allow time for fonts/layout to settle (animation classes are gone, but logic needs a tick)
                await new Promise(resolve => setTimeout(resolve, 300)); 
                
                const canvas = await html2canvas(sandbox, { scale: 2, useCORS: true, backgroundColor: '#000000' });
                const imgData = canvas.toDataURL('image/png');
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            }
            pdf.save('Business_Highlight.pdf');
            document.body.removeChild(sandbox);
        } catch (e) { console.error(e); alert('Failed to export.'); } finally { hideLoading(); }
    });

    await populateSelectors();
}